import React, { useState, useEffect } from "react";
import { MdOutlineArrowRightAlt } from "react-icons/md";
import FeatureAccessControl from './FeatureAccessControl';
import FeatureButton from './FeatureButton';
import { IoIosWarning } from "react-icons/io";
import { FiSearch } from "react-icons/fi";
import { IoRocket, IoDocument } from "react-icons/io5";
import { IoDocumentText, IoCash } from "react-icons/io5";
import { TfiTarget } from "react-icons/tfi";
import { IoStar } from "react-icons/io5";
import { BiSolidRightArrow } from "react-icons/bi";
import AgentCard from './AgentCard';

// Import agent flows
import {
  WorkVisaAgentFlow,
  AsylumProtectionAgentFlow,
  StudentVisaAgentFlow,
  CitizenshipAgentFlow,
  DacaTpsRenewalAgentFlow,
  RfeNoidResponseAgentFlow,
} from "./AgentFlows";

// Hybrid Chat Component - Reusable across all steps
const HybridChatAssistant = ({
  formData,
  stepContext = "general",
  agentType = null,
}) => {
  const [showChatWindow, setShowChatWindow] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");

  // Handle quick question buttons
  const handleQuickQuestion = (question) => {
    const newMessage = {
      id: Date.now(),
      text: question,
      sender: "user",
      timestamp: new Date(),
    };

    setChatMessages((prev) => [...prev, newMessage]);
    setChatInput("");

    // Process the AI response
    handleChatMessageWithText(question);
  };

  // Handle chat with AI
  const handleChatMessageWithText = async (messageText) => {
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
      const response = await fetch(`${backendUrl}/api/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: messageText,
          agent_type: "family-based",
          conversation_history: chatMessages.map((msg) => ({
            sender: msg.sender === "user" ? "user" : "assistant",
            text: msg.text,
          })),
        }),
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        const aiResponse = {
          id: Date.now() + 1,
          text: data.response,
          sender: "ai",
          timestamp: new Date(),
        };
        setChatMessages((prev) => [...prev, aiResponse]);
      } else if (response.status === 403) {
        // Handle subscription limit errors
        const errorData = await response.json();
        const errorMessage = errorData.detail || "Access denied";
        
        const errorResponse = {
          id: Date.now() + 1,
          text: `🚫 ${errorMessage}\n\n💡 To access this agent, please upgrade your subscription plan. Click on your profile menu and visit the subscription management section to upgrade.`,
          sender: "ai",
          timestamp: new Date(),
        };
        setChatMessages((prev) => [...prev, errorResponse]);
      } else if (response.status === 401) {
        // Handle authentication errors
        const errorResponse = {
          id: Date.now() + 1,
          text: "🔐 Please log in to continue using the AI agents. You need to be authenticated to access this feature.",
          sender: "ai",
          timestamp: new Date(),
        };
        setChatMessages((prev) => [...prev, errorResponse]);
      }
    } catch (error) {
      console.error("Chat error:", error);
    }
  };

  // Handle chat with AI
  const handleChatMessage = async () => {
    if (!chatInput.trim()) return;

    const newMessage = {
      id: Date.now(),
      text: chatInput,
      sender: "user",
      timestamp: new Date(),
    };

    setChatMessages((prev) => [...prev, newMessage]);
    const currentMessage = chatInput;
    setChatInput("");

    // Process the AI response
    handleChatMessageWithText(currentMessage);
  };

  // Function to render formatted text with basic markdown support
  const renderFormattedText = (text) => {
    if (!text) return "";

    // Convert markdown-style formatting to HTML
    let formattedText = text
      // Headers
      .replace(
        /### (.*$)/gm,
        '<h3 class="text-white font-bold text-base mt-3 mb-2">$1</h3>'
      )
      .replace(
        /## (.*$)/gm,
        '<h2 class="text-white font-bold text-lg mt-3 mb-2">$1</h2>'
      )
      .replace(
        /# (.*$)/gm,
        '<h1 class="text-white font-bold text-xl mt-3 mb-2">$1</h1>'
      )
      // Bold text
      .replace(
        /\*\*(.*?)\*\*/g,
        '<strong class="text-blue-300 font-semibold">$1</strong>'
      )
      // Bullet points
      .replace(
        /^• (.*$)/gm,
        '<div class="flex items-start mt-1"><span class="text-blue-400 mr-2 mt-1">•</span><span>$1</span></div>'
      )
      .replace(
        /^- (.*$)/gm,
        '<div class="flex items-start mt-1"><span class="text-blue-400 mr-2 mt-1">•</span><span>$1</span></div>'
      )
      // Numbered lists (basic)
      .replace(
        /^(\d+)\. (.*$)/gm,
        '<div class="flex items-start mt-1"><span class="text-blue-400 mr-2 mt-1 font-medium">$1.</span><span>$2</span></div>'
      )
      // Line breaks
      .replace(/\n\n/g, "<br/><br/>")
      .replace(/\n/g, "<br/>");

    return formattedText;
  };

  // Agent-specific questions for main chat interface
  const getAgentSpecificQuestions = (agentType) => {
    const agentQuestions = {
      "family-based-agent": [
        "What documents do I need for my I-130 petition?",
        "How long does family-based immigration take?",
        "What income requirements must I meet as a sponsor?",
        "Can I file I-130 and I-485 at the same time?",
        "What evidence proves our relationship is genuine?",
      ],
      "work-visa-agent": [
        "What are the requirements for H-1B visa?",
        "How long does the H-1B process take?",
        "What documents does my employer need to provide?",
        "Can I change jobs while my H-1B is pending?",
        "What's the difference between L-1A and L-1B?",
      ],
      "asylum-protection-agent": [
        "What evidence do I need for my asylum case?",
        "How long do I have to file for asylum?",
        "What should I include in my personal statement?",
        "Can family members be included in my asylum case?",
        "What country condition evidence should I gather?",
      ],
      "student-exchange-visa-agent": [
        "What documents do I need for F-1 visa?",
        "How do I apply for OPT work authorization?",
        "Can I transfer to a different school on F-1?",
        "What are the requirements for J-1 visa?",
        "How long can I stay on student visa?",
      ],
      "citizenship-agent": [
        "Am I eligible for naturalization?",
        "What documents do I need for N-400 application?",
        "How long does the citizenship process take?",
        "What questions are on the civics test?",
        "Can I travel while my N-400 is pending?",
      ],
      "rfe-noid-response-agent": [
        "What does an RFE mean for my case?",
        "How long do I have to respond to an RFE?",
        "What evidence should I submit in my RFE response?",
        "Can I get an extension to respond to an RFE?",
        "What's the difference between RFE and NOID?",
      ],
      "daca-tps-renewal-agent": [
        "When should I file my DACA renewal?",
        "What documents do I need for DACA renewal?",
        "How much does DACA renewal cost?",
        "What if my DACA expired before I renewed?",
        "Can I travel with advance parole on DACA?",
      ],
      "visitor-tourist-visa-agent": [
        "What documents do I need for B-2 tourist visa?",
        "How long can I stay on a visitor visa?",
        "What's the difference between B-1 and B-2 visas?",
        "Can I extend my visitor visa while in the US?",
        "What should I prepare for the visa interview?",
      ],
    };

    return agentQuestions[agentType] || agentQuestions["family-based-agent"];
  };

  const getQuickQuestions = () => {
    // If we have an agent type, use agent-specific questions
    if (agentType) {
      const agentSpecificQuestions = getAgentSpecificQuestions(agentType);
      return agentSpecificQuestions.slice(0, 3); // Show first 3 agent-specific questions
    }

    // Otherwise use step-context specific questions
    const questionsByContext = {
      general: [
        "What documents are still missing?",
        "How strong is my case?",
        "What are the next steps?",
        "How long will the process take?",
        "What are common red flags to avoid?",
      ],
      welcome: [
        "What case type should I choose?",
        "Am I eligible for family-based immigration?",
        "What's the difference between I-130 and I-485?",
        "How long does the process take?",
        "What documents will I need?",
      ],
      profile: [
        "What if my spouse is undocumented?",
        "Do I need an attorney?",
        "What income requirements must I meet?",
        "Can my spouse work while the case is pending?",
        "What if we don't have joint finances yet?",
      ],
      documents: [
        "What if I don't have my birth certificate?",
        "Are digital photos acceptable?",
        "How many photos should I submit?",
        "What if documents are in another language?",
        "Can I add documents later?",
      ],
      analysis: [
        "Why is my score not higher?",
        "What are the most critical missing documents?",
        "How can I improve my case?",
        "What red flags should I worry about?",
        "Is my evidence sufficient?",
      ],
    };
    return questionsByContext[stepContext] || questionsByContext.general;
  };

  return (
    <div className="bg-blue-900/20 border border-blue-400 p-4 rounded-lg mb-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold text-white flex items-center">
          <span className="text-xl mr-2">💬</span>
          AI Immigration Assistant
        </h4>
        <button
          onClick={() => setShowChatWindow(!showChatWindow)}
          className="bg-blue-400 text-black px-4 py-2 rounded font-medium hover:bg-blue-300 transition-colors"
        >
          {showChatWindow ? "Hide Chat" : "Ask Questions"}
        </button>
      </div>

      {showChatWindow && (
        <div className="bg-gray-800 rounded-lg p-4">
          {/* Chat Messages */}
          <div className="h-64 overflow-y-auto bg-gray-900 rounded p-3 mb-4">
            {chatMessages.length === 0 ? (
              <div className="text-center text-gray-400 py-4">
                <span className="text-3xl block mb-2">🤖</span>
                <p className="text-sm">I'm here to help with your case!</p>
                <p className="text-xs mt-1">
                  Ask me anything about this step or the immigration process.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {chatMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.sender === "user"
                        ? "justify-end"
                        : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-md lg:max-w-lg px-4 py-3 rounded-lg ${
                        message.sender === "user"
                          ? "bg-emerald-500 text-white"
                          : "bg-gray-700 text-gray-200"
                      }`}
                    >
                      {message.sender === "user" ? (
                        <p className="text-sm">{message.text}</p>
                      ) : (
                        <div
                          className="text-sm formatted-response"
                          dangerouslySetInnerHTML={{
                            __html: renderFormattedText(message.text),
                          }}
                        />
                      )}
                      <p className="text-xs opacity-70 mt-1">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Chat Input */}
          <div className="flex items-center space-x-2 mb-3">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleChatMessage()}
              placeholder="Type your question..."
              className="flex-1 bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-400 focus:outline-none text-sm"
            />
            <button
              onClick={handleChatMessage}
              disabled={!chatInput.trim()}
              className="bg-blue-400 text-black px-4 py-2 rounded font-medium hover:bg-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Send
            </button>
          </div>

          {/* Quick Questions */}
          <div>
            <p className="text-gray-400 text-xs mb-2">Quick questions:</p>
            <div className="flex flex-wrap gap-1">
              {getQuickQuestions()
                .slice(0, 3)
                .map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickQuestion(question)}
                    className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded text-xs transition-colors"
                  >
                    {question}
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      {!showChatWindow && (
        <div className="text-center py-2">
          <p className="text-gray-400 text-sm">
            Have questions? Chat with our AI assistant anytime!
          </p>
        </div>
      )}
    </div>
  );
};

// Agent Modal Component
const AgentModal = ({ agent, isOpen, onClose, onSelectFlow }) => {
  if (!isOpen || !agent) return null;

  // Get agent-specific descriptions for the modal
  const getAgentModalDescription = (agentName) => {
    switch (agentName) {
      case "Family-Based Agent":
        return {
          chatDescription:
            "Start a conversation with our AI agent. Get instant answers about family-based immigration, I-130 petitions, adjustment of status, and relationship evidence requirements.",
          reviewDescription:
            "Get a comprehensive review of your family-based immigration case with document analysis, evidence evaluation, and personalized recommendations from our AI immigration expert.",
        };
      case "Work Visa Agent":
        return {
          chatDescription:
            "Ask questions about H-1B, L-1, O-1, TN visas, and employment-based immigration. Get guidance on labor certifications, employer requirements, and visa processes.",
          reviewDescription:
            "Get a thorough analysis of your work visa petition including evidence coaching, document review, and expert recommendations to strengthen your employment-based case.",
        };
      case "Asylum & Protection Agent":
        return {
          chatDescription:
            "Confidential assistance with asylum, VAWA, and humanitarian relief cases. Get guidance on personal statements, evidence requirements, and protection applications.",
          reviewDescription:
            "Comprehensive case review with trauma-informed approach. Our AI will help build your personal statement, organize evidence, and strengthen your protection case.",
        };
      case "Student & Exchange Visa Agent":
        return {
          chatDescription:
            "Get help with F-1, J-1, M-1 visas, OPT applications, and student status questions. Ask about school transfers, work authorization, and academic requirements.",
          reviewDescription:
            "Complete review of your student visa application including personal statement building, financial documentation analysis, and interview preparation guidance.",
        };
      case "Citizenship Agent":
        return {
          chatDescription:
            "Ask questions about naturalization eligibility, N-400 applications, civics test preparation, and citizenship requirements. Get guidance on the naturalization process.",
          reviewDescription:
            "Comprehensive eligibility assessment and application review. Our AI will analyze your travel history, background, and documentation to prepare your naturalization case.",
        };
      case "DACA / TPS Renewal Agent":
        return {
          chatDescription:
            "Get help with DACA and TPS renewal questions, deadlines, requirements, and documentation. Ask about eligibility changes and renewal procedures.",
          reviewDescription:
            "Complete renewal packet preparation and review. Our AI will ensure your DACA or TPS renewal application is complete and submitted on time.",
        };
      case "RFE / NOID Response Agent":
        return {
          chatDescription:
            "Get immediate help understanding your USCIS notice, RFE requirements, or NOID concerns. Ask about deadlines, evidence requirements, and response strategies.",
          reviewDescription:
            "Comprehensive analysis of your USCIS notice with strategic response planning. Our AI will help you understand requirements and build a strong response packet.",
        };
      default:
        return {
          chatDescription:
            "Start a conversation with our AI agent. Get instant answers to your specific immigration questions.",
          reviewDescription:
            "Get a comprehensive review of your case with document analysis, evidence evaluation, and personalized recommendations.",
        };
    }
  };

  const descriptions = getAgentModalDescription(agent.name);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <span className="text-3xl mr-4">{agent.icon}</span>
              <div>
                <h2 className="text-2xl font-bold text-white">{agent.name}</h2>
                <p className="text-gray-400">{agent.description}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>

          {/* Agent Features */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">
              What I can help you with:
            </h3>
            <ul className="space-y-2">
              {agent.features.map((feature, index) => (
                <li key={index} className="flex items-start text-gray-300">
                  <span className="text-emerald-400 mr-2">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Flow Options */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              Choose how you'd like to proceed:
            </h3>

            <button
              onClick={() => onSelectFlow("chat")}
              className="w-full bg-gray-800 hover:bg-gray-700 p-6 rounded-lg text-left transition-colors border-2 border-gray-700 hover:border-emerald-400"
            >
              <div className="flex items-center mb-2">
                <span className="text-2xl mr-3">💬</span>
                <span className="text-xl font-semibold text-white">
                  Ask Questions
                </span>
              </div>
              <p className="text-gray-400">{descriptions.chatDescription}</p>
            </button>

            {/* Review My Case - Feature Access Controlled */}
            <FeatureAccessControl 
              feature="review_case_modal"
              fallbackContent={
                <div className="w-full relative">
                  <button
                    disabled
                    className="w-full bg-gray-800/50 p-6 rounded-lg text-left transition-colors border-2 border-gray-600 opacity-50 cursor-not-allowed relative"
                  >
                    <div className="absolute top-2 right-2 text-yellow-500">
                      🔒
                    </div>
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-3">📋</span>
                      <span className="text-xl font-semibold text-white">
                        Review My Case
                      </span>
                      <span className="ml-2 text-sm bg-yellow-600 text-black px-2 py-1 rounded">
                        PREMIUM
                      </span>
                    </div>
                    <p className="text-gray-500">
                      Available on Navigator ($39/month) and higher plans. Upgrade to access comprehensive case review features.
                    </p>
                  </button>
                </div>
              }
            >
              <button
                onClick={() => onSelectFlow("review")}
                className="w-full bg-emerald-900/20 hover:bg-emerald-900/30 p-6 rounded-lg text-left transition-colors border-2 border-emerald-400"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-3">📋</span>
                  <span className="text-xl font-semibold text-white">
                    Review My Case
                  </span>
                </div>
                <p className="text-gray-400">{descriptions.reviewDescription}</p>
              </button>
            </FeatureAccessControl>
          </div>
        </div>
      </div>
    </div>
  );
};

// Family-Based Agent Flow Component
const FamilyBasedAgentFlow = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    caseType: "",
    petitionerDetails: {},
    beneficiaryDetails: {},
    relationshipDetails: {},
    documents: [],
    analysisResults: {},
    recommendations: [],
    finalScore: 0,
  });

  const steps = [
    "Welcome & Case Type Selection",
    "Profile + Relationship Intake",
    "Document Upload",
    "Smart Evidence Analysis",
    "Recommendations & Suggestions",
    "User Correction / Addition Loop",
    "Final Review & Assembly",
    "Re-Analysis (if needed)",
    "Final Confirmation & Generation",
  ];

  const handleStepNavigation = (stepType) => {
    if (stepType === "reanalysis") {
      setCurrentStep(7); // Go to re-analysis step
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  // When used in full-screen mode, isOpen might be undefined
  const showComponent = isOpen !== false;

  if (!showComponent) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <span className="text-3xl mr-4">👨‍👩‍👧‍👦</span>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  Family-Based Agent
                </h2>
                <p className="text-gray-400">Case Review Process</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-400">
                Step {currentStep + 1} of {steps.length}
              </span>
              <span className="text-sm text-gray-400">
                {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2">
              <div
                className="bg-emerald-400 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentStep + 1) / steps.length) * 100}%`,
                }}
              ></div>
            </div>
          </div>

          {/* Steps Navigation */}
          <div className="mb-8">
            <div className="flex flex-wrap justify-center gap-2">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`text-xs px-3 py-1 rounded-full transition-colors ${
                    index === currentStep
                      ? "bg-emerald-400 text-black"
                      : index < currentStep
                      ? "bg-emerald-600 text-white"
                      : "bg-gray-700 text-gray-400"
                  }`}
                >
                  {step}
                </div>
              ))}
            </div>
          </div>

          {/* Current Step Content */}
          {currentStep === 0 && (
            <Step1CaseTypeSelection
              formData={formData}
              setFormData={setFormData}
              onNext={() => setCurrentStep(1)}
            />
          )}
          {currentStep === 1 && (
            <Step2ProfileIntake
              formData={formData}
              setFormData={setFormData}
              onNext={() => setCurrentStep(2)}
              onBack={() => setCurrentStep(0)}
            />
          )}
          {currentStep === 2 && (
            <Step3DocumentUpload
              formData={formData}
              setFormData={setFormData}
              onNext={() => setCurrentStep(3)}
              onBack={() => setCurrentStep(1)}
            />
          )}
          {currentStep === 3 && (
            <Step4EvidenceAnalysis
              formData={formData}
              setFormData={setFormData}
              onNext={() => setCurrentStep(4)}
              onBack={() => setCurrentStep(2)}
            />
          )}
          {currentStep === 4 && (
            <Step5Recommendations
              formData={formData}
              setFormData={setFormData}
              onNext={() => setCurrentStep(5)}
              onBack={() => setCurrentStep(3)}
            />
          )}
          {currentStep === 5 && (
            <Step6UserCorrection
              formData={formData}
              setFormData={setFormData}
              onNext={() => setCurrentStep(6)}
              onBack={() => setCurrentStep(4)}
            />
          )}
          {currentStep === 6 && (
            <Step7FinalReview
              formData={formData}
              setFormData={setFormData}
              onNext={() => setCurrentStep(7)}
              onBack={() => setCurrentStep(5)}
            />
          )}
          {currentStep === 7 && (
            <Step8FinalConfirmation
              formData={formData}
              setFormData={setFormData}
              onComplete={onClose}
              onBack={() => setCurrentStep(6)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Step 1: Welcome & Case Type Selection (Updated)
const Step1CaseTypeSelection = ({ formData, setFormData, onNext }) => {
  const [selectedCaseType, setSelectedCaseType] = useState(
    formData.caseType || ""
  );
  const [screeningAnswers, setScreeningAnswers] = useState(
    formData.screeningAnswers || {}
  );

  const caseTypes = [
    {
      id: "i130",
      title: "I-130 Petition for Alien Relative",
      description: "For spouse, parent, or child",
      icon: "💑",
      processingTime: "12-18 months",
      category: "Immediate Relative",
    },
    {
      id: "i751",
      title: "I-751 Removal of Conditions",
      description: "Conditional permanent residence",
      icon: "🔄",
      processingTime: "8-12 months",
      category: "Status Adjustment",
    },
    {
      id: "i485",
      title: "I-485 Adjustment of Status",
      description: "Green card application",
      icon: "🏠",
      processingTime: "10-16 months",
      category: "Status Adjustment",
    },
    {
      id: "k1",
      title: "K-1 Fiancé(e) Visa",
      description: "Engagement-based immigration",
      icon: "💍",
      processingTime: "6-12 months",
      category: "Fiancé Visa",
    },
  ];

  const enhancedScreeningQuestions = [
    {
      id: "relative_type",
      question: "Are you an immediate relative or in a preference category?",
      type: "radio",
      options: [
        "Immediate Relative (spouse, parent, unmarried child under 21)",
        "Preference Category (other family relationships)",
      ],
    },
    {
      id: "processing_times",
      question: "Have you checked current USCIS processing times?",
      type: "radio",
      options: [
        "Yes, I understand current wait times",
        "No, I need guidance on processing times",
      ],
    },
    {
      id: "previous_filing",
      question: "Have you filed this type of petition before?",
      type: "radio",
      options: [
        "No, this is my first time",
        "Yes, and it was approved",
        "Yes, but it was denied",
        "Yes, but it was withdrawn",
      ],
    },
    {
      id: "rfe_noid",
      question: "Is this a response to an RFE or NOID?",
      type: "radio",
      options: [
        "No, this is an initial filing",
        "Yes, responding to RFE",
        "Yes, responding to NOID",
      ],
    },
    {
      id: "complications",
      question: "Do you have a criminal record or previous denials?",
      type: "radio",
      options: [
        "No complications",
        "Minor criminal history",
        "Previous immigration denials",
        "Both criminal history and denials",
      ],
    },
  ];

  const handleScreeningAnswer = (questionId, answer) => {
    setScreeningAnswers({
      ...screeningAnswers,
      [questionId]: answer,
    });
  };

  const handleContinue = () => {
    if (!selectedCaseType) {
      alert("Please select a case type to continue");
      return;
    }

    const incompleteQuestions = enhancedScreeningQuestions.filter(
      (q) => !screeningAnswers[q.id]
    );
    if (incompleteQuestions.length > 0) {
      alert("Please answer all screening questions to continue");
      return;
    }

    setFormData({
      ...formData,
      caseType: selectedCaseType,
      screeningAnswers,
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      {/* Enhanced Welcome Header */}
      <div className="text-center mb-8 bg-gradient-to-r from-emerald-900/20 to-blue-900/20 p-8 rounded-lg border border-emerald-400/30">
        <div className="text-6xl mb-4">🌟</div>
        <h3 className="text-4xl font-bold text-white mb-4">
          Welcome to Your Immigration Journey
        </h3>
        <p className="text-gray-300 text-lg mb-4">
          AI-powered case review and document preparation
        </p>
        <div className="flex justify-center items-center space-x-6 text-sm">
          <div className="flex items-center">
            <span className="text-emerald-400 mr-2">✓</span>
            <span className="text-gray-300">USCIS Expert Analysis</span>
          </div>
          <div className="flex items-center">
            <span className="text-emerald-400 mr-2">✓</span>
            <span className="text-gray-300">Personalized Recommendations</span>
          </div>
          <div className="flex items-center">
            <span className="text-emerald-400 mr-2">✓</span>
            <span className="text-gray-300">Professional Documents</span>
          </div>
        </div>
      </div>

      {/* Interactive Progress Indicator */}
      <div className="bg-gray-800 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white font-medium">Progress</span>
          <span className="text-gray-400 text-sm">
            {selectedCaseType ? "25%" : "0%"} Complete
          </span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div
            className="bg-emerald-400 h-2 rounded-full transition-all duration-500"
            style={{ width: selectedCaseType ? "25%" : "0%" }}
          ></div>
        </div>
      </div>

      {/* Interactive Case Type Selection */}
      <div className="space-y-4">
        <div className="text-center">
          <h4 className="text-2xl font-semibold text-white mb-2">
            Choose Your Case Type
          </h4>
          <p className="text-gray-400">
            Select the immigration petition that best matches your situation
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {caseTypes.map((caseType) => (
            <button
              key={caseType.id}
              onClick={() => setSelectedCaseType(caseType.id)}
              className={`p-6 rounded-lg text-left transition-all duration-300 border-2 transform hover:scale-105 ${
                selectedCaseType === caseType.id
                  ? "border-emerald-400 bg-emerald-900/20 shadow-lg shadow-emerald-400/20"
                  : "border-gray-700 bg-gray-800 hover:border-emerald-400 hover:bg-gray-700"
              }`}
            >
              <div className="flex items-center mb-3">
                <span className="text-3xl mr-3">{caseType.icon}</span>
                <div>
                  <h4 className="text-lg font-semibold text-white">
                    {caseType.title}
                  </h4>
                  <p className="text-gray-400 text-sm">
                    {caseType.description}
                  </p>
                </div>
              </div>
              <div className="text-xs text-gray-500 flex items-center justify-between">
                <span className="bg-gray-700 px-2 py-1 rounded">
                  {caseType.category}
                </span>
                <span className="text-blue-400">
                  ⏱️ {caseType.processingTime}
                </span>
              </div>
              {selectedCaseType === caseType.id && (
                <div className="mt-3 text-emerald-400 text-sm flex items-center">
                  <span className="mr-2">✓</span>
                  Selected - Continue below to answer screening questions
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Enhanced Interactive Screening Questions */}
      {selectedCaseType && (
        <div className="space-y-6 bg-gradient-to-r from-gray-800 to-gray-900 p-6 rounded-lg border border-gray-700">
          <div className="text-center mb-6">
            <h4 className="text-2xl font-semibold text-white mb-2">
              📋 Case Screening Questions
            </h4>
            <p className="text-gray-400">
              Help us understand your specific situation for better guidance
            </p>
            <div className="mt-4">
              <div className="text-sm text-gray-400">
                {Object.keys(screeningAnswers).length} of{" "}
                {enhancedScreeningQuestions.length} questions completed
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                <div
                  className="bg-blue-400 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${
                      (Object.keys(screeningAnswers).length /
                        enhancedScreeningQuestions.length) *
                      100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          </div>

          {enhancedScreeningQuestions.map((question, qIndex) => (
            <div
              key={question.id}
              className="space-y-3 bg-gray-800 p-4 rounded-lg border border-gray-700"
            >
              <div className="flex items-center mb-3">
                <span className="text-2xl mr-3">{qIndex + 1}.</span>
                <h5 className="text-white font-medium">{question.question}</h5>
                {screeningAnswers[question.id] && (
                  <span className="ml-auto text-emerald-400 text-sm">✓</span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {question.options.map((option, index) => (
                  <label
                    key={index}
                    className={`flex items-center p-3 rounded border-2 cursor-pointer transition-all duration-200 hover:bg-gray-700 ${
                      screeningAnswers[question.id] === option
                        ? "border-emerald-400 bg-emerald-900/20"
                        : "border-gray-600 bg-gray-800"
                    }`}
                  >
                    <input
                      type="radio"
                      name={question.id}
                      value={option}
                      checked={screeningAnswers[question.id] === option}
                      onChange={() =>
                        handleScreeningAnswer(question.id, option)
                      }
                      className="mr-3 text-emerald-400"
                    />
                    <span className="text-white text-sm">{option}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hybrid Chat Assistant */}
      <HybridChatAssistant formData={formData} stepContext="welcome" />

      {/* Navigation */}
      <div className="flex justify-end">
        <button
          onClick={handleContinue}
          disabled={!selectedCaseType}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            selectedCaseType
              ? "bg-emerald-400 text-black hover:bg-emerald-300"
              : "bg-gray-700 text-gray-400 cursor-not-allowed"
          }`}
        >
          Continue to Profile Setup →
        </button>
      </div>
    </div>
  );
};

// Step 2: Profile + Relationship Intake (Updated)
const Step2ProfileIntake = ({ formData, setFormData, onNext, onBack }) => {
  const [petitionerDetails, setPetitionerDetails] = useState(
    formData.petitionerDetails || {}
  );
  const [beneficiaryDetails, setBeneficiaryDetails] = useState(
    formData.beneficiaryDetails || {}
  );
  const [relationshipDetails, setRelationshipDetails] = useState(
    formData.relationshipDetails || {}
  );
  const [completionFlags, setCompletionFlags] = useState({});

  // Auto-generate timeline feature
  const generateTimeline = () => {
    const events = [];
    if (relationshipDetails.metDate)
      events.push({ date: relationshipDetails.metDate, event: "First met" });
    if (relationshipDetails.firstDate)
      events.push({ date: relationshipDetails.firstDate, event: "First date" });
    if (relationshipDetails.engagementDate)
      events.push({
        date: relationshipDetails.engagementDate,
        event: "Engagement",
      });
    if (relationshipDetails.marriageDate)
      events.push({
        date: relationshipDetails.marriageDate,
        event: "Marriage",
      });
    if (relationshipDetails.cohabitationStart)
      events.push({
        date: relationshipDetails.cohabitationStart,
        event: "Started living together",
      });

    return events.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Check consistency feature
  const checkConsistency = () => {
    const issues = [];
    if (relationshipDetails.marriageDate && relationshipDetails.metDate) {
      const metDate = new Date(relationshipDetails.metDate);
      const marriageDate = new Date(relationshipDetails.marriageDate);
      if (marriageDate < metDate) {
        issues.push("Marriage date cannot be before you met");
      }
    }
    return issues;
  };

  const handleContinue = () => {
    // Flag missing information
    const missingInfo = [];
    if (!petitionerDetails.fullName) missingInfo.push("Petitioner full name");
    if (!beneficiaryDetails.fullName) missingInfo.push("Beneficiary full name");
    if (!relationshipDetails.howMet) missingInfo.push("How you met");

    if (missingInfo.length > 0) {
      alert(
        `Please complete the following required fields: ${missingInfo.join(
          ", "
        )}`
      );
      return;
    }

    const consistencyIssues = checkConsistency();
    if (consistencyIssues.length > 0) {
      alert(
        `Please resolve these consistency issues: ${consistencyIssues.join(
          ", "
        )}`
      );
      return;
    }

    setFormData({
      ...formData,
      petitionerDetails,
      beneficiaryDetails,
      relationshipDetails,
      timeline: generateTimeline(),
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">
          Profile & Relationship Details
        </h3>
        <p className="text-gray-400 text-lg">
          Collect core petitioner/beneficiary info and relationship timeline
        </p>
      </div>

      {/* Petitioner Details */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4 flex items-center">
          <span className="text-2xl mr-3">👤</span>
          Petitioner Details
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">
              Full Legal Name *
            </label>
            <input
              type="text"
              placeholder="Full Legal Name"
              value={petitionerDetails.fullName || ""}
              onChange={(e) =>
                setPetitionerDetails({
                  ...petitionerDetails,
                  fullName: e.target.value,
                })
              }
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">
              Immigration Status *
            </label>
            <select
              value={petitionerDetails.status || ""}
              onChange={(e) =>
                setPetitionerDetails({
                  ...petitionerDetails,
                  status: e.target.value,
                })
              }
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-emerald-400"
            >
              <option value="">Select Status</option>
              <option value="us-citizen">U.S. Citizen</option>
              <option value="permanent-resident">Permanent Resident</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Current Address</label>
            <input
              type="text"
              placeholder="Current Address"
              value={petitionerDetails.address || ""}
              onChange={(e) =>
                setPetitionerDetails({
                  ...petitionerDetails,
                  address: e.target.value,
                })
              }
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">
              Employment Status
            </label>
            <input
              type="text"
              placeholder="Employment Status"
              value={petitionerDetails.employment || ""}
              onChange={(e) =>
                setPetitionerDetails({
                  ...petitionerDetails,
                  employment: e.target.value,
                })
              }
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Annual Income</label>
            <input
              type="number"
              placeholder="Annual Income"
              value={petitionerDetails.income || ""}
              onChange={(e) =>
                setPetitionerDetails({
                  ...petitionerDetails,
                  income: e.target.value,
                })
              }
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
            />
          </div>
        </div>
      </div>

      {/* Beneficiary Details */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4 flex items-center">
          <span className="text-2xl mr-3">👥</span>
          Beneficiary Details
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">
              Full Legal Name *
            </label>
            <input
              type="text"
              placeholder="Full Legal Name"
              value={beneficiaryDetails.fullName || ""}
              onChange={(e) =>
                setBeneficiaryDetails({
                  ...beneficiaryDetails,
                  fullName: e.target.value,
                })
              }
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">
              Current Status in U.S.
            </label>
            <select
              value={beneficiaryDetails.status || ""}
              onChange={(e) =>
                setBeneficiaryDetails({
                  ...beneficiaryDetails,
                  status: e.target.value,
                })
              }
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-emerald-400"
            >
              <option value="">Select Status</option>
              <option value="visitor">Visitor (B-1/B-2)</option>
              <option value="student">Student (F-1)</option>
              <option value="work">Work Visa (H-1B, L-1, etc.)</option>
              <option value="refugee">Refugee/Asylum</option>
              <option value="undocumented">Undocumented</option>
              <option value="outside-us">Outside the U.S.</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-300 mb-2">
              Country of Origin
            </label>
            <input
              type="text"
              placeholder="Country of Origin"
              value={beneficiaryDetails.country || ""}
              onChange={(e) =>
                setBeneficiaryDetails({
                  ...beneficiaryDetails,
                  country: e.target.value,
                })
              }
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Current Location</label>
            <input
              type="text"
              placeholder="Current Location"
              value={beneficiaryDetails.location || ""}
              onChange={(e) =>
                setBeneficiaryDetails({
                  ...beneficiaryDetails,
                  location: e.target.value,
                })
              }
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
            />
          </div>
        </div>
      </div>

      {/* Relationship Timeline */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4 flex items-center">
          <span className="text-2xl mr-3">💕</span>
          Relationship Timeline
        </h4>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">How You Met *</label>
              <textarea
                placeholder="Describe how and when you first met..."
                value={relationshipDetails.howMet || ""}
                onChange={(e) =>
                  setRelationshipDetails({
                    ...relationshipDetails,
                    howMet: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                rows="3"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">
                Dating Milestones
              </label>
              <textarea
                placeholder="First date, becoming exclusive, moving in together..."
                value={relationshipDetails.milestones || ""}
                onChange={(e) =>
                  setRelationshipDetails({
                    ...relationshipDetails,
                    milestones: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                rows="3"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">Date You Met</label>
              <input
                type="date"
                value={relationshipDetails.metDate || ""}
                onChange={(e) =>
                  setRelationshipDetails({
                    ...relationshipDetails,
                    metDate: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-emerald-400"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">First Date</label>
              <input
                type="date"
                value={relationshipDetails.firstDate || ""}
                onChange={(e) =>
                  setRelationshipDetails({
                    ...relationshipDetails,
                    firstDate: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-emerald-400"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">
                Engagement Date
              </label>
              <input
                type="date"
                value={relationshipDetails.engagementDate || ""}
                onChange={(e) =>
                  setRelationshipDetails({
                    ...relationshipDetails,
                    engagementDate: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-emerald-400"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Marriage Date</label>
              <input
                type="date"
                value={relationshipDetails.marriageDate || ""}
                onChange={(e) =>
                  setRelationshipDetails({
                    ...relationshipDetails,
                    marriageDate: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-emerald-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">
                Cohabitation History
              </label>
              <textarea
                placeholder="When did you start living together? Address history..."
                value={relationshipDetails.cohabitation || ""}
                onChange={(e) =>
                  setRelationshipDetails({
                    ...relationshipDetails,
                    cohabitation: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                rows="3"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">
                Children or Dependents
              </label>
              <textarea
                placeholder="List children from this relationship, step-children, other dependents..."
                value={relationshipDetails.children || ""}
                onChange={(e) =>
                  setRelationshipDetails({
                    ...relationshipDetails,
                    children: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                rows="3"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Auto-Generated Timeline Preview */}
      {generateTimeline().length > 0 && (
        <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">
            Auto-Generated Timeline
          </h4>
          <div className="space-y-2">
            {generateTimeline().map((event, index) => (
              <div key={index} className="flex items-center text-gray-300">
                <span className="text-emerald-400 mr-2">📅</span>
                <span className="font-medium mr-2">{event.date}:</span>
                <span>{event.event}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Consistency Check Results */}
      {checkConsistency().length > 0 && (
        <div className="bg-red-900/20 border border-red-400 p-4 rounded-lg">
          <h4 className="text-white font-medium mb-2">
            ⚠️ Consistency Issues Detected
          </h4>
          <ul className="space-y-1">
            {checkConsistency().map((issue, index) => (
              <li key={index} className="text-red-300 text-sm">
                • {issue}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Hybrid Chat Assistant */}
      <HybridChatAssistant formData={formData} stepContext="profile" />

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Case Type
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium bg-emerald-400 text-black hover:bg-emerald-300 transition-colors"
        >
          Continue to Document Upload →
        </button>
      </div>
    </div>
  );
};

// Success Stories Component
const SuccessStories = () => {
  const testimonials = [
    {
      name: "Maria Rodriguez",
      caseType: "I-130 Family Petition",
      result: "Approved in 8 months",
      quote:
        "The AI agent helped us identify missing evidence and strengthen our relationship proof. The personalized recommendations saved us thousands in attorney fees.",
      rating: 5,
    },
    {
      name: "Ahmed Hassan",
      caseType: "H-1B Work Visa",
      result: "Approved without RFE",
      quote:
        "The Work Visa Agent guided me through the complex requirements and helped prepare a comprehensive petition that impressed USCIS.",
      rating: 5,
    },
    {
      name: "Sophie Chen",
      caseType: "N-400 Naturalization",
      result: "Passed citizenship test",
      quote:
        "The interview prep was incredible! I felt confident walking into my citizenship test. The AI coaching covered everything I needed to know.",
      rating: 5,
    },
  ];

  return (
    <div className="px-6 py-16">
      <div className="max-w-4xl mx-auto">
        {/* Simple Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Success Stories
          </h2>
          <p className="text-gray-400">Real results from our users</p>
        </div>

        {/* Simple Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="bg-gray-900/50 border border-gray-700 rounded-lg p-6 hover:border-[#00fba6]/50 transition-colors duration-200"
            >
              {/* Rating */}
              <div className="flex items-center mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <IoStar key={i} className="w-4 h-4 text-[#00fba6] mr-1" />
                ))}
              </div>

              {/* Quote */}
              <p className="text-gray-300 text-sm leading-relaxed mb-4 italic">
                "{testimonial.quote}"
              </p>

              {/* Author & Case */}
              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-white font-medium text-sm">
                  {testimonial.name}
                </h4>
                <p className="text-[#00fba6] text-xs font-medium mt-1">
                  {testimonial.caseType}
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  {testimonial.result}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Simple CTA */}
        <div className="text-center mt-12">
          <button className="bg-gradient-to-r from-[#00fba6] to-[#00d9f1] text-black px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity duration-200">
            Get Started →
          </button>
        </div>
      </div>
    </div>
  );
};

export const Header = ({
  onOpenAIExpert,
  onOpenUSCISScanner,
  user,
  onAuthAction,
}) => {

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMegaOpen, setIsMegaOpen] = useState(false);
  const [isMobileMegaOpen, setIsMobileMegaOpen] = useState(false);


  const [formData, setFormData] = useState({
      fullName: '',
      email: '',
      phone: '',
      lawFirm: '',
      stateOfPractice: '',
      areasOfSpecialization: [],
      additionalInfo: ''
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMegaOpen && !event.target.closest(".mega-menu")) {
        setIsMegaOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMegaOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleMegaMenu = () => setIsMegaOpen(!isMegaOpen);
  const toggleMobileMega = () => setIsMobileMegaOpen(!isMobileMegaOpen);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      areasOfSpecialization: checked 
        ? [...prev.areasOfSpecialization, name]
        : prev.areasOfSpecialization.filter(area => area !== name)
    }));
  };


  const handleDropdownLinkClick = (href) => {
    setIsMegaOpen(false);
    setIsMobileMegaOpen(false);
    if (href.startsWith("/")) {
      window.location.href = href;
    } else {
      // Handle anchor links
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const navLinks = [
    { name: "Services", href: "#" },
    { name: "How it Works", href: "#" },
    { name: "Pricing", href: "#" },
    { name: "Support", href: "#" },
    { name: "For Attorneys", href: "#", isMega: true },
  ];

  const specializations = [
    "Family-Based",
    "Work Visa",
    "Asylum",
    "Citizenship",
    "RFE/NOID",
    "Other",
  ];
  
  const areasOfSpecialization = [
    "Family-Based",
    "Work Visa",
    "Asylum",
    "Citizenship",
    "RFE/NOID",
    "Other",
  ];

  const features = [
    {
      icon: "🔍",
      title: "AI Case Analysis",
      desc: "Get instant case strength assessments, identify potential issues early, and receive strategic recommendations powered by analysis of thousands of successful cases.",
    },
    {
      icon: "📄",
      title: "Automated Document Review",
      desc: "Upload client documents and receive comprehensive analysis, completeness checks, and recommendations for strengthening applications.",
    },
    {
      icon: "🧑",
      title: "Client Self-Service Portal",
      desc: "Empower clients with guided form completion, document preparation, and 24/7 support, reducing your administrative burden.",
    },
    {
      icon: "📬",
      title: "RFE/NOID Response Assistant",
      desc: "Quickly analyze USCIS notices, identify required evidence, and generate comprehensive response strategies with supporting documentation checklists.",
    },
    {
      icon: "📈",
      title: "Practice Analytics",
      desc: "Track case outcomes, identify success patterns, and optimize your practice with data-driven insights and performance metrics.",
    },
    {
      icon: "🔒",
      title: "Compliance & Security",
      desc: "Built with attorney-client privilege in mind, featuring bank-level security, audit trails, and full compliance with legal industry standards.",
    },
  ];

  const aiAgents = [
    "Family-Based Immigration Specialist",
    "Work Visa & Employment Expert",
    "Asylum & Protection Advocate",
    "Student & Exchange Visa Guide",
    "Naturalization & Citizenship Coach",
    "RFE/NOID Response Specialist",
    "DACA/TPS Renewal Expert",
    "Visitor & Tourist Visa Assistant",
  ];

   const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔵 Attorney waitlist form submission started');
    console.log('🔵 Form data:', formData);
    
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      // Validate required fields
      const requiredFields = {
        fullName: 'Full Name',
        email: 'Email Address',
        lawFirm: 'Law Firm/Practice Name',
        stateOfPractice: 'State of Practice'
      };

      for (const [field, label] of Object.entries(requiredFields)) {
        if (!formData[field].trim()) {
          console.log('❌ Validation failed for:', field);
          setSubmitStatus({
            type: 'error',
            message: `${label} is required`
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        console.log('❌ Email validation failed');
        setSubmitStatus({
          type: 'error',
          message: 'Please enter a valid email address'
        });
        setIsSubmitting(false);
        return;
      }

      console.log('✅ Validation passed, submitting to backend');

      // Submit to backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      console.log('🔵 Backend URL:', backendUrl);
      
      const response = await fetch(`${backendUrl}/api/attorney/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      console.log('🔵 Response status:', response.status);
      
      const data = await response.json();
      console.log('🔵 Response data:', data);

      if (response.ok) {
        console.log('✅ Submission successful!');
        setSubmitStatus({
          type: 'success',
          message: 'Thank you for your interest! We\'ll be in touch soon with early access details.'
        });
        
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          lawFirm: '',
          stateOfPractice: '',
          areasOfSpecialization: [],
          additionalInfo: ''
        });
      } else {
        console.log('❌ Submission failed:', data.detail);
        setSubmitStatus({
          type: 'error',
          message: data.detail || 'An error occurred. Please try again.'
        });
      }
    } catch (error) {
      console.error('🔴 Attorney waitlist error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
      console.log('🔵 Form submission completed');
    }
  };

  return (
    <header className="flex items-center justify-between py-4 px-4 sm:px-8 border-b border-gray-700 bg-black relative z-50">
      <a
        href="/"
        className="text-2xl sm:text-3xl text-[#54edb6] z-20 font-bold"
      >
        Immigrantvli
      </a>

      {/* Desktop Nav */}
      <nav className="hidden lg:flex items-center gap-8 relative">
        {navLinks.map((link, index) =>
          link.isMega ? (
            <div key={index} className="relative mega-menu">
              <button
                className="text-lg cursor-pointer hover:text-[#54edb6] transition-colors duration-200 flex items-center gap-1"
                onClick={toggleMegaMenu}
              >
                {link.name}
                <svg
                  className={`w-4 h-4 transition-transform duration-300 ${
                    isMegaOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          ) : (
            <p className="text-lg" key={index}>
              <a
                href={link.href}
                className="hover:text-[#54edb6] transition-colors duration-200"
              >
                {link.name}
              </a>
            </p>
          )
        )}

        {/* Authentication Buttons */}
        <div className="flex items-center gap-4">
          {user ? (
            <>
              <button
                onClick={() => handleDropdownLinkClick("/profile")}
                className="text-lg hover:text-[#54edb6] transition-colors duration-200"
              >
                Profile
              </button>
              <button
                onClick={onAuthAction}
                className="bg-gradient-to-r from-[#54edb6] to-[#54edb6]/80 text-black font-semibold px-4 py-2 rounded-lg hover:from-[#54edb6]/90 hover:to-[#54edb6]/70 transition-all duration-200"
              >
                Log Out
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onAuthAction}
                className="text-lg hover:text-[#54edb6] transition-colors duration-200"
              >
                Sign In
              </button>
              <button
                onClick={() => onAuthAction("signup")}
                className="bg-gradient-to-r from-[#54edb6] to-[#54edb6]/80 text-black font-semibold px-4 py-2 rounded-lg hover:from-[#54edb6]/90 hover:to-[#54edb6]/70 transition-all duration-200"
              >
                Sign Up
              </button>
            </>
          )}
        </div>
        {isMegaOpen && (
          <div
            className="absolute top-full left-1/2 transform -translate-x-1/2 transition-all duration-500 ease-in-out opacity-100 visible mt-2 translate-y-0 mega-menu"
            style={{
              width: "90vw",
              maxWidth: "1400px",
              zIndex: 1000,
              marginLeft: "calc(-50vw + 50%)",
            }}
          >
            <div className="w-full bg-[#0b0a0f] backdrop-blur-xl border border-[#54edb6]/20 shadow-2xl rounded-lg mt-2">
              <div className="px-12 py-8">
                {/* Header Section */}
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-[#54edb6] mb-3">
                    Immigrantvli for Attorneys
                  </h2>
                  <p className="text-gray-300 text-lg max-w-4xl mx-auto">
                    Immigrantvli's AI-powered platform helps immigration
                    attorneys streamline case management, improve client
                    outcomes, and scale their practice efficiently.
                  </p>
                </div>

                {/* Row 1: Comprehensive Features - All cards in one row */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-[#54edb6] mb-6 flex items-center justify-center gap-2">
                    <span className="w-2 h-2 bg-[#54edb6] rounded-full"></span>
                    Comprehensive Features
                  </h3>
                  <div className="grid grid-cols-3 gap-6">
                    {features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="p-6 rounded-lg hover:bg-white/5 transition-colors duration-200 border border-gray-800 hover:border-[#54edb6]/30"
                      >
                        <div className="flex flex-col items-center text-center mb-3">
                          <span className="text-2xl mb-3">{feature.icon}</span>
                          <h4 className="text-white font-semibold text-base mb-3">
                            {feature.title}
                          </h4>
                        </div>
                        <p className="text-gray-400 text-sm leading-relaxed text-center">
                          {feature.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Row 2: AI Agents and Form side by side */}
                <div className="grid grid-cols-2 gap-12">
                  {/* AI Agents Column */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-[#54edb6] mb-6 flex items-center gap-2">
                      <span className="w-2 h-2 bg-[#54edb6] rounded-full"></span>
                      Specialized AI Agents for Every Case Type
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {aiAgents.map((agent, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors duration-200"
                        >
                          <div className="w-2 h-2 bg-[#54edb6] rounded-full flex-shrink-0"></div>
                          <span className="text-gray-300 text-sm">{agent}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 p-4 bg-gradient-to-r from-[#54edb6]/10 to-[#54edb6]/5 rounded-lg border border-[#54edb6]/20">
                      <p className="text-gray-300 text-sm italic leading-relaxed">
                        "You remain the legal expert. We just help users get
                        organized, confident, and prepared."
                      </p>
                    </div>
                  </div>

                  {/* Waitlist Form Column */}
                  <div>
                    <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-lg border border-[#54edb6]/20 rounded-xl p-6">
                      <h3 className="text-xl font-semibold text-[#54edb6] mb-6 text-center">
                        Join the Waitlist
                      </h3>
                     <form className="space-y-6" onSubmit={handleSubmit}>
                {/* Status Message */}
                {submitStatus.message && (
                  <div className={`p-4 rounded-lg text-center ${
                    submitStatus.type === 'success' 
                      ? 'bg-emerald-900/30 border border-emerald-400 text-emerald-400' 
                      : 'bg-red-900/30 border border-red-400 text-red-400'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name *"
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="text"
                    name="lawFirm"
                    placeholder="Law Firm/Practice Name *"
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                    value={formData.lawFirm}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                
                <input
                  type="text"
                  name="stateOfPractice"
                  placeholder="State of Practice *"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                  value={formData.stateOfPractice}
                  onChange={handleInputChange}
                  required
                />
                
                <div>
                  <label className="block text-white mb-3 font-medium">Areas of Specialization</label>
                  <div className="grid grid-cols-2 gap-3">
                    {areasOfSpecialization.map(area => (
                      <label key={area} className="flex items-center text-gray-300 cursor-pointer py-2">
                        <input
                          type="checkbox"
                          name={area}
                          className="mr-3 accent-emerald-400 w-5 h-5 md:w-4 md:h-4"
                          checked={formData.areasOfSpecialization.includes(area)}
                          onChange={handleCheckboxChange}
                        />
                        <span className="text-base md:text-sm">{area}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <textarea
                  name="additionalInfo"
                  placeholder="Message (Optional)"
                  rows="4"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                />
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-lg font-medium transition-colors disabled:bg-emerald-700 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Joining Waitlist...
                    </div>
                  ) : (
                    'Join Waitlist'
                  )}
                </button>
                
                <p className="text-gray-400 text-sm text-center">
                  Your information is secure and will only be used to contact you about early access opportunities.
                </p>
              </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile Menu Button */}
      <button
        className="lg:hidden z-20 flex flex-col justify-center items-center w-8 h-8 space-y-1"
        onClick={toggleMenu}
        aria-label="Toggle menu"
      >
        <span
          className={`block w-6 h-0.5 bg-white transition-all duration-300 ease-in-out ${
            isMenuOpen ? "rotate-45 translate-y-1.5" : ""
          }`}
        />
        <span
          className={`block w-6 h-0.5 bg-white transition-all duration-300 ease-in-out ${
            isMenuOpen ? "opacity-0" : ""
          }`}
        />
        <span
          className={`block w-6 h-0.5 bg-white transition-all duration-300 ease-in-out ${
            isMenuOpen ? "-rotate-45 -translate-y-1.5" : ""
          }`}
        />
      </button>

      {/* Mobile Overlay */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/50 z-10 transition-opacity duration-300 ${
          isMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={toggleMenu}
      />

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed top-0 left-0 h-full w-full bg-black z-20 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Mobile Menu Header with Logo and Close Button */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 sticky top-0 bg-black z-30">
          <h1 className="text-2xl text-[#54edb6] font-bold">Immigrantvli</h1>
          <button
            onClick={toggleMenu}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors duration-200"
            aria-label="Close menu"
          >
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex flex-col px-6 pb-8">
          {/* Regular Navigation Links */}
          {navLinks
            .filter((link) => !link.isMega)
            .map((link, index) => (
              <a
                key={index}
                href={link.href}
                className="text-lg text-white hover:text-[#54edb6] transition-colors duration-200 py-6 border-b border-gray-700 text-left"
                onClick={() => {
                  toggleMenu();
                  if (link.href.startsWith("#")) {
                    handleDropdownLinkClick(link.href);
                  }
                }}
              >
                {link.name}
              </a>
            ))}

          {/* For Attorneys Dropdown in Mobile */}
          <div className="border-b border-gray-700 py-4">
            <button
              onClick={toggleMobileMega}
              className="flex items-center justify-between w-full text-lg text-white hover:text-[#54edb6] transition-colors duration-200 py-3"
            >
              For Attorneys
              <svg
                className={`w-4 h-4 transition-transform duration-300 ${
                  isMobileMegaOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Mobile Mega Menu Dropdown */}
            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                isMobileMegaOpen
                  ? "max-h-[2000px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="mt-4 space-y-6">
                <p className="text-gray-300 text-sm leading-relaxed">
                  Immigrantvli's AI-powered platform helps immigration attorneys
                  streamline case management, improve client outcomes, and scale
                  their practice efficiently.
                </p>

                {/* All Mobile Features */}
                <div>
                  <h4 className="text-md font-semibold text-[#54edb6] mb-4">
                    Comprehensive Features
                  </h4>
                  <div className="space-y-4">
                    {features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="p-4 bg-gray-900/50 rounded-lg border border-gray-800"
                      >
                        <div className="flex items-start gap-3 mb-2">
                          <span className="text-lg">{feature.icon}</span>
                          <h5 className="text-white font-semibold text-sm">
                            {feature.title}
                          </h5>
                        </div>
                        <p className="text-gray-400 text-xs leading-relaxed ml-8">
                          {feature.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-md font-semibold text-[#54edb6] mb-4">
                    Specialized AI Agents for Every Case Type
                  </h4>
                  <div className="grid grid-cols-1 gap-2">
                    {aiAgents.map((agent, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-900/50 transition-colors duration-200"
                      >
                        <div className="w-2 h-2 bg-[#54edb6] rounded-full flex-shrink-0"></div>
                        <span className="text-gray-300 text-sm">{agent}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-4 bg-gradient-to-r from-[#54edb6]/10 to-[#54edb6]/5 rounded-lg border border-[#54edb6]/20">
                    <p className="text-gray-300 text-sm italic leading-relaxed">
                      "You remain the legal expert. We just help users get
                      organized, confident, and prepared."
                    </p>
                  </div>
                </div>

                {/* Mobile Waitlist Form */}
                <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-800">
                  <h4 className="text-md font-semibold text-[#54edb6] mb-4 text-center">
                    Join the Waitlist
                  </h4>
                  {/* <form className="space-y-3">
                    <input
                      type="text"
                      placeholder="Full Name"
                      className="w-full px-3 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-[#54edb6] focus:outline-none transition-colors duration-200 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Law Firm / Practice Name"
                      className="w-full px-3 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-[#54edb6] focus:outline-none transition-colors duration-200 text-sm"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        type="email"
                        placeholder="Email Address"
                        className="w-full px-3 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-[#54edb6] focus:outline-none transition-colors duration-200 text-sm"
                      />
                      <input
                        type="tel"
                        placeholder="Phone Number"
                        className="w-full px-3 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-[#54edb6] focus:outline-none transition-colors duration-200 text-sm"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="State of Practice"
                      className="w-full px-3 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-[#54edb6] focus:outline-none transition-colors duration-200 text-sm"
                    />

                    <div>
                      <label className="block text-sm text-gray-300 mb-3">
                        Areas of Specialization
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {specializations.map((area) => (
                          <label
                            key={area}
                            className="flex items-center gap-2 text-xs text-gray-300 hover:text-white transition-colors duration-200 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              className="w-3 h-3 text-[#54edb6] bg-black/40 border-gray-600 rounded focus:ring-[#54edb6] focus:ring-1"
                            />
                            {area}
                          </label>
                        ))}
                      </div>
                    </div>
                    <textarea
                      placeholder="Message (Optional)"
                      rows={3}
                      className="w-full px-3 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-[#54edb6] focus:outline-none transition-colors duration-200 resize-none text-sm"
                    ></textarea>

                    <button
                      type="submit"
                      className="w-full bg-gradient-to-r from-[#54edb6] to-[#54edb6]/80 text-black font-semibold px-4 py-3 rounded-lg hover:from-[#54edb6]/90 hover:to-[#54edb6]/70 transition-all duration-200 text-sm"
                    >
                      Join Waitlist
                    </button>
                  </form> */}
                </div>
              </div>
            </div>
          </div>
          <div className="border-b border-gray-700 pb-6">
            {user ? (
              <>
                <button
                  onClick={() => {
                    handleDropdownLinkClick("/profile");
                    toggleMenu();
                  }}
                  className="flex items-center justify-between w-full text-lg text-white hover:text-[#54edb6] transition-colors duration-200 py-3"
                >
                  Profile
                </button>
                <button
                  onClick={() => {
                    onAuthAction();
                    toggleMenu();
                  }}
                  className="w-full bg-gradient-to-r from-[#54edb6] to-[#54edb6]/80 text-black font-semibold px-4 py-3 rounded-lg hover:from-[#54edb6]/90 hover:to-[#54edb6]/70 transition-all duration-200 mt-3"
                >
                  Log Out
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    onAuthAction("signin");
                    toggleMenu();
                  }}
                  className="w-full bg-gradient-to-r from-[#54edb6] to-[#54edb6]/80 text-black font-semibold px-4 py-3 rounded-lg hover:from-[#54edb6]/90 hover:to-[#54edb6]/70 transition-all duration-200 mt-2"
                >
                  Sign In
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
const Banner = ({ onOpenUSCISScanner, onOpenAIExpert }) => {
  return (
    <section className="w-full bg-[#ff5555] px-8 py-4">
      <div className="flex items-center justify-between max-[800px]:flex-col max-[800px]:gap-2">
        <IoIosWarning size={50} className="text-yellow-400" />
        <div className="text-center">
          <p className="text-2xl font-bold max-[1000px]:text-lg">
            Stressed About a USCIS Letter?
          </p>
          <p className="text-lg max-[1000px]:text-sm">
            Get immediate AI-powered case review and response guidance
          </p>
        </div>
        <div className="flex items-center gap-6">
          <FeatureButton
            feature="scan_uscis_notice"
            onClick={onOpenUSCISScanner}
            className="flex items-center gap-2 px-5 py-3 text-xl text-black font-bold rounded-full bg-gradient-to-r from-[#00fba6] to-[#00d9f1] max-[1000px]:text-sm max-[500px]:text-xs hover:scale-105 transition-transform"
            lockedClassName="grayscale"
          >
            Scan My USCIS Notice <MdOutlineArrowRightAlt color="black" />
          </FeatureButton>
          <FeatureButton
            feature="ai_immigration_assistant" 
            onClick={onOpenAIExpert}
            className="flex items-center gap-2 px-5 py-3 text-xl text-[#54edb6] border-2 border-[#54edb6] font-bold rounded-full max-[1000px]:text-sm max-[500px]:text-xs hover:scale-105 transition-transform"
            lockedClassName="grayscale border-gray-500 text-gray-500"
          >
            AI Immigration Expert <MdOutlineArrowRightAlt color="#54edb6" />
          </FeatureButton>
        </div>
      </div>
    </section>
  );
};
// Hero Component
const Hero = ({
  searchQuery,
  setSearchQuery,
  onOpenAIExpert,
  onOpenUSCISScanner,
  user,
  onAuthRequired,
}) => {
  // Smart search function to analyze user query and suggest appropriate agent
  const analyzeSearchQuery = (query) => {
    const lowerQuery = query.toLowerCase();

    // Define keyword mappings for each agent type
    const agentKeywords = {
      "family-based": [
        "family",
        "spouse",
        "husband",
        "wife",
        "marriage",
        "fiance",
        "fiancee",
        "i-130",
        "i130",
        "i-485",
        "i485",
        "i-751",
        "i751",
        "green card",
        "greencard",
        "permanent resident",
        "adjustment of status",
        "family petition",
        "family based",
        "parent",
        "child",
        "sibling",
        "brother",
        "sister",
        "relative",
      ],
      "work-visa": [
        "work",
        "job",
        "employment",
        "employer",
        "h1b",
        "h-1b",
        "l1",
        "l-1",
        "o1",
        "o-1",
        "work visa",
        "employment visa",
        "specialty occupation",
        "intracompany transfer",
        "extraordinary ability",
        "work permit",
        "ead",
      ],
      "asylum-protection": [
        "asylum",
        "persecution",
        "refugee",
        "protection",
        "fear",
        "violence",
        "political",
        "religious",
        "social group",
        "torture",
        "safety",
        "harm",
        "country conditions",
        "vawa",
        "trafficking",
        "domestic violence",
      ],
      "student-exchange": [
        "student",
        "school",
        "university",
        "college",
        "f1",
        "f-1",
        "j1",
        "j-1",
        "m1",
        "m-1",
        "study",
        "education",
        "opt",
        "cpt",
        "stem",
        "academic",
        "exchange",
        "student visa",
        "international student",
      ],
      citizenship: [
        "citizenship",
        "naturalization",
        "n-400",
        "n400",
        "citizen",
        "civics test",
        "english test",
        "oath ceremony",
        "citizenship test",
        "naturalize",
      ],
      "rfe-noid": [
        "rfe",
        "noid",
        "request for evidence",
        "notice of intent to deny",
        "response",
        "uscis notice",
        "letter from uscis",
        "evidence request",
        "additional evidence",
        "denial notice",
      ],
      "daca-tps": [
        "daca",
        "tps",
        "temporary protected status",
        "deferred action",
        "childhood arrivals",
        "dreamers",
        "daca renewal",
        "tps renewal",
        "work authorization",
        "advance parole",
      ],
      "visitor-tourist": [
        "visitor",
        "tourist",
        "b1",
        "b-1",
        "b2",
        "b-2",
        "visit",
        "tourism",
        "temporary visit",
        "visitor visa",
        "tourist visa",
        "business visitor",
      ],
    };

    // Score each agent based on keyword matches
    const scores = {};
    for (const [agentType, keywords] of Object.entries(agentKeywords)) {
      scores[agentType] = 0;
      keywords.forEach((keyword) => {
        if (lowerQuery.includes(keyword)) {
          scores[agentType]++;
        }
      });
    }

    // Find the agent with the highest score
    const bestMatch = Object.entries(scores).reduce(
      (best, [agent, score]) => {
        return score > best.score ? { agent, score } : best;
      },
      { agent: null, score: 0 }
    );

    return bestMatch.score > 0 ? bestMatch.agent : null;
  };

  // Handle search submission
  const handleSearch = () => {
    if (!searchQuery.trim()) return;

    // Check authentication first
    if (!user) {
      onAuthRequired("signin");
      return;
    }

    // Analyze the search query
    const suggestedAgent = analyzeSearchQuery(searchQuery);

    if (suggestedAgent) {
      // Scroll to AI Agents section and highlight the suggested agent
      const element = document.querySelector("#ai-agents-section");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });

        // Add a subtle highlight effect or show a message
        setTimeout(() => {
          alert(
            `Based on your search "${searchQuery}", we recommend our ${suggestedAgent
              .replace("-", " ")
              .replace(/\b\w/g, (l) =>
                l.toUpperCase()
              )} Agent. Look for the highlighted agent below!`
          );
        }, 1000);
      }
    } else {
      // No specific match, open general AI Immigration Expert
      onOpenAIExpert();
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="flex items-center justify-center mt-16">
      <section className="flex flex-col items-center gap-6 my-30 px-12 max-[900px]:px-0 max-[900px]:w-[80%]">
        <div
          className="flex items-center gap-2 text-white px-6 py-2 border border-[#00fba6] rounded-full bg-[#09241d] w-fit mb-5 max-[600px]:text-xs"
          style={{
            animation: "glow 2s ease-in-out infinite alternate",
          }}
        >
          🚀 10,000+ Cases Reviewed
          <span className="w-1 h-1 bg-white rounded-full inline-block"></span>
          95% Accuracy Rate
          <span className="w-1 h-1 bg-white rounded-full inline-block"></span>
          1/10th Cost of Attorney
        </div>
        <div>
          <h1 className="text-8xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white via-[#00fba6] to-[#00d9f1] relative inline-block text-center  max-[1200px]:text-7xl max-[600px]:text-5xl">
            AI Immigration Assistant
            <span
              className="block h-[4px] w-[60%] mt-6 mx-auto rounded-full"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #00fba6, #00d9f1, #00fba6)",
                backgroundSize: "200% auto",
                backgroundClip: "padding-box",
                animation: "gradientShift 2s linear infinite",
              }}
            ></span>
          </h1>
        </div>
        <p className="text-2xl text-[#8f9094] text-center max-[600px]:text-sm">
          Get your immigration case reviewed by AI trained on thousands of
          successful filings. Avoid costly errors and file with confidence.
        </p>
        <p className="text-lg text-[#8f9094] text-center max-[600px]:text-sm">
          Whether you're applying for a green card, renewing DACA, or responding
          to a NOID <br /> Immigrantvli has a specialized AI agent to guide you
          through it.
        </p>

        {/* Updated SearchBox with functionality integrated */}
        <div className="relative w-full max-w-2xl mx-auto">
          {/* Main container with relative positioning */}
          <div className="relative z-0">
            {/* Glow effect that appears on focus/hover */}
            <div
              className={`absolute inset-0 rounded-full bg-gradient-to-r from-[#00fba6]/20 to-[#00fba6]/10 blur-md transition-opacity duration-300 ${
                searchQuery ? "opacity-100" : "opacity-0"
              }`}
            ></div>

            {/* Input container */}
            <div
              className={`relative flex items-center bg-black/40 backdrop-blur-lg border transition-all duration-300 rounded-full ${
                searchQuery
                  ? "border-[#00fba6] shadow-[0_0_25px_rgba(0,251,166,0.3)]"
                  : "border-white/20 hover:border-[#00fba6]/50"
              }`}
            >
              {/* Search icon */}
              <div className="absolute left-5">
                <FiSearch
                  className={`text-xl transition-colors duration-300 ${
                    searchQuery ? "text-[#00fba6]" : "text-gray-400"
                  }`}
                />
              </div>

              {/* Actual input element */}
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="What do you need help with? (e.g., H1B visa, family petition, asylum case)"
                className="w-full py-4 pl-14 pr-6 text-white bg-transparent placeholder-gray-400 outline-none text-lg rounded-full"
              />

              {/* Search button */}
              {searchQuery && (
                <button
                  onClick={handleSearch}
                  className="absolute right-2 bg-[#00fba6] hover:bg-[#00d9f1] text-black px-4 py-2 rounded-full text-sm font-medium transition-colors"
                >
                  Search
                </button>
              )}
            </div>
          </div>
          {searchQuery && (
            <div className="absolute top-full left-0 right-0 mt-2 text-center">
              <p className="text-xs text-gray-500">
                Press Enter or click Search to find the right agent for: "
                {searchQuery}"
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-8 mt-6 max-[600px]:flex-col max-[600px]:gap-4">
          <FeatureButton
            feature="start_free_case_review"
            onClick={() => {
              // Start Free Case Review should be available to ALL users per requirements
              // Including non-authenticated users - but needs authentication for full functionality
              if (!user) {
                onAuthRequired("signin");
                return;
              }
              onOpenAIExpert();
            }}
            className="bg-gradient-to-r from-[#00fba6] to-[#00d9f1] text-xl text-black font-bold px-6 py-3 rounded-full cursor-pointer max-[600px]:text-sm max-[600px]:w-[200px]"
            showUpgradeModal={false}
          >
            Start Free Case Review
          </FeatureButton>
          <button
            onClick={() => {
              window.scrollTo({ top: 2000, behavior: "smooth" });
            }}
            className="border-2 border-[#00fba6] text-xl text-[#00fba6] font-bold px-6 py-3 rounded-full cursor-pointer max-[600px]:text-sm max-[600px]:w-[200px]"
          >
            Explore AI Agents
          </button>
        </div>

        {/* Featured Tools Section */}
        <div className="max-w-4xl mx-auto mt-16">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">
            <span className="bg-gradient-to-r from-[#00fba6] to-[#00d9f1] bg-clip-text text-transparent">
              🔥 Featured AI Tools
            </span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* AI Immigration Expert */}
            <div className="relative bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/[0.05] hover:border-[#00fba6]/30 transition-all duration-300 group">
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] via-transparent to-white/[0.03] pointer-events-none rounded-2xl"></div>

              <div className="relative z-10">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#00fba6] to-[#00d9f1] rounded-xl flex items-center justify-center mr-4">
                    <IoRocket className="w-6 h-6 text-black" />
                  </div>
                  <div className="text-left">
                    <h4 className="text-xl font-bold text-white group-hover:text-[#00fba6] transition-colors">
                      AI Immigration Expert
                    </h4>
                    <p className="text-gray-400 text-sm">
                      Comprehensive case analysis & evidence optimization
                    </p>
                  </div>
                </div>

                <p className="text-gray-300 text-sm mb-6 text-left leading-relaxed">
                  Upload your documents for AI analysis, risk detection,
                  personalized recommendations, and professional packet
                  generation.
                </p>

                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-400 space-x-4">
                    <span className="flex items-center gap-1">
                      📁 8-Step Process
                    </span>
                    <span className="flex items-center gap-1">
                      ⏱️ 30-60 min
                    </span>
                  </div>
                  <FeatureButton
                    feature="ai_immigration_assistant"
                    onClick={onOpenAIExpert}
                    className="bg-gradient-to-r from-[#00fba6] to-[#00d9f1] text-black px-6 py-2 rounded-xl hover:opacity-90 transition-all duration-200 font-medium hover:scale-105 active:scale-95"
                    lockedClassName="grayscale"
                  >
                    Try Now →
                  </FeatureButton>
                </div>
              </div>

              {/* Hover Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#00fba6]/5 to-[#00d9f1]/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </div>

            {/* USCIS Notice Scanner */}
            <div className="relative bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/[0.05] hover:border-[#00d9f1]/30 transition-all duration-300 group">
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] via-transparent to-white/[0.03] pointer-events-none rounded-2xl"></div>

              <div className="relative z-10">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#00d9f1] to-[#00fba6] rounded-xl flex items-center justify-center mr-4">
                    <IoDocument className="w-6 h-6 text-black" />
                  </div>
                  <div className="text-left">
                    <h4 className="text-xl font-bold text-white group-hover:text-[#00d9f1] transition-colors">
                      Scan my USCIS Notice
                    </h4>
                    <p className="text-gray-400 text-sm">
                      RFE/NOID response generator with AI analysis
                    </p>
                  </div>
                </div>

                <p className="text-gray-300 text-sm mb-6 text-left leading-relaxed">
                  Upload your RFE or NOID for instant AI parsing, strategic
                  response planning, and professional response packet
                  generation.
                </p>

                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-400 space-x-4">
                    <span className="flex items-center gap-1">
                      🔍 12-Step Flow
                    </span>
                    <span className="flex items-center gap-1">
                      ⏱️ 45-90 min
                    </span>
                  </div>
                  <FeatureButton
                    feature="scan_uscis_notice"
                    onClick={onOpenUSCISScanner}
                    className="bg-gradient-to-r from-[#00d9f1] to-[#00fba6] text-black px-6 py-2 rounded-xl hover:opacity-90 transition-all duration-200 font-medium hover:scale-105 active:scale-95"
                    lockedClassName="grayscale"
                  >
                    Scan Now →
                  </FeatureButton>
                </div>
              </div>

              {/* Hover Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#00d9f1]/5 to-[#00fba6]/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
// Stats Section Component (Enhanced)
const StatsSection = () => {
  const stats = [
    { number: "10,000+", label: "Cases Reviewed", icon: IoDocumentText },
    { number: "95%", label: "Accuracy Rate", icon: TfiTarget },
    { number: "1/10th", label: "Cost of Attorney", icon: IoCash },
    { number: "8", label: "AI Specialists", icon: IoRocket },
  ];

  return (
    <div className="px-6 py-20 relative">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-64 h-64 bg-[#00fba6]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-[#00d9f1]/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-[#00fba6] to-[#00d9f1] bg-clip-text text-transparent mb-6">
            Powered by Advanced AI Technology
          </h2>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center mb-16">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="relative bg-white/[0.03] backdrop-blur-md border border-white/10 p-8 rounded-2xl hover:bg-white/[0.05] hover:border-[#00fba6]/30 transition-all duration-300 group"
              >
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] via-transparent to-white/[0.03] pointer-events-none rounded-2xl"></div>

                <div className="relative z-10">
                  {/* Icon */}
                  <div className="w-16 h-16 bg-gradient-to-r from-[#00fba6] to-[#00d9f1] rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-8 h-8 text-black" />
                  </div>

                  {/* Number */}
                  <div className="text-3xl font-bold text-white mb-3 group-hover:text-[#00fba6] transition-colors duration-300">
                    {stat.number}
                  </div>

                  {/* Label */}
                  <div className="text-gray-400 text-sm uppercase tracking-wide font-medium">
                    {stat.label}
                  </div>
                </div>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#00fba6]/5 to-[#00d9f1]/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            );
          })}
        </div>

        {/* Bottom Badge */}
        <div className="text-center">
        </div>
      </div>
    </div>
  );
};

// Step 3: Document Upload (Updated)
const Step3DocumentUpload = ({ formData, setFormData, onNext, onBack }) => {
  const [uploadedDocs, setUploadedDocs] = useState(formData.documents || []);
  const [dragActive, setDragActive] = useState(false);
  const [processingDocs, setProcessingDocs] = useState([]);

  const documentCategories = [
    {
      id: "government",
      title: "Government & Legal Documents",
      icon: "🏛️",
      examples: [
        "Passports",
        "I-130 Form",
        "I-864 Affidavit of Support",
        "Marriage Certificates",
        "Birth Certificates",
        "Naturalization Certificates",
        "Previous Visas",
        "I-94 Records",
      ],
    },
    {
      id: "relationship",
      title: "Proof of Relationship",
      icon: "💕",
      examples: [
        "Dating Photos",
        "Engagement Photos",
        "Wedding Photos",
        "Family Photos",
        "Text Messages",
        "Email Communication",
        "Travel Tickets",
        "Hotel Reservations",
        "Gift Receipts",
        "Love Letters",
      ],
    },
    {
      id: "shared-life",
      title: "Proof of Shared Life",
      icon: "🏠",
      examples: [
        "Joint Bank Statements",
        "Joint Credit Cards",
        "Joint Loans",
        "Lease Agreements",
        "Mortgage Documents",
        "Utility Bills",
        "Insurance Policies",
        "Joint Tax Returns",
        "Joint Investment Accounts",
      ],
    },
    {
      id: "third-party",
      title: "Third-Party Evidence",
      icon: "👥",
      examples: [
        "Family Affidavits",
        "Friend Affidavits",
        "Employer Letters",
        "Social Media Screenshots",
        "Professional Records",
        "Community Organization Records",
        "Religious Leader Letters",
        "Neighbor Statements",
      ],
    },
    {
      id: "special-circumstances",
      title: "Special Circumstances",
      icon: "⚖️",
      examples: [
        "RFE/NOID Letters",
        "Waiver Documentation",
        "Court Records",
        "Previous Denial Letters",
        "Medical Records",
        "Psychological Evaluations",
        "Police Reports",
        "Military Records",
      ],
    },
  ];

  // Auto-categorize feature
  const autoCategorizeDocument = (fileName) => {
    const name = fileName.toLowerCase();
    if (
      name.includes("passport") ||
      name.includes("i-130") ||
      name.includes("i-864") ||
      name.includes("birth") ||
      name.includes("marriage")
    ) {
      return "government";
    }
    if (
      name.includes("photo") ||
      name.includes("message") ||
      name.includes("email") ||
      name.includes("travel")
    ) {
      return "relationship";
    }
    if (
      name.includes("bank") ||
      name.includes("tax") ||
      name.includes("lease") ||
      name.includes("utility") ||
      name.includes("insurance")
    ) {
      return "shared-life";
    }
    if (
      name.includes("affidavit") ||
      name.includes("letter") ||
      name.includes("statement")
    ) {
      return "third-party";
    }
    if (
      name.includes("rfe") ||
      name.includes("noid") ||
      name.includes("court") ||
      name.includes("waiver")
    ) {
      return "special-circumstances";
    }
    return "government"; // default
  };

  // Enhanced quality check with specific feedback
  const checkDocumentQuality = (file) => {
    const issues = [];
    const suggestions = [];

    if (file.size > 10 * 1024 * 1024) {
      // 10MB
      issues.push("File size too large - compress to under 10MB");
    }
    if (file.size < 50 * 1024) {
      // 50KB
      issues.push(
        "File size may be too small - ensure document is clearly readable"
      );
    }

    // File type specific validation
    if (file.type === "application/pdf") {
      suggestions.push("PDF format is ideal for government documents");
    } else if (file.type.startsWith("image/")) {
      if (file.name.toLowerCase().includes("photo")) {
        suggestions.push("Photo format is good for relationship evidence");
      } else {
        suggestions.push("Consider converting document scans to PDF format");
      }
    } else {
      issues.push("Unsupported file type - use PDF, JPG, or PNG");
    }

    // Content-based validation
    if (file.name.toLowerCase().includes("marriage")) {
      suggestions.push(
        "Marriage certificate - ensure it shows both names clearly"
      );
    }
    if (file.name.toLowerCase().includes("bank")) {
      suggestions.push(
        "Bank statements - ensure they show joint account activity"
      );
    }
    if (file.name.toLowerCase().includes("lease")) {
      suggestions.push(
        "Lease agreement - verify both names are listed as tenants"
      );
    }

    return { issues, suggestions };
  };

  // Duplicate detection
  const isDuplicate = (newFile) => {
    return uploadedDocs.some(
      (doc) => doc.name === newFile.name && doc.size === newFile.size
    );
  };

  // Translation needs check
  const checkTranslationNeeds = (fileName) => {
    const foreignDocKeywords = [
      "foreign",
      "spanish",
      "chinese",
      "arabic",
      "french",
      "german",
      "russian",
    ];
    return foreignDocKeywords.some((keyword) =>
      fileName.toLowerCase().includes(keyword)
    );
  };

  const handleDrop = (e, category) => {
    e.preventDefault();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    processFiles(files, category);
  };

  const handleFileInput = (e, category) => {
    const files = Array.from(e.target.files);
    processFiles(files, category);
  };

  const processFiles = (files, suggestedCategory) => {
    files.forEach((file) => {
      // Check for duplicates
      if (isDuplicate(file)) {
        alert(`File "${file.name}" already exists. Skipping duplicate.`);
        return;
      }

      // Quality check with specific feedback
      const qualityCheck = checkDocumentQuality(file);
      if (qualityCheck.issues.length > 0) {
        if (
          !confirm(
            `File "${file.name}" has issues: ${qualityCheck.issues.join(
              ", "
            )}. Continue anyway?`
          )
        ) {
          return;
        }
      }

      // Show suggestions for better document quality
      if (qualityCheck.suggestions.length > 0) {
        console.log(
          `Suggestions for "${file.name}": ${qualityCheck.suggestions.join(
            ", "
          )}`
        );
      }

      // Auto-categorize
      const autoCategory = autoCategorizeDocument(file.name);
      const finalCategory = suggestedCategory || autoCategory;

      // Check translation needs
      const needsTranslation = checkTranslationNeeds(file.name);

      const newDoc = {
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        category: finalCategory,
        uploaded: true,
        autoCategorized: suggestedCategory !== finalCategory,
        qualityIssues: qualityCheck.issues,
        suggestions: qualityCheck.suggestions,
        needsTranslation,
        uploadDate: new Date().toISOString(),
      };

      setUploadedDocs((prev) => [...prev, newDoc]);

      // Simulate processing
      setProcessingDocs((prev) => [...prev, newDoc.id]);
      setTimeout(() => {
        setProcessingDocs((prev) => prev.filter((id) => id !== newDoc.id));
      }, 2000);
    });
  };

  const removeDocument = (docId) => {
    setUploadedDocs(uploadedDocs.filter((doc) => doc.id !== docId));
  };

  const getCategoryStats = () => {
    const stats = {};
    documentCategories.forEach((cat) => {
      stats[cat.id] = uploadedDocs.filter(
        (doc) => doc.category === cat.id
      ).length;
    });
    return stats;
  };

  const handleContinue = () => {
    const stats = getCategoryStats();
    const missingCategories = documentCategories.filter(
      (cat) => stats[cat.id] === 0
    );

    if (missingCategories.length > 2) {
      if (
        !confirm(
          `You have no documents in ${missingCategories.length} categories. Continue anyway?`
        )
      ) {
        return;
      }
    }

    setFormData({
      ...formData,
      documents: uploadedDocs,
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Document Upload</h3>
        <p className="text-gray-400 text-lg">
          User uploads documents to categorized buckets
        </p>
      </div>

      {/* Upload Features Notice */}
      <div className="bg-emerald-900/20 border border-emerald-400 p-4 rounded-lg">
        <h4 className="text-white font-medium mb-2">
          🤖 Smart Upload Features
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-300">
          <div>✓ Auto-categorize</div>
          <div>✓ Quality enhancement</div>
          <div>✓ Duplicate detection</div>
          <div>✓ Translation checking</div>
        </div>
      </div>

      {/* Document Categories */}
      <div className="space-y-6">
        {documentCategories.map((category) => (
          <div key={category.id} className="bg-gray-800 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <span className="text-2xl mr-3">{category.icon}</span>
                <h4 className="text-xl font-semibold text-white">
                  {category.title}
                </h4>
              </div>
              <span className="bg-emerald-900/20 text-emerald-400 px-3 py-1 rounded text-sm">
                {getCategoryStats()[category.id]} files
              </span>
            </div>

            {/* Document Examples */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              {category.examples.map((example, index) => (
                <div
                  key={index}
                  className="text-xs bg-gray-700 px-2 py-1 rounded text-gray-300"
                >
                  {example}
                </div>
              ))}
            </div>

            {/* Upload Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive
                  ? "border-emerald-400 bg-emerald-900/20"
                  : "border-gray-600 hover:border-emerald-400"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => handleDrop(e, category.id)}
            >
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => handleFileInput(e, category.id)}
                className="hidden"
                id={`upload-${category.id}`}
              />
              <label
                htmlFor={`upload-${category.id}`}
                className="cursor-pointer"
              >
                <div className="text-gray-400 mb-2">
                  <span className="text-3xl block mb-2">📁</span>
                  <p>Drag & drop files here or click to browse</p>
                  <p className="text-sm">
                    Supports PDF, JPG, PNG, DOC, DOCX (Max 10MB)
                  </p>
                </div>
              </label>
            </div>

            {/* Uploaded Documents */}
            {uploadedDocs.filter((doc) => doc.category === category.id).length >
              0 && (
              <div className="mt-4 space-y-2">
                <h5 className="text-white font-medium">Uploaded Documents:</h5>
                {uploadedDocs
                  .filter((doc) => doc.category === category.id)
                  .map((doc) => (
                    <div key={doc.id} className="bg-gray-700 p-3 rounded">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1">
                          <span className="text-emerald-400 mr-2">📄</span>
                          <div className="flex-1">
                            <div className="flex items-center">
                              <span className="text-white text-sm mr-2">
                                {doc.name}
                              </span>
                              {doc.autoCategorized && (
                                <span className="bg-blue-900/20 text-blue-400 px-2 py-1 rounded text-xs">
                                  Auto-categorized
                                </span>
                              )}
                              {doc.needsTranslation && (
                                <span className="bg-yellow-900/20 text-yellow-400 px-2 py-1 rounded text-xs ml-1">
                                  Translation needed
                                </span>
                              )}
                              {processingDocs.includes(doc.id) && (
                                <span className="bg-emerald-900/20 text-emerald-400 px-2 py-1 rounded text-xs ml-1">
                                  Processing...
                                </span>
                              )}
                            </div>
                            <div className="text-gray-400 text-xs">
                              {(doc.size / 1024 / 1024).toFixed(1)} MB •{" "}
                              {new Date(doc.uploadDate).toLocaleDateString()}
                            </div>

                            {/* Quality Issues */}
                            {doc.qualityIssues &&
                              doc.qualityIssues.length > 0 && (
                                <div className="text-red-400 text-xs mt-1">
                                  ⚠️ Issues: {doc.qualityIssues.join(", ")}
                                </div>
                              )}

                            {/* Suggestions */}
                            {doc.suggestions && doc.suggestions.length > 0 && (
                              <div className="text-emerald-400 text-xs mt-1">
                                💡 Tips: {doc.suggestions.join(", ")}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => removeDocument(doc.id)}
                          className="text-red-400 hover:text-red-300 text-sm ml-2"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Upload Summary */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">
          Upload Summary
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
          {documentCategories.map((category) => (
            <div key={category.id} className="text-center">
              <div className="text-2xl mb-1">{category.icon}</div>
              <div className="text-lg font-bold text-emerald-400">
                {getCategoryStats()[category.id]}
              </div>
              <div className="text-xs text-gray-400">
                {category.title.split(" ")[0]}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-white">
            Total Documents: {uploadedDocs.length}
          </span>
          <span className="text-emerald-400">
            {uploadedDocs.length > 0
              ? "✅ Ready for Analysis"
              : "⚠️ Upload documents to continue"}
          </span>
        </div>

        {uploadedDocs.filter((doc) => doc.needsTranslation).length > 0 && (
          <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-400 rounded">
            <span className="text-yellow-400">
              ⚠️ {uploadedDocs.filter((doc) => doc.needsTranslation).length}{" "}
              documents may need certified English translations
            </span>
          </div>
        )}
      </div>

      {/* Hybrid Chat Assistant */}
      <HybridChatAssistant formData={formData} stepContext="documents" />

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Profile
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium bg-emerald-400 text-black hover:bg-emerald-300 transition-colors"
        >
          Continue to Analysis →
        </button>
      </div>
    </div>
  );
};

// Step 4: Smart Evidence Analysis (Updated with Real AI)
const Step4EvidenceAnalysis = ({ formData, setFormData, onNext, onBack }) => {
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);

  React.useEffect(() => {
    const performAnalysis = async () => {
      try {
        setAnalysisError(null);

        // Get the backend URL from environment or default
        const backendUrl =
          process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

        // Call real OpenAI backend for case analysis
        const response = await fetch(`${backendUrl}/api/ai/analyze-case`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            case_type: formData.caseType,
            petitioner_details: formData.petitionerDetails,
            beneficiary_details: formData.beneficiaryDetails,
            relationship_details: formData.relationshipDetails,
            documents: (formData.documents || []).map(doc => {
              // Create a copy without the file object for JSON serialization
              const { file, ...docWithoutFile } = doc;
              return docWithoutFile;
            }),
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const analysisData = await response.json();
        setAnalysisResults(analysisData);
        setAnalysisComplete(true);
      } catch (error) {
        console.error("Analysis error:", error);
        setAnalysisError(error.message);

        // Generate dynamic analysis based on actual user input when API fails
        const generateDynamicAnalysis = () => {
          const documents = formData.documents || [];
          const relationshipDetails = formData.relationshipDetails || {};
          const petitionerDetails = formData.petitionerDetails || {};
          const beneficiaryDetails = formData.beneficiaryDetails || {};

          // Debug logging to track what data we have
          console.log('🔍 Family Agent Dynamic Analysis Debug:', {
            documents: documents.length,
            relationshipDetails: Object.keys(relationshipDetails).length,
            petitionerDetails: Object.keys(petitionerDetails).length,
            beneficiaryDetails: Object.keys(beneficiaryDetails).length,
            sampleRelationship: relationshipDetails,
            samplePetitioner: petitionerDetails
          });

          // Enhanced document analysis with more granular scoring
          const documentTypes = [
            "i-130",
            "marriage",
            "identity", 
            "financial",
            "photo",
            "communication",
          ];
          const uploadedTypes = documents.map((doc) => doc.name.toLowerCase());
          const documentCompleteness = documentTypes.reduce((count, type) => {
            const hasType = uploadedTypes.some((name) => name.includes(type));
            console.log(`📄 Document type "${type}": ${hasType ? 'Found' : 'Missing'}`);
            return count + (hasType ? 1 : 0);
          }, 0);

          console.log(`📊 Document completeness: ${documentCompleteness}/${documentTypes.length}`);

          // Enhanced scoring with wider range (25-95) and more factors
          let baseScore = 25; // Start with minimum viable score
          console.log(`🎯 Starting base score: ${baseScore}`);
          
          // Document completeness (40 points maximum)
          const documentPoints = (documentCompleteness / documentTypes.length) * 40;
          baseScore += documentPoints;
          console.log(`📄 Document points: +${Math.round(documentPoints)} = ${Math.round(baseScore)}`);
          
          // Relationship strength factors (25 points maximum)
          let relationshipScore = 0;
          if (relationshipDetails.howMet && relationshipDetails.howMet.length > 50) {
            relationshipScore += 5; // Detailed story
            console.log(`💕 Detailed story: +5 points`);
          }
          if (relationshipDetails.marriageDate) {
            const marriageDate = new Date(relationshipDetails.marriageDate);
            const now = new Date();
            const monthsMarried = (now - marriageDate) / (1000 * 60 * 60 * 24 * 30);
            if (monthsMarried > 24) {
              relationshipScore += 8; // Long marriage
              console.log(`💒 Long marriage (${Math.round(monthsMarried)} months): +8 points`);
            } else if (monthsMarried > 12) {
              relationshipScore += 5; // Medium marriage
              console.log(`💒 Medium marriage (${Math.round(monthsMarried)} months): +5 points`);
            } else {
              relationshipScore += 2; // New marriage
              console.log(`💒 New marriage (${Math.round(monthsMarried)} months): +2 points`);
            }
          }
          if (relationshipDetails.children && relationshipDetails.children.toLowerCase().includes('yes')) {
            relationshipScore += 7; // Children together
            console.log(`👶 Children together: +7 points`);
          }
          if (relationshipDetails.cohabitation && relationshipDetails.cohabitation.length > 30) {
            relationshipScore += 5; // Detailed cohabitation history
            console.log(`🏠 Detailed cohabitation: +5 points`);
          }
          const relationshipPoints = Math.min(25, relationshipScore);
          baseScore += relationshipPoints;
          console.log(`💕 Relationship points: +${relationshipPoints} = ${Math.round(baseScore)}`);
          
          // Petitioner strength factors (15 points maximum)
          let petitionerScore = 0;
          if (petitionerDetails.status === 'us-citizen') {
            petitionerScore += 10; // Strongest category
            console.log(`🇺🇸 US Citizen petitioner: +10 points`);
          } else if (petitionerDetails.status === 'permanent-resident') {
            petitionerScore += 7; // Good category
            console.log(`🟢 LPR petitioner: +7 points`);
          }
          if (petitionerDetails.income) {
            const income = parseInt(petitionerDetails.income.replace(/[^0-9]/g, ''));
            if (income > 75000) {
              petitionerScore += 5; // High income
              console.log(`💰 High income ($${income}): +5 points`);
            } else if (income > 40000) {
              petitionerScore += 3; // Adequate income
              console.log(`💰 Adequate income ($${income}): +3 points`);
            } else if (income > 20000) {
              petitionerScore += 1; // Low but qualifying income
              console.log(`💰 Qualifying income ($${income}): +1 point`);
            }
          }
          const petitionerPoints = Math.min(15, petitionerScore);
          baseScore += petitionerPoints;
          console.log(`👤 Petitioner points: +${petitionerPoints} = ${Math.round(baseScore)}`);
          
          // Beneficiary factors (10 points maximum)
          let beneficiaryScore = 0;
          if (beneficiaryDetails.status && beneficiaryDetails.status !== 'undocumented') {
            beneficiaryScore += 5; // Legal status
            console.log(`✅ Beneficiary legal status: +5 points`);
          }
          if (beneficiaryDetails.location === 'United States') {
            beneficiaryScore += 3; // Already in US
            console.log(`🏠 Beneficiary in US: +3 points`);
          }
          if (beneficiaryDetails.country && !['syria', 'afghanistan', 'iran', 'north korea'].includes(beneficiaryDetails.country.toLowerCase())) {
            beneficiaryScore += 2; // Non-restricted country
            console.log(`🌍 Non-restricted country: +2 points`);
          }
          const beneficiaryPoints = Math.min(10, beneficiaryScore);
          baseScore += beneficiaryPoints;
          console.log(`👥 Beneficiary points: +${beneficiaryPoints} = ${Math.round(baseScore)}`);
          
          // Country-specific risk factors
          const countryRiskAdjustment = () => {
            if (!beneficiaryDetails.country) return 0;
            const country = beneficiaryDetails.country.toLowerCase();
            
            // High-risk countries (security concerns)
            if (['syria', 'afghanistan', 'iran', 'north korea', 'yemen'].includes(country)) {
              console.log(`⚠️ High-risk country (${country}): -8 points`);
              return -8;
            }
            // Medium-risk countries (fraud concerns)
            if (['bangladesh', 'pakistan', 'nigeria', 'ghana'].includes(country)) {
              console.log(`⚠️ Medium-risk country (${country}): -3 points`);
              return -3;
            }
            // Low-risk countries (minimal concerns)
            if (['canada', 'uk', 'australia', 'germany', 'france', 'japan', 'south korea'].includes(country)) {
              console.log(`✅ Low-risk country (${country}): +2 points`);
              return +2;
            }
            return 0;
          };
          
          const countryAdjustment = countryRiskAdjustment();
          baseScore += countryAdjustment;
          console.log(`🌍 Country adjustment: ${countryAdjustment >= 0 ? '+' : ''}${countryAdjustment} = ${Math.round(baseScore)}`);
          
          // Quality bonus for exceptional cases (5 points maximum)
          let qualityBonus = 0;
          if (documents.length > 15) {
            qualityBonus += 2; // Extensive documentation
            console.log(`📚 Extensive documentation (${documents.length} docs): +2 points`);
          }
          if (uploadedTypes.some(name => name.includes('affidavit'))) {
            qualityBonus += 2; // Third-party support
            console.log(`📝 Affidavit evidence: +2 points`);
          }
          if (relationshipDetails.milestones && relationshipDetails.milestones.length > 100) {
            qualityBonus += 1; // Detailed timeline
            console.log(`⏰ Detailed timeline: +1 point`);
          }
          const qualityPoints = Math.min(5, qualityBonus);
          baseScore += qualityPoints;
          console.log(`⭐ Quality bonus: +${qualityPoints} = ${Math.round(baseScore)}`);
          
          // Final score capping (25-95 range)
          const finalScore = Math.max(25, Math.min(95, Math.round(baseScore)));
          console.log(`🎯 FINAL SCORE: ${finalScore}% (before capping: ${Math.round(baseScore)})`);

          // Enhanced dynamic issues generation
          const issues = [];
          const criticalIssues = [];
          
          // Document-specific issues
          if (!uploadedTypes.some((name) => name.includes("financial"))) {
            criticalIssues.push("Missing joint financial documents (tax returns, bank statements)");
          }
          if (!uploadedTypes.some((name) => name.includes("photo"))) {
            issues.push("Need relationship photos spanning the relationship timeline");
          }
          if (!uploadedTypes.some((name) => name.includes("i-130"))) {
            criticalIssues.push("Form I-130 petition not uploaded");
          }
          if (!uploadedTypes.some((name) => name.includes("marriage"))) {
            criticalIssues.push("Marriage certificate required for spouse petitions");
          }
          
          // Relationship-specific issues
          if (!relationshipDetails.howMet || relationshipDetails.howMet.length < 30) {
            issues.push("Provide detailed story of how you met (minimum 30 characters)");
          }
          if (!relationshipDetails.cohabitation || relationshipDetails.cohabitation.length < 20) {
            issues.push("Need detailed cohabitation history");
          }
          if (!relationshipDetails.milestones) {
            issues.push("Provide relationship timeline with key milestones");
          }
          
          // Country-specific warnings
          if (beneficiaryDetails.country) {
            const country = beneficiaryDetails.country.toLowerCase();
            if (['syria', 'afghanistan', 'iran'].includes(country)) {
              criticalIssues.push(`Additional security screening required for ${beneficiaryDetails.country} - expect longer processing times`);
            }
          }
          
          // Status-specific warnings
          if (beneficiaryDetails.status === 'undocumented') {
            criticalIssues.push("Beneficiary's undocumented status may require waiver - consult immigration attorney");
          }

          // Enhanced dynamic strengths generation
          const strengths = [];
          const majorStrengths = [];
          
          if (uploadedTypes.some((name) => name.includes("communication"))) {
            strengths.push("Communication evidence demonstrates ongoing relationship");
          }
          if (relationshipDetails.marriageDate) {
            const marriageDate = new Date(relationshipDetails.marriageDate);
            const monthsMarried = (new Date() - marriageDate) / (1000 * 60 * 60 * 24 * 30);
            if (monthsMarried > 24) {
              majorStrengths.push(`Long-term marriage (${Math.floor(monthsMarried)} months) demonstrates relationship authenticity`);
            } else {
              strengths.push("Marriage certificate establishes legal relationship");
            }
          }
          if (documents.length > 10) {
            majorStrengths.push("Comprehensive document package demonstrates thorough preparation");
          }
          if (petitionerDetails.status === 'us-citizen') {
            majorStrengths.push("US citizen petitioner provides strongest sponsorship category");
          }
          if (relationshipDetails.children && relationshipDetails.children.toLowerCase().includes('yes')) {
            majorStrengths.push("Children together strongly support bona fide marriage claim");
          }
          if (petitionerDetails.income && parseInt(petitionerDetails.income.replace(/[^0-9]/g, '')) > 50000) {
            strengths.push(`Strong financial sponsorship ($${petitionerDetails.income} annual income)`);
          }

          return {
            overall_score: finalScore,
            photo_analysis: {
              score: Math.round(finalScore * 0.9),
              photos_analyzed: documents.filter((doc) =>
                doc.name.toLowerCase().includes("photo")
              ).length,
              timeline_span: relationshipDetails.relationshipLength || 
                (relationshipDetails.marriageDate ? 
                  `Since ${relationshipDetails.marriageDate}` : "Not specified"),
              authenticity: Math.round(finalScore * 1.1),
              issues: issues.filter((issue) => issue.includes("photo")),
            },
            document_analysis: {
              score: Math.round(finalScore * 0.85),
              completeness: Math.round((documentCompleteness / documentTypes.length) * 100),
              authenticity: Math.round(finalScore * 1.05),
              consistency: Math.round(finalScore * 0.95),
              issues: [...criticalIssues, ...issues.filter((issue) => !issue.includes("photo"))],
              available_docs: documents.map(doc => doc.name),
              missing_docs: documentTypes.filter(type => 
                !uploadedTypes.some(name => name.includes(type))
              ).map(type => type.charAt(0).toUpperCase() + type.slice(1) + " documents")
            },
            red_flags: finalScore < 50 
              ? [...criticalIssues, "Case requires immediate strengthening before submission"]
              : criticalIssues,
            strengths: [...majorStrengths, ...strengths].length > 0 
              ? [...majorStrengths, ...strengths] 
              : ["Case information collected successfully"],
            case_strength: finalScore >= 80 ? "Strong" : finalScore >= 65 ? "Moderate" : "Needs Improvement",
            approval_likelihood: finalScore >= 85 ? "High (85-95%)" : 
                                finalScore >= 70 ? "Good (70-84%)" : 
                                finalScore >= 55 ? "Fair (55-69%)" : "Low (below 55%)"
          };
        };

        const mockAnalysis = generateDynamicAnalysis();

        setAnalysisResults(mockAnalysis);
        setAnalysisComplete(true);
      }
    };

    // Start analysis after a short delay
    const timer = setTimeout(performAnalysis, 2000);
    return () => clearTimeout(timer);
  }, [formData]);

  const handleContinue = () => {
    setFormData({
      ...formData,
      analysisResults,
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">
          Smart Evidence Analysis
        </h3>
        <p className="text-gray-400 text-lg">
          AI evaluates quality, completeness, and red flags
        </p>
        {/* Force Re-analysis Button */}
        <button
          onClick={() => {
            console.log('🔄 Force re-analyzing case...');
            setAnalysisResults({});
            setAnalysisComplete(false);
            setAnalysisError(null);
            // Force immediate re-analysis
            setTimeout(() => {
              window.location.reload(); // Temporary fix to force complete re-analysis
            }, 100);
          }}
          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          🔄 Re-analyze Case with Current Data
        </button>
      </div>

      {!analysisComplete ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-emerald-400 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">
            Analyzing Your Evidence with AI
          </h4>
          <p className="text-gray-400">
            Processing {formData.documents?.length || 0} documents...
          </p>
          <div className="mt-4 space-y-2">
            <p className="text-sm text-gray-500">
              📸 AI analyzing photo metadata and authenticity...
            </p>
            <p className="text-sm text-gray-500">
              📄 AI reviewing document completeness...
            </p>
            <p className="text-sm text-gray-500">
              🔍 AI checking for red flags and inconsistencies...
            </p>
          </div>
          {analysisError && (
            <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-400 rounded">
              <p className="text-yellow-400 text-sm">
                Note: Using backup analysis due to API connectivity
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Overall Score */}
          <div className="bg-gray-800 p-6 rounded-lg text-center">
            <h4 className="text-2xl font-bold text-white mb-2">
              AI-Powered Case Strength Analysis
            </h4>
            <div className="text-5xl font-bold text-emerald-400 mb-2">
              {analysisResults?.overall_score}%
            </div>
            <div className="text-gray-400">
              {analysisResults?.overall_score >= 80
                ? "Strong Case"
                : analysisResults?.overall_score >= 60
                ? "Moderate Case"
                : "Needs Improvement"}
            </div>
            {!analysisError && (
              <p className="text-sm text-emerald-400 mt-2">
                ✨ Powered by GPT-4 Analysis
              </p>
            )}
          </div>

          {/* Analysis Details */}
          <div className="space-y-6">
            {/* Debug Information */}
            <div className="bg-blue-900/20 border border-blue-400 p-4 rounded-lg">
              <h4 className="text-blue-300 font-semibold mb-2">🔍 Debug Information</h4>
              <div className="text-sm text-blue-200 space-y-1">
                <p>Analysis Timestamp: {new Date().toLocaleTimeString()}</p>
                <p>Calculated Score: {analysisResults.overall_score}%</p>
                <p>Documents Analyzed: {(formData.documents || []).length}</p>
                <p>Relationship Details: {Object.keys(formData.relationshipDetails || {}).length} fields</p>
                <p>Petitioner Details: {Object.keys(formData.petitionerDetails || {}).length} fields</p>
                <p>Beneficiary Details: {Object.keys(formData.beneficiaryDetails || {}).length} fields</p>
              </div>
              <button
                onClick={() => {
                  console.log('📊 Current Analysis Results:', analysisResults);
                  console.log('📋 Current Form Data:', formData);
                  alert(`Current Score: ${analysisResults.overall_score}%\n\nCheck browser console for detailed breakdown.`);
                }}
                className="mt-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs"
              >
                Show Full Debug Data
              </button>
            </div>

            {/* Main Analysis Results */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Photo Analysis */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-xl font-semibold text-white mb-4 flex items-center">
                <span className="text-2xl mr-3">📸</span>
                AI Photo Analysis
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Overall Score:</span>
                  <span className="text-emerald-400 font-bold">
                    {analysisResults?.photo_analysis?.score}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Photos Analyzed:</span>
                  <span className="text-white">
                    {analysisResults?.photo_analysis?.photos_analyzed}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Timeline Span:</span>
                  <span className="text-white">
                    {analysisResults?.photo_analysis?.timeline_span}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Authenticity:</span>
                  <span className="text-emerald-400">
                    {analysisResults?.photo_analysis?.authenticity}%
                  </span>
                </div>
              </div>
            </div>

            {/* Document Analysis */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-xl font-semibold text-white mb-4 flex items-center">
                <span className="text-2xl mr-3">📄</span>
                AI Document Analysis
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Overall Score:</span>
                  <span className="text-emerald-400 font-bold">
                    {analysisResults?.document_analysis?.score}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Completeness:</span>
                  <span className="text-white">
                    {analysisResults?.document_analysis?.completeness}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Authenticity:</span>
                  <span className="text-emerald-400">
                    {analysisResults?.document_analysis?.authenticity}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Consistency:</span>
                  <span className="text-white">
                    {analysisResults?.document_analysis?.consistency}%
                  </span>
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* Red Flags */}
          <div className="bg-red-900/20 border border-red-400 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4 flex items-center">
              <span className="text-2xl mr-3">🚨</span>
              AI-Detected Red Flags
            </h4>
            <ul className="space-y-2">
              {analysisResults?.red_flags?.map((flag, index) => (
                <li key={index} className="flex items-start text-red-300">
                  <span className="text-red-400 mr-2">⚠️</span>
                  {flag}
                </li>
              ))}
            </ul>
          </div>

          {/* Strengths */}
          <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4 flex items-center">
              <span className="text-2xl mr-3">✅</span>
              AI-Identified Strengths
            </h4>
            <ul className="space-y-2">
              {analysisResults?.strengths?.map((strength, index) => (
                <li key={index} className="flex items-start text-emerald-300">
                  <span className="text-emerald-400 mr-2">✓</span>
                  {strength}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Hybrid Chat Assistant */}
      <HybridChatAssistant formData={formData} stepContext="analysis" />

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Documents
        </button>
        <button
          onClick={handleContinue}
          disabled={!analysisComplete}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            analysisComplete
              ? "bg-emerald-400 text-black hover:bg-emerald-300"
              : "bg-gray-700 text-gray-400 cursor-not-allowed"
          }`}
        >
          Continue to Recommendations →
        </button>
      </div>
    </div>
  );
};

// Step 5: Recommendations & Suggestions
const Step5Recommendations = ({ formData, setFormData, onNext, onBack }) => {
  const [showContentLibrary, setShowContentLibrary] = useState(null);
  const [generatedContent, setGeneratedContent] = useState({});
  // Get recommendations from API analysis results or generate dynamic ones
  const getRecommendations = () => {
    // Use API-generated recommendations if available
    if (
      formData.analysisResults?.recommendations &&
      formData.analysisResults.recommendations.length > 0
    ) {
      return formData.analysisResults.recommendations;
    }

    // Generate dynamic recommendations based on analysis results and user data
    const analysisResults = formData.analysisResults || {};
    const documents = formData.documents || [];
    const relationshipDetails = formData.relationshipDetails || {};
    const petitionerDetails = formData.petitionerDetails || {};
    const beneficiaryDetails = formData.beneficiaryDetails || {};
    const dynamicRecommendations = [];

    // Enhanced document-based recommendations
    const documentIssues = analysisResults.document_analysis?.issues || [];
    if (documentIssues.length > 0) {
      dynamicRecommendations.push({
        category: "Critical Document Issues",
        priority: "high",
        items: documentIssues.map((issue) => `⚠️ ${issue}`),
      });
    }

    // Photo evidence recommendations
    const photoIssues = analysisResults.photo_analysis?.issues || [];
    if (photoIssues.length > 0) {
      dynamicRecommendations.push({
        category: "Photo Evidence Enhancement",
        priority: "medium",
        items: photoIssues.map((issue) => `📸 ${issue}`),
      });
    }

    // Enhanced missing document analysis
    const missingItems = [];
    const criticalMissing = [];
    
    // Government documents
    if (!documents.some((doc) => doc.name.toLowerCase().includes("i-130"))) {
      criticalMissing.push("📋 File Form I-130 petition (most critical document)");
    }
    if (!documents.some((doc) => doc.name.toLowerCase().includes("marriage"))) {
      criticalMissing.push("💍 Marriage certificate (certified copy required)");
    }
    if (!documents.some((doc) => doc.name.toLowerCase().includes("birth"))) {
      criticalMissing.push("📜 Birth certificates for both petitioner and beneficiary");
    }
    
    // Financial evidence
    if (!documents.some((doc) => doc.name.toLowerCase().includes("financial"))) {
      criticalMissing.push("💰 Joint financial documents (tax returns, bank statements, I-864)");
    }
    if (!documents.some((doc) => doc.name.toLowerCase().includes("tax"))) {
      missingItems.push("📊 Joint tax returns (3 years recommended)");
    }
    
    // Relationship evidence
    if (!documents.some((doc) => doc.name.toLowerCase().includes("communication"))) {
      missingItems.push("💬 Communication evidence (emails, texts, call logs)");
    }
    if (!documents.some((doc) => doc.name.toLowerCase().includes("photo"))) {
      missingItems.push("📷 Relationship photos spanning entire relationship timeline");
    }
    
    // Shared life evidence
    if (!documents.some((doc) => doc.name.toLowerCase().includes("lease") || doc.name.toLowerCase().includes("mortgage"))) {
      missingItems.push("🏠 Joint housing documents (lease, mortgage, utility bills)");
    }
    if (!documents.some((doc) => doc.name.toLowerCase().includes("insurance"))) {
      missingItems.push("🛡️ Joint insurance policies (health, auto, life)");
    }

    // Add critical missing items
    if (criticalMissing.length > 0) {
      dynamicRecommendations.push({
        category: "CRITICAL Missing Documents",
        priority: "high",
        items: criticalMissing,
      });
    }

    // Add regular missing items
    if (missingItems.length > 0) {
      dynamicRecommendations.push({
        category: "Additional Evidence Needed",
        priority: "medium",
        items: missingItems,
      });
    }

    // Relationship-specific recommendations
    const relationshipIssues = [];
    if (!relationshipDetails.howMet || relationshipDetails.howMet.length < 50) {
      relationshipIssues.push("📝 Expand relationship origin story (minimum 50+ words with specific details)");
    }
    if (!relationshipDetails.milestones || relationshipDetails.milestones.length < 100) {
      relationshipIssues.push("⏰ Create detailed relationship timeline with specific dates and events");
    }
    if (!relationshipDetails.cohabitation || relationshipDetails.cohabitation.length < 30) {
      relationshipIssues.push("🏡 Provide comprehensive cohabitation history with addresses and dates");
    }
    if (!relationshipDetails.children || relationshipDetails.children.toLowerCase() === 'none') {
      relationshipIssues.push("👶 If applicable, document any children from relationship (birth certificates)");
    }

    if (relationshipIssues.length > 0) {
      dynamicRecommendations.push({
        category: "Relationship Documentation",
        priority: "medium",
        items: relationshipIssues,
      });
    }

    // Case strength-based recommendations
    const score = analysisResults.overall_score || 0;
    const strengthRecommendations = [];
    
    if (score < 50) {
      strengthRecommendations.push("🚨 URGENT: Case requires immediate strengthening before submission");
      strengthRecommendations.push("👨‍💼 Strongly recommend consulting with immigration attorney");
      strengthRecommendations.push("📋 Complete comprehensive case review and evidence gathering");
    } else if (score < 70) {
      strengthRecommendations.push("⚠️ Case needs strengthening for optimal approval chances");
      strengthRecommendations.push("📄 Add 3-5 additional supporting documents");
      strengthRecommendations.push("👥 Obtain affidavits from friends and family members");
    } else if (score >= 85) {
      strengthRecommendations.push("✅ Strong case - maintain current documentation quality");
      strengthRecommendations.push("🔍 Final review for any minor improvements");
    }

    if (strengthRecommendations.length > 0) {
      dynamicRecommendations.push({
        category: score < 70 ? "Case Strengthening Required" : "Case Optimization",
        priority: score < 50 ? "high" : score < 70 ? "medium" : "low",
        items: strengthRecommendations,
      });
    }

    // Country-specific recommendations
    if (beneficiaryDetails.country) {
      const country = beneficiaryDetails.country.toLowerCase();
      const countryRecommendations = [];
      
      // High-risk countries
      if (['syria', 'afghanistan', 'iran', 'north korea', 'yemen'].includes(country)) {
        countryRecommendations.push(`🔐 Enhanced security screening expected for ${beneficiaryDetails.country} cases`);
        countryRecommendations.push("⏳ Prepare for 12-18 month processing delays");
        countryRecommendations.push("📋 Gather comprehensive background documentation");
      }
      
      // Fraud-risk countries
      if (['bangladesh', 'pakistan', 'nigeria', 'ghana'].includes(country)) {
        countryRecommendations.push(`🔍 Enhanced evidence required for ${beneficiaryDetails.country} cases`);
        countryRecommendations.push("👥 Additional third-party verification recommended");
        countryRecommendations.push("📸 Extra relationship photos and communication evidence");
      }

      if (countryRecommendations.length > 0) {
        dynamicRecommendations.push({
          category: `${beneficiaryDetails.country} Country-Specific Requirements`,
          priority: "high",
          items: countryRecommendations,
        });
      }
    }

    // Status-specific recommendations
    if (beneficiaryDetails.status) {
      const statusRecommendations = [];
      
      if (beneficiaryDetails.status.toLowerCase() === 'undocumented') {
        statusRecommendations.push("⚖️ CRITICAL: Consult immigration attorney for unlawful presence analysis");
        statusRecommendations.push("📋 May require I-601A provisional waiver application");
        statusRecommendations.push("🕐 Calculate unlawful presence periods carefully");
      } else if (beneficiaryDetails.status.toLowerCase() === 'visitor') {
        statusRecommendations.push("⏰ Timing critical - avoid 90-day rule complications");
        statusRecommendations.push("📋 Document benign intent at entry if adjusting status");
      }

      if (statusRecommendations.length > 0) {
        dynamicRecommendations.push({
          category: `${beneficiaryDetails.status} Status Considerations`,
          priority: "high",
          items: statusRecommendations,
        });
      }
    }

    // Return comprehensive recommendations or defaults
    if (dynamicRecommendations.length === 0) {
      return [
        {
          category: "General Case Review",
          priority: "medium",
          items: [
            "📋 Review all submitted documents for completeness and accuracy",
            "✍️ Ensure all forms are properly signed and dated",
            "📄 Consider adding additional supporting evidence",
            "👨‍💼 Consider consultation with immigration attorney for complex cases",
          ],
        },
      ];
    }

    return dynamicRecommendations;
  };

  const recommendations = getRecommendations();

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "border-red-400 bg-red-900/20";
      case "medium":
        return "border-yellow-400 bg-yellow-900/20";
      case "low":
        return "border-blue-400 bg-blue-900/20";
      default:
        return "border-gray-400 bg-gray-900/20";
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case "high":
        return "🚨";
      case "medium":
        return "⚠️";
      case "low":
        return "ℹ️";
      default:
        return "📋";
    }
  };

  const handleContinue = () => {
    setFormData({
      ...formData,
      recommendations,
    });
    onNext();
  };

  // Handle content generation
  const handleGenerateContent = async (contentType) => {
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

      let content;

      // Try to call backend API for content generation
      try {
        const response = await fetch(`${backendUrl}/api/ai/generate-content`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            content_type: contentType,
            petitioner_details: formData.petitionerDetails,
            beneficiary_details: formData.beneficiaryDetails,
            relationship_details: formData.relationshipDetails,
            case_type: formData.caseType,
            analysis_results: formData.analysisResults,
            recommendations: recommendations,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          content = data.content;
        } else {
          throw new Error("API call failed");
        }
      } catch (apiError) {
        console.log("Using fallback content generation");
        content = generateRecommendationsContent(contentType);
      }

      setGeneratedContent({
        ...generatedContent,
        [contentType]: content,
      });

      // Auto-download the content as Word document
      downloadWordDocument(content, contentType);
    } catch (error) {
      console.error("Content generation error:", error);
      alert("Error generating content. Please try again.");
    }
  };

  // Generate recommendations-based content
  const generateRecommendationsContent = (contentType) => {
    const caseStrength = formData.analysisResults?.overallScore || 0;
    const petitionerName =
      formData.petitionerDetails?.fullName || "[Petitioner Name]";
    const beneficiaryName =
      formData.beneficiaryDetails?.fullName || "[Beneficiary Name]";
    const marriageDate =
      formData.relationshipDetails?.marriageDate || "[Marriage Date]";
    const howMet = formData.relationshipDetails?.howMet || "[How you met]";
    const relationshipStart =
      formData.relationshipDetails?.relationshipStart || "[Relationship Start]";

    const contentTemplates = {
      affidavit: `AFFIDAVIT OF SUPPORT FOR IMMIGRATION PETITION
      
I, [Affiant Full Name], hereby declare under penalty of perjury that I have personal knowledge of the bona fide marital relationship between ${petitionerName} and ${beneficiaryName}.

RELATIONSHIP KNOWLEDGE:
I have known ${petitionerName} for [Duration] years and have observed their relationship with ${beneficiaryName} since ${relationshipStart}. Through my personal observations, I can attest to the genuine nature of their marriage and their commitment to building a life together.

SPECIFIC OBSERVATIONS:
Based on the current case analysis (strength: ${caseStrength}%), I have observed the following:

1. RELATIONSHIP DEVELOPMENT:
   - The couple met ${howMet}
   - Their relationship has progressed naturally over time
   - They married on ${marriageDate}
   - Their interactions consistently demonstrate genuine affection and care

2. SHARED LIFE EVIDENCE:
   - They have combined their finances and share financial responsibilities
   - They maintain a shared residence and household
   - They participate in family and social activities together
   - They have integrated their social circles and family relationships

3. ONGOING COMMITMENT:
   - They demonstrate mutual support during challenges
   - They make joint decisions about their future
   - They maintain regular communication when apart
   - They share common interests and goals

CREDIBILITY STATEMENT:
I make this statement based on my personal knowledge and observations. I have no financial interest in the outcome of this petition. I am providing this affidavit voluntarily to support their application.

I declare under penalty of perjury under the laws of the United States that the foregoing is true and correct.

_______________________                    Date: _______________
[Affiant Signature]

[Printed Name]
[Address]
[City, State, ZIP]
[Phone Number]
[Email Address]
[Relationship to Petitioner/Beneficiary]`,

      "cover-letter": `COVER LETTER FOR IMMIGRATION PETITION
      
TO: U.S. Citizenship and Immigration Services
FROM: ${petitionerName}
RE: Form I-130 Petition for Alien Relative - ${beneficiaryName}
DATE: ${new Date().toLocaleDateString()}

Dear USCIS Officer,

I am writing to submit my Form I-130 petition for my spouse, ${beneficiaryName}, to immigrate to the United States. This petition is supported by comprehensive evidence demonstrating our bona fide marriage and eligibility for family-based immigration benefits.

EXECUTIVE SUMMARY:
Our case has achieved a ${caseStrength}% strength rating based on AI-powered analysis of evidence quality, completeness, and USCIS requirements. We have systematically addressed all requirements and potential concerns to present a compelling case for approval.

CASE BACKGROUND:
• Petitioner: ${petitionerName} (U.S. ${
        formData.petitionerDetails?.status || "Citizen"
      })
• Beneficiary: ${beneficiaryName} (${
        formData.beneficiaryDetails?.country || "Foreign National"
      })
• Marriage Date: ${marriageDate}
• Relationship Duration: Since ${relationshipStart}
• How We Met: ${howMet}

EVIDENCE ANALYSIS:
Based on our comprehensive case review, we have addressed the following key areas:

1. RELATIONSHIP AUTHENTICITY:
   ${
     recommendations
       .find((r) => r.category.includes("Photo"))
       ?.items?.map((item) => `   • ${item}`)
       .join("\n") || "   • Comprehensive photo documentation provided"
   }

2. FINANCIAL INTEGRATION:
   ${
     recommendations
       .find((r) => r.category.includes("Missing Documents"))
       ?.items?.map((item) => `   • ${item}`)
       .join("\n") || "   • Joint financial documentation provided"
   }

3. THIRD-PARTY VERIFICATION:
   ${
     recommendations
       .find((r) => r.category.includes("Third-Party"))
       ?.items?.map((item) => `   • ${item}`)
       .join("\n") || "   • Affidavits and supporting letters included"
   }

CASE STRENGTHS:
• Strong documentary evidence of bona fide marriage
• Comprehensive financial integration documentation
• Robust third-party corroboration
• Clear relationship timeline and progression
• Proactive address of potential concerns

RECOMMENDATIONS IMPLEMENTED:
We have carefully implemented all critical recommendations to strengthen our case:
${recommendations
  .map(
    (rec) =>
      `\n${rec.category.toUpperCase()}:\n${rec.items
        .map((item) => `• ${item}`)
        .join("\n")}`
  )
  .join("\n")}

CONCLUSION:
We respectfully request favorable consideration of this petition. The evidence demonstrates a genuine marital relationship that meets all USCIS requirements. We are committed to building our life together in the United States and contributing positively to our community.

Thank you for your time and consideration. Please contact me if you need any additional information.

Respectfully submitted,

_______________________
${petitionerName}
Petitioner

Enclosures: Supporting documentation as per attached exhibit list`,

      "relationship-statement": `DETAILED RELATIONSHIP STATEMENT
      
RELATIONSHIP HISTORY: ${petitionerName} and ${beneficiaryName}

INTRODUCTION:
This statement provides a detailed account of our relationship history and the genuine nature of our marriage. Our case has been thoroughly analyzed and achieved a ${caseStrength}% strength rating, demonstrating the authenticity of our relationship.

HOW WE MET:
${howMet}

Our relationship began on ${relationshipStart}, and we have been together for [duration]. From our first meeting, we felt a strong connection that has only grown stronger over time.

RELATIONSHIP TIMELINE:
• First Meeting: ${relationshipStart}
• First Date: ${formData.relationshipDetails?.firstDate || "[First Date]"}
• Relationship Official: ${
        formData.relationshipDetails?.relationshipOfficial ||
        "[When relationship became official]"
      }
• Engagement: ${
        formData.relationshipDetails?.engagementDate || "[Engagement Date]"
      }
• Marriage: ${marriageDate}
• Cohabitation: ${
        formData.relationshipDetails?.cohabitationStart ||
        "[When started living together]"
      }

DAILY LIFE TOGETHER:
We share all aspects of our daily lives, including:
• Financial responsibilities and joint accounts
• Household duties and decisions
• Social activities and family events
• Future planning and goal setting
• Emotional support during challenges

FAMILY INTEGRATION:
Both families have embraced our relationship:
• Regular communication with both sets of parents
• Participation in family gatherings and traditions
• Holiday celebrations together
• Extended family acceptance and support

SHARED INTERESTS AND ACTIVITIES:
• [List common interests and activities]
• [Hobbies you enjoy together]
• [Travel experiences]
• [Social activities and friends]

FUTURE PLANS:
We have made numerous plans for our future together:
• Career planning and support
• Home ownership goals
• Family planning discussions
• Long-term financial planning
• Community involvement plans

EVIDENCE OF GENUINE MARRIAGE:
Our relationship is supported by extensive evidence:
• Joint financial accounts and credit cards
• Shared lease/mortgage and utility bills
• Joint insurance policies
• Photographs spanning our relationship
• Communication records and correspondence
• Affidavits from family and friends
• Joint travel documentation

ADDRESSING POTENTIAL CONCERNS:
We have proactively addressed all potential concerns:
${recommendations
  .map(
    (rec) =>
      `\n${rec.category}:\n${rec.items.map((item) => `• ${item}`).join("\n")}`
  )
  .join("\n")}

CONCLUSION:
Our marriage is genuine and was entered into for love, not for immigration benefits. We look forward to building our life together in the United States and contributing positively to our community.

_______________________                    Date: _______________
${petitionerName}

_______________________                    Date: _______________
${beneficiaryName}`,

      "evidence-summary": `EVIDENCE SUMMARY AND ANALYSIS
      
COMPREHENSIVE EVIDENCE REVIEW FOR: ${petitionerName} and ${beneficiaryName}
CASE STRENGTH RATING: ${caseStrength}%
ANALYSIS DATE: ${new Date().toLocaleDateString()}

OVERVIEW:
This document provides a comprehensive analysis of all evidence submitted in support of the I-130 petition for ${beneficiaryName}. Each category of evidence has been evaluated for quality, completeness, and alignment with USCIS requirements.

EVIDENCE CATEGORIES AND ANALYSIS:

1. IDENTITY AND RELATIONSHIP DOCUMENTS:
   • Marriage Certificate: [Status and quality assessment]
   • Birth Certificates: [Status and quality assessment]
   • Divorce Decrees (if applicable): [Status and quality assessment]
   • Passport Copies: [Status and quality assessment]

2. FINANCIAL INTEGRATION EVIDENCE:
   • Joint Bank Accounts: [Number and duration]
   • Joint Credit Cards: [Number and usage patterns]
   • Joint Tax Returns: [Years covered and filing status]
   • Joint Insurance Policies: [Types and coverage]
   • Joint Utility Bills: [Types and duration]

3. COHABITATION EVIDENCE:
   • Lease/Mortgage Documents: [Joint or individual]
   • Utility Bills: [Joint responsibility evidence]
   • Mail Correspondence: [Joint address verification]
   • Neighbor Affidavits: [Third-party verification]

4. PHOTOGRAPHIC EVIDENCE:
   • Relationship Timeline Photos: [Quantity and time span]
   • Family Gathering Photos: [Integration evidence]
   • Travel Photos: [Shared experiences]
   • Wedding Photos: [Ceremony and reception]

5. THIRD-PARTY AFFIDAVITS:
   • Family Members: [Number and relationship]
   • Friends: [Number and duration of knowledge]
   • Employers: [Professional recognition]
   • Community Members: [Social integration]

RECOMMENDATIONS ANALYSIS:
Based on our comprehensive review, we have identified and addressed the following areas:

HIGH PRIORITY RECOMMENDATIONS:
${recommendations
  .filter((r) => r.priority === "high")
  .map(
    (rec) =>
      `\n${rec.category}:\n${rec.items.map((item) => `• ${item}`).join("\n")}`
  )
  .join("\n")}

MEDIUM PRIORITY RECOMMENDATIONS:
${recommendations
  .filter((r) => r.priority === "medium")
  .map(
    (rec) =>
      `\n${rec.category}:\n${rec.items.map((item) => `• ${item}`).join("\n")}`
  )
  .join("\n")}

LOW PRIORITY RECOMMENDATIONS:
${recommendations
  .filter((r) => r.priority === "low")
  .map(
    (rec) =>
      `\n${rec.category}:\n${rec.items.map((item) => `• ${item}`).join("\n")}`
  )
  .join("\n")}

CASE STRENGTH FACTORS:
• Document Quality: High
• Evidence Completeness: ${
        caseStrength > 85
          ? "Excellent"
          : caseStrength > 70
          ? "Good"
          : "Needs Improvement"
      }
• Timeline Consistency: Verified
• Third-Party Corroboration: Strong
• Red Flag Mitigation: Addressed

CONCLUSION:
This evidence package demonstrates a well-documented, genuine marital relationship that meets all USCIS requirements for family-based immigration benefits. The comprehensive nature of the evidence and proactive approach to addressing potential concerns significantly strengthens the likelihood of approval.

Prepared by: AI Case Analysis System
Date: ${new Date().toLocaleDateString()}
Case ID: ${formData.caseType || "I-130"}-${Date.now()}`,
    };

    return contentTemplates[contentType] || "Content generated successfully!";
  };

  // Download content as Word document
  const downloadWordDocument = (content, contentType) => {
    const element = document.createElement("a");
    const file = new Blob([content], { type: "application/msword" });
    element.href = URL.createObjectURL(file);
    element.download = `${contentType.replace("-", "_")}_${Date.now()}.doc`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    const docNames = {
      affidavit: "Affidavit of Support",
      "cover-letter": "Professional Cover Letter",
      "relationship-statement": "Detailed Relationship Statement",
      "evidence-summary": "Evidence Summary and Analysis",
    };

    alert(
      `${docNames[contentType]} has been generated and downloaded successfully!`
    );
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">
          Recommendations & Suggestions
        </h3>
        <p className="text-gray-400 text-lg">
          Personalized strategies to strengthen your case
        </p>
      </div>

      {/* Case Strength Summary */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-xl font-semibold text-white">
            Current Case Strength
          </h4>
          <span className="text-2xl font-bold text-emerald-400">
            {formData.analysisResults?.overallScore}%
          </span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-3">
          <div
            className="bg-emerald-400 h-3 rounded-full transition-all duration-300"
            style={{ width: `${formData.analysisResults?.overallScore}%` }}
          />
        </div>
        <p className="text-gray-400 mt-2">
          With improvements, your case could reach 90-95% strength
        </p>
      </div>

      {/* Recommendations */}
      <div className="space-y-4">
        {recommendations.map((rec, index) => (
          <div
            key={index}
            className={`border-2 rounded-lg p-6 ${getPriorityColor(
              rec.priority
            )}`}
          >
            <div className="flex items-center mb-4">
              <span className="text-2xl mr-3">
                {getPriorityIcon(rec.priority)}
              </span>
              <h4 className="text-lg font-semibold text-white">
                {rec.category}
              </h4>
              <span
                className={`ml-auto px-2 py-1 rounded text-xs font-medium ${
                  rec.priority === "high"
                    ? "bg-red-600 text-white"
                    : rec.priority === "medium"
                    ? "bg-yellow-600 text-white"
                    : "bg-blue-600 text-white"
                }`}
              >
                {rec.priority.toUpperCase()} PRIORITY
              </span>
            </div>
            <ul className="space-y-2">
              {rec.items.map((item, itemIndex) => (
                <li key={itemIndex} className="flex items-start text-gray-300">
                  <span className="text-emerald-400 mr-2">▶</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* AI Assistant Offer */}
      <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4 flex items-center">
          <span className="text-2xl mr-3">🤖</span>
          AI-Assisted Content Generation
        </h4>
        <p className="text-gray-400 mb-4">
          Our AI can help you create personalized affidavits, cover letters, and
          document explanations based on your specific case details.
        </p>
        <div className="flex gap-2">
          <button className="bg-emerald-400 text-black px-4 py-2 rounded font-medium hover:bg-emerald-300 transition-colors">
            Generate Affidavit Templates
          </button>
          <button className="bg-gray-700 text-white px-4 py-2 rounded font-medium hover:bg-gray-600 transition-colors">
            Create Cover Letter
          </button>
        </div>
      </div>

      {/* Hybrid Chat Assistant */}
      <HybridChatAssistant formData={formData} stepContext="general" />

      {/* AI-Assisted Content Generation Library */}
      <div className="bg-purple-900/20 border border-purple-400 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4 flex items-center">
          <span className="text-2xl mr-3">🤖</span>
          AI-Assisted Content Generation Library
        </h4>
        <p className="text-gray-300 mb-6">
          Generate professional documents using AI analysis of your case data
          and current recommendations.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-3">✍️</span>
              <div>
                <h5 className="text-white font-medium">Generate Affidavit</h5>
                <p className="text-gray-400 text-sm">
                  Professional affidavit with case-specific details
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowContentLibrary("affidavit")}
              className="w-full bg-emerald-400 text-black px-4 py-2 rounded font-medium hover:bg-emerald-300 transition-colors"
            >
              Generate & Download
            </button>
          </div>

          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-3">📋</span>
              <div>
                <h5 className="text-white font-medium">
                  Generate Cover Letter
                </h5>
                <p className="text-gray-400 text-sm">
                  Comprehensive case summary with recommendations
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowContentLibrary("cover-letter")}
              className="w-full bg-emerald-400 text-black px-4 py-2 rounded font-medium hover:bg-emerald-300 transition-colors"
            >
              Generate & Download
            </button>
          </div>

          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-3">💕</span>
              <div>
                <h5 className="text-white font-medium">
                  Relationship Statement
                </h5>
                <p className="text-gray-400 text-sm">
                  Detailed relationship history and timeline
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowContentLibrary("relationship-statement")}
              className="w-full bg-emerald-400 text-black px-4 py-2 rounded font-medium hover:bg-emerald-300 transition-colors"
            >
              Generate & Download
            </button>
          </div>

          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-3">📊</span>
              <div>
                <h5 className="text-white font-medium">Evidence Summary</h5>
                <p className="text-gray-400 text-sm">
                  Comprehensive analysis of all evidence
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowContentLibrary("evidence-summary")}
              className="w-full bg-emerald-400 text-black px-4 py-2 rounded font-medium hover:bg-emerald-300 transition-colors"
            >
              Generate & Download
            </button>
          </div>
        </div>
      </div>

      {/* Content Generation Library Modal */}
      {showContentLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">
                Generate Professional Document
              </h3>
              <button
                onClick={() => setShowContentLibrary(null)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <div className="bg-gray-800 p-4 rounded-lg mb-6">
              <h4 className="text-lg font-semibold text-white mb-2">
                {showContentLibrary === "affidavit" && "Affidavit of Support"}
                {showContentLibrary === "cover-letter" &&
                  "Professional Cover Letter"}
                {showContentLibrary === "relationship-statement" &&
                  "Detailed Relationship Statement"}
                {showContentLibrary === "evidence-summary" &&
                  "Evidence Summary and Analysis"}
              </h4>
              <p className="text-gray-300 text-sm">
                {showContentLibrary === "affidavit" &&
                  "Generate a personalized affidavit with specific case details and recommendations."}
                {showContentLibrary === "cover-letter" &&
                  "Generate a professional cover letter incorporating your case analysis and recommendations."}
                {showContentLibrary === "relationship-statement" &&
                  "Generate a detailed relationship statement with timeline and evidence references."}
                {showContentLibrary === "evidence-summary" &&
                  "Generate a comprehensive analysis of all evidence with recommendations."}
              </p>
            </div>

            <div className="bg-emerald-900/20 border border-emerald-400 p-4 rounded-lg mb-6">
              <h5 className="text-emerald-400 font-medium mb-2">
                AI-Generated Content Includes:
              </h5>
              <ul className="space-y-1 text-gray-300 text-sm">
                {showContentLibrary === "affidavit" && (
                  <>
                    <li>
                      • Personal knowledge statement with case-specific details
                    </li>
                    <li>• Relationship timeline based on your information</li>
                    <li>
                      • Specific observations incorporating recommendations
                    </li>
                    <li>• Professional legal format with signature lines</li>
                  </>
                )}
                {showContentLibrary === "cover-letter" && (
                  <>
                    <li>• Executive summary with case strength rating</li>
                    <li>• Comprehensive evidence analysis</li>
                    <li>• Implementation of all recommendations</li>
                    <li>• Professional USCIS-ready format</li>
                  </>
                )}
                {showContentLibrary === "relationship-statement" && (
                  <>
                    <li>• Detailed relationship history and timeline</li>
                    <li>• Evidence of genuine marriage</li>
                    <li>• Family integration details</li>
                    <li>• Future plans and shared goals</li>
                  </>
                )}
                {showContentLibrary === "evidence-summary" && (
                  <>
                    <li>• Comprehensive evidence categorization</li>
                    <li>• Quality assessment for each document type</li>
                    <li>• Recommendations implementation status</li>
                    <li>• Case strength analysis</li>
                  </>
                )}
              </ul>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setShowContentLibrary(null)}
                className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleGenerateContent(showContentLibrary);
                  setShowContentLibrary(null);
                }}
                className="flex-1 px-6 py-3 bg-emerald-400 text-black rounded-lg hover:bg-emerald-300 transition-colors font-medium"
              >
                🤖 Generate & Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Analysis
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium bg-emerald-400 text-black hover:bg-emerald-300 transition-colors"
        >
          Continue to Corrections →
        </button>
      </div>
    </div>
  );
};

// Step 7: Final Review & Assembly
const Step7FinalReview = ({ formData, setFormData, onNext, onBack }) => {
  const [reviewChecked, setReviewChecked] = useState({});
  const [finalScore, setFinalScore] = useState(0);
  const [showDocumentUpload, setShowDocumentUpload] = useState(null);
  const [showChatWindow, setShowChatWindow] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [uploadingDocs, setUploadingDocs] = useState([]);
  const [needsReanalysis, setNeedsReanalysis] = useState(false);

  React.useEffect(() => {
    // Calculate final score based on improvements - use dynamic base score
    const baseScore = formData.analysisResults?.overall_score || 0;
    const improvementBonus = (formData.newDocuments?.length || 0) * 3;
    const finalCalculated = Math.min(baseScore + improvementBonus, 95);
    setFinalScore(finalCalculated);
  }, [formData]);

  // Check which documents are actually available - IMPROVED DYNAMIC CHECKING
  const checkDocumentAvailability = () => {
    const allDocs = [
      ...(formData.documents || []),
      ...(formData.newDocuments || []),
    ];
    const docNames = allDocs.map((doc) => doc.name.toLowerCase());

    console.log("Checking documents:", docNames); // Debug log

    // Use more flexible pattern matching and also check document types from analysis
    const documentStatus = {
      "Forms and Applications": {
        "I-130 Petition": docNames.some(
          (name) =>
            name.includes("i-130") ||
            name.includes("petition") ||
            (name.includes("form") &&
              (name.includes("130") || name.includes("petition")))
        ),
        "G-325A (if required)": docNames.some(
          (name) =>
            name.includes("g-325") ||
            name.includes("g325") ||
            name.includes("biographical")
        ),
        "I-864 Affidavit of Support": docNames.some(
          (name) =>
            name.includes("i-864") ||
            name.includes("i864") ||
            name.includes("affidavit") ||
            name.includes("support")
        ),
      },
      "Identity Documents": {
        "Petitioner passport/citizenship proof": docNames.some(
          (name) =>
            name.includes("passport") ||
            name.includes("citizenship") ||
            name.includes("naturalization") ||
            (name.includes("certificate") && name.includes("citizen"))
        ),
        "Beneficiary passport": docNames.some(
          (name) =>
            name.includes("passport") &&
            (name.includes("beneficiary") ||
              name.includes("spouse") ||
              !name.includes("petitioner"))
        ),
        "Birth certificates": docNames.some(
          (name) =>
            name.includes("birth") ||
            (name.includes("certificate") && name.includes("birth"))
        ),
      },
      "Relationship Evidence": {
        "Marriage certificate": docNames.some(
          (name) =>
            name.includes("marriage") ||
            name.includes("wedding") ||
            (name.includes("certificate") && name.includes("marriage"))
        ),
        "Joint financial documents": docNames.some(
          (name) =>
            name.includes("bank") ||
            name.includes("financial") ||
            name.includes("tax") ||
            name.includes("joint") ||
            name.includes("account")
        ),
        "Communication records": docNames.some(
          (name) =>
            name.includes("message") ||
            name.includes("email") ||
            name.includes("communication") ||
            name.includes("text") ||
            name.includes("chat")
        ),
        "Photos together": docNames.some(
          (name) =>
            name.includes("photo") ||
            name.includes("picture") ||
            name.includes("image") ||
            name.includes("jpg") ||
            name.includes("jpeg") ||
            name.includes("png")
        ),
      },
      "Additional Evidence": {
        "Travel records": docNames.some(
          (name) =>
            name.includes("travel") ||
            name.includes("ticket") ||
            name.includes("itinerary") ||
            name.includes("boarding")
        ),
        "Third-party affidavits": docNames.some(
          (name) =>
            name.includes("affidavit") ||
            name.includes("letter") ||
            name.includes("statement") ||
            name.includes("testimony")
        ),
        "Medical records": docNames.some(
          (name) =>
            name.includes("medical") ||
            name.includes("health") ||
            name.includes("doctor") ||
            name.includes("clinic")
        ),
      },
    };

    // If no documents uploaded, everything should be false
    if (allDocs.length === 0) {
      Object.keys(documentStatus).forEach((section) => {
        Object.keys(documentStatus[section]).forEach((doc) => {
          documentStatus[section][doc] = false;
        });
      });
    }

    return documentStatus;
  };

  // Handle document upload
  const handleDocumentUpload = (files, category) => {
    const newDocs = files.map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      category: category,
      uploaded: true,
      uploadDate: new Date().toISOString(),
    }));

    setUploadingDocs([...uploadingDocs, ...newDocs]);
    setFormData({
      ...formData,
      newDocuments: [...(formData.newDocuments || []), ...newDocs],
    });
    setShowDocumentUpload(null);

    // Set flag to trigger re-analysis
    setNeedsReanalysis(true);

    // Show notification
    alert(
      `Successfully uploaded ${files.length} document(s). Your case will be re-analyzed with the new evidence.`
    );
  };

  // Handle quick question buttons
  const handleQuickQuestion = (question) => {
    const newMessage = {
      id: Date.now(),
      text: question,
      sender: "user",
      timestamp: new Date(),
    };

    setChatMessages((prev) => [...prev, newMessage]);
    setChatInput("");

    // Process the AI response
    handleChatMessageWithText(question);
  };

  // Handle chat with AI
  const handleChatMessageWithText = async (messageText) => {
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
      const response = await fetch(`${backendUrl}/api/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: messageText,
          agent_type: "family-based",
          conversation_history: chatMessages.map((msg) => ({
            sender: msg.sender === "user" ? "user" : "assistant",
            text: msg.text,
          })),
        }),
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        const aiResponse = {
          id: Date.now() + 1,
          text: data.response,
          sender: "ai",
          timestamp: new Date(),
        };
        setChatMessages((prev) => [...prev, aiResponse]);
      }
    } catch (error) {
      console.error("Chat error:", error);
    }
  };

  // Handle chat with AI
  const handleChatMessage = async () => {
    if (!chatInput.trim()) return;

    const newMessage = {
      id: Date.now(),
      text: chatInput,
      sender: "user",
      timestamp: new Date(),
    };

    setChatMessages((prev) => [...prev, newMessage]);
    const currentMessage = chatInput;
    setChatInput("");

    // Process the AI response
    handleChatMessageWithText(currentMessage);
  };

  // Generate AI content for missing documents
  const generateAIContent = async (documentType) => {
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
      const response = await fetch(`${backendUrl}/api/ai/generate-content`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          document_type: documentType,
          petitioner_details: formData.petitionerDetails,
          beneficiary_details: formData.beneficiaryDetails,
          relationship_details: formData.relationshipDetails,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        alert(`AI Generated ${documentType}:\n\n${data.content}`);
      }
    } catch (error) {
      console.error("AI generation error:", error);
    }
  };

  // Calculate section completion status
  const getSectionStatus = (sectionTitle) => {
    const documentStatus = checkDocumentAvailability(); // Fix: Get current document status
    const sectionDocs = documentStatus[sectionTitle] || {};
    const totalDocs = Object.keys(sectionDocs).length;
    const completedDocs = Object.values(sectionDocs).filter(Boolean).length;

    if (completedDocs === 0)
      return { status: "missing", color: "text-red-400", icon: "❌" };
    if (completedDocs === totalDocs)
      return { status: "complete", color: "text-emerald-400", icon: "✓" };
    return { status: "partial", color: "text-yellow-400", icon: "⚠️" };
  };

  const documentSections = [
    {
      title: "Forms and Applications",
      items: [
        "I-130 Petition",
        "G-325A (if required)",
        "I-864 Affidavit of Support",
      ],
    },
    {
      title: "Identity Documents",
      items: [
        "Petitioner passport/citizenship proof",
        "Beneficiary passport",
        "Birth certificates",
      ],
    },
    {
      title: "Relationship Evidence",
      items: [
        "Marriage certificate",
        "Joint financial documents",
        "Communication records",
        "Photos spanning relationship",
      ],
    },
    {
      title: "Supporting Documentation",
      items: [
        "Affidavits from family/friends",
        "Tax returns",
        "Insurance policies",
        "Travel documents",
      ],
    },
  ];

  const handleContinue = () => {
    setFormData({
      ...formData,
      finalScore,
      reviewChecked,
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">
          Final Review & Assembly
        </h3>
        <p className="text-gray-400 text-lg">
          Professional document organization and quality control
        </p>
      </div>

      {/* Final Score */}
      <div className="bg-gray-800 p-6 rounded-lg text-center">
        <h4 className="text-2xl font-bold text-white mb-2">
          Final Case Strength Score
        </h4>
        <div className="text-6xl font-bold text-emerald-400 mb-2">
          {finalScore}%
        </div>
        <div className="text-gray-400">
          {finalScore >= 90
            ? "Excellent - High approval probability"
            : finalScore >= 80
            ? "Very Good - Strong case"
            : finalScore >= 70
            ? "Good - Solid foundation"
            : "Needs improvement"}
        </div>
        <div className="mt-4 text-sm text-gray-400">
          +{finalScore - (formData.analysisResults?.overall_score || 0)} points
          improvement from initial analysis
        </div>
      </div>

      {/* Document Assembly Preview */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">
          Document Package Assembly
        </h4>
        <div className="space-y-4">
          {documentSections.map((section, index) => {
            const sectionStatus = getSectionStatus(section.title);
            const documentStatus = checkDocumentAvailability(); // Get current document status
            return (
              <div key={index} className="border border-gray-700 rounded p-4">
                <div className="flex items-center justify-between mb-4">
                  <h5 className="text-white font-medium">{section.title}</h5>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${sectionStatus.color}`}>
                      {sectionStatus.icon}{" "}
                      {sectionStatus.status.charAt(0).toUpperCase() +
                        sectionStatus.status.slice(1)}
                    </span>
                    <button
                      onClick={() => setShowDocumentUpload(section.title)}
                      className="bg-emerald-400 text-black px-3 py-1 rounded text-xs font-medium hover:bg-emerald-300 transition-colors"
                    >
                      + Add Documents
                    </button>
                  </div>
                </div>
                <ul className="space-y-2">
                  {section.items.map((item, itemIndex) => (
                    <li
                      key={itemIndex}
                      className="flex items-center justify-between"
                    >
                      <span className="flex items-center text-sm">
                        <span
                          className={`mr-2 ${
                            documentStatus[section.title]?.[item]
                              ? "text-emerald-400"
                              : "text-red-400"
                          }`}
                        >
                          {documentStatus[section.title]?.[item] ? "✓" : "❌"}
                        </span>
                        <span className="text-gray-400">{item}</span>
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs ${
                            documentStatus[section.title]?.[item]
                              ? "text-emerald-400"
                              : "text-red-400"
                          }`}
                        >
                          {documentStatus[section.title]?.[item]
                            ? "Available"
                            : "Not Provided"}
                        </span>
                        {!documentStatus[section.title]?.[item] && (
                          <button
                            onClick={() => setShowDocumentUpload(item)}
                            className="text-emerald-400 hover:text-emerald-300 text-xs"
                          >
                            Upload
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quality Assurance Checklist */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">
          Quality Assurance Checklist
        </h4>
        <div className="space-y-3">
          {[
            "All required documents uploaded and properly formatted",
            "Evidence quality standards met across all categories",
            "Red flags resolved or adequately explained",
            "Consistency verified across all submitted materials",
            "Translation requirements fulfilled for foreign documents",
          ].map((item, index) => (
            <label key={index} className="flex items-center">
              <input
                type="checkbox"
                checked={reviewChecked[index] || false}
                onChange={(e) =>
                  setReviewChecked({
                    ...reviewChecked,
                    [index]: e.target.checked,
                  })
                }
                className="mr-3 w-4 h-4"
              />
              <span className="text-white text-sm">{item}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Professional Assembly Features */}
      <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">
          Professional Package Features
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start">
            <span className="text-emerald-400 mr-2">✓</span>
            <div>
              <div className="text-white font-medium">Exhibit Labeling</div>
              <div className="text-gray-400 text-sm">
                Professional A, B, C format
              </div>
            </div>
          </div>
          <div className="flex items-start">
            <span className="text-emerald-400 mr-2">✓</span>
            <div>
              <div className="text-white font-medium">Table of Contents</div>
              <div className="text-gray-400 text-sm">
                Organized index with page numbers
              </div>
            </div>
          </div>
          <div className="flex items-start">
            <span className="text-emerald-400 mr-2">✓</span>
            <div>
              <div className="text-white font-medium">Cover Letter</div>
              <div className="text-gray-400 text-sm">
                Personalized case narrative
              </div>
            </div>
          </div>
          <div className="flex items-start">
            <span className="text-emerald-400 mr-2">✓</span>
            <div>
              <div className="text-white font-medium">Evidence Summary</div>
              <div className="text-gray-400 text-sm">
                Comprehensive documentation overview
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Chat Assistant - Hybrid Interface */}
      <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-xl font-semibold text-white flex items-center">
            <span className="text-2xl mr-3">💬</span>
            AI Immigration Assistant
          </h4>
          <button
            onClick={() => setShowChatWindow(!showChatWindow)}
            className="bg-blue-400 text-black px-4 py-2 rounded font-medium hover:bg-blue-300 transition-colors"
          >
            {showChatWindow ? "Hide Chat" : "Ask Questions"}
          </button>
        </div>

        {showChatWindow && (
          <div className="bg-gray-800 rounded-lg p-4">
            {/* Chat Messages */}
            <div className="h-80 overflow-y-auto bg-gray-900 rounded p-4 mb-4">
              {chatMessages.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  <span className="text-4xl block mb-2">🤖</span>
                  <p>Hello! I'm your AI immigration assistant.</p>
                  <p className="text-sm mt-2">
                    Ask me any questions about your case, document requirements,
                    or the immigration process.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {chatMessages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${
                        message.sender === "user"
                          ? "justify-end"
                          : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.sender === "user"
                            ? "bg-emerald-500 text-white"
                            : "bg-gray-700 text-gray-200"
                        }`}
                      >
                        <p className="text-sm">{message.text}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Chat Input */}
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleChatMessage()}
                placeholder="Type your question about immigration, documents, or your case..."
                className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-400 focus:outline-none"
              />
              <button
                onClick={handleChatMessage}
                disabled={!chatInput.trim()}
                className="bg-blue-400 text-black px-4 py-2 rounded-lg font-medium hover:bg-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send
              </button>
            </div>

            {/* Quick Questions */}
            <div className="mt-4">
              <p className="text-gray-400 text-sm mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  "What documents are still missing?",
                  "How strong is my case?",
                  "What are the next steps?",
                  "How long will the process take?",
                  "What are common red flags to avoid?",
                ].map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickQuestion(question)}
                    className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1 rounded text-sm transition-colors"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {!showChatWindow && (
          <div className="text-center py-4">
            <p className="text-gray-400 mb-2">
              Have questions about your documents or case?
            </p>
            <p className="text-blue-400 text-sm">
              Click "Ask Questions" to chat with our AI assistant while
              reviewing your case.
            </p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Corrections
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium bg-emerald-400 text-black hover:bg-emerald-300 transition-colors"
        >
          Continue to Final Confirmation →
        </button>
      </div>

      {/* Document Upload Modal */}
      {showDocumentUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Upload Document</h3>
              <button
                onClick={() => setShowDocumentUpload(null)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <div className="mb-4">
              <p className="text-gray-300 mb-2">Document Type:</p>
              <p className="text-emerald-400 font-medium">
                {showDocumentUpload}
              </p>
            </div>

            <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center">
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => {
                  if (e.target.files.length > 0) {
                    handleDocumentUpload(
                      Array.from(e.target.files),
                      showDocumentUpload
                    );
                  }
                }}
                className="hidden"
                id="document-upload"
              />
              <label htmlFor="document-upload" className="cursor-pointer">
                <div className="text-4xl mb-2">📄</div>
                <p className="text-white mb-2">
                  Click to upload or drag files here
                </p>
                <p className="text-gray-400 text-sm">
                  PDF, JPG, PNG, DOC, DOCX files accepted
                </p>
              </label>
            </div>

            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={() => setShowDocumentUpload(null)}
                className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Step 7.5: Re-Analysis After New Document Upload
const Step7ReAnalysis = ({ formData, setFormData, onNext, onBack }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [reanalysisResults, setReanalysisResults] = useState(null);

  React.useEffect(() => {
    // Auto-start analysis when component mounts
    if (!analyzing && !analysisComplete) {
      performReAnalysis();
    }
  }, []);

  const performReAnalysis = async () => {
    setAnalyzing(true);

    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

      // Call backend for re-analysis
      const response = await fetch(`${backendUrl}/api/ai/analyze-case`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          case_type: formData.caseType,
          petitioner_details: formData.petitionerDetails,
          beneficiary_details: formData.beneficiaryDetails,
          relationship_details: formData.relationshipDetails,
          documents: [
            ...(formData.documents || []),
            ...(formData.newDocuments || []),
          ],
          previous_analysis: formData.analysisResults,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setReanalysisResults(data);

        // Update form data with new analysis
        setFormData({
          ...formData,
          analysisResults: data,
          finalScore: data.overall_score,
          needsReanalysis: false,
        });
      } else {
        // Fallback to mock analysis
        const mockResults = generateMockReanalysis();
        setReanalysisResults(mockResults);
        setFormData({
          ...formData,
          analysisResults: mockResults,
          finalScore: mockResults.overall_score,
          needsReanalysis: false,
        });
      }
    } catch (error) {
      console.error("Re-analysis error:", error);
      // Use mock analysis as fallback
      const mockResults = generateMockReanalysis();
      setReanalysisResults(mockResults);
      setFormData({
        ...formData,
        analysisResults: mockResults,
        finalScore: mockResults.overall_score,
        needsReanalysis: false,
      });
    } finally {
      setAnalyzing(false);
      setAnalysisComplete(true);
    }
  };

  const generateMockReanalysis = () => {
    const newDocCount = formData.newDocuments?.length || 0;
    const baseScore = formData.analysisResults?.overall_score || 0;
    const improvementScore = Math.min(baseScore + newDocCount * 4, 95);

    return {
      overall_score: improvementScore,
      strengths: [
        ...(formData.analysisResults?.strengths || []),
        `Enhanced evidence with ${newDocCount} additional document${
          newDocCount > 1 ? "s" : ""
        }`,
        "Improved documentation completeness",
        "Strengthened case foundation",
      ],
      red_flags:
        formData.analysisResults?.red_flags?.filter(
          (flag) => !flag.includes("missing") && !flag.includes("insufficient")
        ) || [],
      document_analysis: {
        completeness_score: Math.min(
          (formData.analysisResults?.document_analysis?.completeness_score ||
            75) + 10,
          95
        ),
        quality_score: Math.min(
          (formData.analysisResults?.document_analysis?.quality_score || 80) +
            8,
          95
        ),
        total_documents: (formData.documents?.length || 0) + newDocCount,
      },
      recommendations: [
        "Excellent improvement in case strength with additional evidence",
        "Case is now ready for submission",
        "Consider final review before submission",
      ],
    };
  };

  const handleContinue = () => {
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">
          Re-Analyzing Your Case
        </h3>
        <p className="text-gray-400 text-lg">
          Analyzing your case with the newly uploaded documents
        </p>
      </div>

      {/* Analysis Progress */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-xl font-semibold text-white">
            Analysis Progress
          </h4>
          {analyzing && (
            <div className="flex items-center text-blue-400">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400 mr-2"></div>
              Analyzing...
            </div>
          )}
          {analysisComplete && (
            <div className="flex items-center text-emerald-400">
              <span className="mr-2">✓</span>
              Complete
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div
            className={`flex items-center ${
              analysisComplete
                ? "text-emerald-400"
                : analyzing
                ? "text-blue-400"
                : "text-gray-400"
            }`}
          >
            <span className="mr-2">
              {analysisComplete ? "✓" : analyzing ? "◯" : "○"}
            </span>
            Processing new document uploads (
            {formData.newDocuments?.length || 0} files)
          </div>
          <div
            className={`flex items-center ${
              analysisComplete
                ? "text-emerald-400"
                : analyzing
                ? "text-blue-400"
                : "text-gray-400"
            }`}
          >
            <span className="mr-2">
              {analysisComplete ? "✓" : analyzing ? "◯" : "○"}
            </span>
            Re-evaluating case strength and completeness
          </div>
          <div
            className={`flex items-center ${
              analysisComplete
                ? "text-emerald-400"
                : analyzing
                ? "text-blue-400"
                : "text-gray-400"
            }`}
          >
            <span className="mr-2">
              {analysisComplete ? "✓" : analyzing ? "◯" : "○"}
            </span>
            Updating recommendations and red flag analysis
          </div>
          <div
            className={`flex items-center ${
              analysisComplete
                ? "text-emerald-400"
                : analyzing
                ? "text-blue-400"
                : "text-gray-400"
            }`}
          >
            <span className="mr-2">
              {analysisComplete ? "✓" : analyzing ? "◯" : "○"}
            </span>
            Generating updated case assessment
          </div>
        </div>
      </div>

      {/* Re-analysis Results */}
      {analysisComplete && reanalysisResults && (
        <div className="space-y-6">
          {/* Updated Score */}
          <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
            <div className="text-center">
              <h4 className="text-2xl font-semibold text-white mb-2">
                Updated Case Strength
              </h4>
              <div className="text-6xl font-bold text-emerald-400 mb-2">
                {reanalysisResults.overall_score}%
              </div>
              <p className="text-emerald-300">
                Improved from {formData.analysisResults?.overall_score || 0}% (+
                {reanalysisResults.overall_score -
                  (formData.analysisResults?.overall_score || 0)}{" "}
                points)
              </p>
            </div>
          </div>

          {/* Key Improvements */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">
              Key Improvements
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-emerald-400 font-medium mb-2">
                  New Strengths Added
                </h5>
                <ul className="space-y-1">
                  {reanalysisResults.strengths
                    .slice(-3)
                    .map((strength, index) => (
                      <li
                        key={index}
                        className="flex items-start text-gray-300 text-sm"
                      >
                        <span className="text-emerald-400 mr-2">+</span>
                        {strength}
                      </li>
                    ))}
                </ul>
              </div>
              <div>
                <h5 className="text-emerald-400 font-medium mb-2">
                  Issues Resolved
                </h5>
                <ul className="space-y-1">
                  <li className="flex items-start text-gray-300 text-sm">
                    <span className="text-emerald-400 mr-2">✓</span>
                    Document completeness improved
                  </li>
                  <li className="flex items-start text-gray-300 text-sm">
                    <span className="text-emerald-400 mr-2">✓</span>
                    Evidence quality enhanced
                  </li>
                  <li className="flex items-start text-gray-300 text-sm">
                    <span className="text-emerald-400 mr-2">✓</span>
                    Case foundation strengthened
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Final Recommendations */}
          <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">
              Final Assessment
            </h4>
            <div className="space-y-3">
              {reanalysisResults.recommendations.map((rec, index) => (
                <div key={index} className="flex items-start">
                  <span className="text-blue-400 mr-2">•</span>
                  <span className="text-gray-300 text-sm">{rec}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hybrid Chat Assistant */}
      <HybridChatAssistant formData={formData} stepContext="analysis" />

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Review
        </button>
        <button
          onClick={handleContinue}
          disabled={!analysisComplete}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            analysisComplete
              ? "bg-emerald-400 text-black hover:bg-emerald-300"
              : "bg-gray-700 text-gray-400 cursor-not-allowed"
          }`}
        >
          Continue to Final Confirmation →
        </button>
      </div>
    </div>
  );
};

// Step 8: Final Confirmation & Generation
const Step8FinalConfirmation = ({
  formData,
  setFormData,
  onComplete,
  onBack,
}) => {
  const [confirmed, setConfirmed] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);

    try {
      // Get the backend URL from environment or default
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

      // Call the document generation API
      const response = await fetch(`${backendUrl}/api/ai/generate-package`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          case_type: formData.caseType,
          petitioner_details: formData.petitionerDetails,
          beneficiary_details: formData.beneficiaryDetails,
          relationship_details: formData.relationshipDetails,
          documents: (formData.documents || []).map(doc => {
            // Create a copy without the file object for JSON serialization
            const { file, ...docWithoutFile } = doc;
            return docWithoutFile;
          }),
          analysis_results: formData.analysisResults || {},
          final_score: formData.finalScore || 0,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const packageData = await response.json();

      setGenerating(false);
      setGenerated(true);

      // Store package data for download
      setFormData({
        ...formData,
        packageData,
      });

      // Show success message with real download link
      setTimeout(() => {
        alert(
          `Your professional immigration package has been generated successfully! Package ID: ${
            packageData.package_id
          }\n\nExpires: ${new Date(
            packageData.expires_at
          ).toLocaleDateString()}`
        );
      }, 1000);
    } catch (error) {
      console.error("Document generation error:", error);
      setGenerating(false);
      alert("Error generating package: " + error.message);
    }
  };

  const handleDownload = () => {
    if (formData.packageData) {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
      const downloadUrl = `${backendUrl}${formData.packageData.download_url}`;

      // Create a temporary link to trigger download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `immigration_package_${formData.packageData.package_id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloaded(true);
    }
  };

  const handleMakeChanges = () => {
    // Go back to previous steps to make changes
    onBack();
  };

  const handleExit = () => {
    // Complete the process
    onComplete();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">
          Final Confirmation & Generation
        </h3>
        <p className="text-gray-400 text-lg">
          Ready to generate your professional immigration package
        </p>
      </div>

      {/* Case Summary */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">Case Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-emerald-400 mb-1">
              {formData.finalScore}%
            </div>
            <div className="text-gray-400 text-sm">Final Strength Score</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-400 mb-1">
              {(formData.documents?.length || 0) +
                (formData.newDocuments?.length || 0)}
            </div>
            <div className="text-gray-400 text-sm">Total Documents</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-400 mb-1">
              {formData.caseType?.toUpperCase()}
            </div>
            <div className="text-gray-400 text-sm">Case Type</div>
          </div>
        </div>
      </div>

      {/* Package Contents */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">
          Your Package Will Include
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-center">
              <span className="text-emerald-400 mr-2">📋</span>
              <span className="text-white">Professional cover letter</span>
            </div>
            <div className="flex items-center">
              <span className="text-emerald-400 mr-2">📑</span>
              <span className="text-white">
                Table of contents with page numbers
              </span>
            </div>
            <div className="flex items-center">
              <span className="text-emerald-400 mr-2">📂</span>
              <span className="text-white">Organized evidence exhibits</span>
            </div>
            <div className="flex items-center">
              <span className="text-emerald-400 mr-2">✍️</span>
              <span className="text-white">AI-generated affidavits</span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center">
              <span className="text-emerald-400 mr-2">📊</span>
              <span className="text-white">Case strength analysis report</span>
            </div>
            <div className="flex items-center">
              <span className="text-emerald-400 mr-2">🔍</span>
              <span className="text-white">Evidence quality assessment</span>
            </div>
            <div className="flex items-center">
              <span className="text-emerald-400 mr-2">⚖️</span>
              <span className="text-white">Legal compliance verification</span>
            </div>
            <div className="flex items-center">
              <span className="text-emerald-400 mr-2">📝</span>
              <span className="text-white">
                Filing instructions and timeline
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Final Confirmation */}
      <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">
          Final Confirmation
        </h4>
        <div className="space-y-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mr-3 w-4 h-4"
            />
            <span className="text-white">
              I confirm that all information provided is accurate and complete.
              I understand that this AI-generated package is for guidance
              purposes and does not constitute legal advice.
            </span>
          </label>

          <div className="bg-gray-800 p-4 rounded">
            <p className="text-gray-300 text-sm">
              <strong>Important:</strong> This package is designed to strengthen
              your case, but we recommend having it reviewed by an immigration
              attorney before submission to USCIS.
            </p>
          </div>
        </div>
      </div>

      {/* Generation Status */}
      {generating && (
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-400 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Generating Your Package</h4>
          <p className="text-gray-400">Creating professional documents...</p>
        </div>
      )}

      {generated && !downloaded && (
        <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">🎉</div>
            <h4 className="text-xl text-white mb-2">
              Package Generated Successfully!
            </h4>
            <p className="text-gray-400">
              Your professional immigration package is ready for download.
            </p>
          </div>

          {/* Clear Download Instructions */}
          <div className="bg-blue-900/20 border border-blue-400 p-4 rounded-lg mb-6">
            <h5 className="text-white font-medium mb-2 flex items-center">
              <span className="text-2xl mr-2">👆</span>
              Download Instructions
            </h5>
            <div className="text-gray-300 text-sm space-y-2">
              <div className="flex items-start">
                <span className="text-blue-400 mr-2">1.</span>
                <span>
                  Click the green "Download Your Immigration Package" button
                  below
                </span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2">2.</span>
                <span>
                  Your browser will download a ZIP file containing all documents
                </span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2">3.</span>
                <span>
                  Extract the ZIP file to access your professional immigration
                  documents
                </span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2">4.</span>
                <span>Review all documents before submitting to USCIS</span>
              </div>
            </div>
          </div>

          {/* Prominent Download Button - Center of Screen */}
          <div className="text-center my-8">
            <div className="mb-4">
              <div className="animate-bounce text-yellow-400 text-2xl mb-2">
                👇
              </div>
              <p className="text-white font-medium">
                Click here to download your package
              </p>
            </div>
            <button
              onClick={handleDownload}
              className="bg-emerald-400 hover:bg-emerald-300 text-black px-12 py-6 rounded-xl font-bold text-2xl transition-colors shadow-lg transform hover:scale-105 animate-pulse"
            >
              📥 Download Your Immigration Package
            </button>
            <div className="mt-4">
              <p className="text-gray-400 text-sm">
                Complete document package • Professional formatting • Ready for
                USCIS
              </p>
              <div className="flex justify-center items-center mt-2 space-x-4 text-xs">
                <span className="text-emerald-400">✓ ZIP Format</span>
                <span className="text-emerald-400">✓ Word Documents</span>
                <span className="text-emerald-400">✓ Professional Layout</span>
              </div>
            </div>
          </div>

          {formData.packageData && (
            <div className="bg-gray-800 p-4 rounded-lg">
              <h5 className="text-white font-medium mb-3">Package Contents:</h5>
              <ul className="space-y-2">
                {formData.packageData.package_contents.map((item, index) => (
                  <li key={index} className="flex items-center text-gray-300">
                    <span className="text-emerald-400 mr-2">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-4 text-xs text-gray-400">
                Package ID: {formData.packageData.package_id}
                <br />
                Expires:{" "}
                {new Date(formData.packageData.expires_at).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Post-Download Options */}
      {downloaded && (
        <div className="bg-green-900/20 border border-green-400 p-6 rounded-lg">
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">✅</div>
            <h4 className="text-xl text-white mb-2">Download Complete!</h4>
            <p className="text-gray-400">
              Your immigration package has been downloaded successfully.
            </p>
          </div>

          <div className="bg-gray-800 p-4 rounded-lg mb-6">
            <h5 className="text-white font-medium mb-2">What's Next?</h5>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li className="flex items-start">
                <span className="text-green-400 mr-2 mt-1">•</span>
                <span>Review all documents in your downloaded package</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2 mt-1">•</span>
                <span>
                  Consider having the package reviewed by an immigration
                  attorney
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2 mt-1">•</span>
                <span>
                  Submit your petition to USCIS according to the filing
                  instructions
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2 mt-1">•</span>
                <span>Keep copies of all documents for your records</span>
              </li>
            </ul>
          </div>

          <div className="text-center space-y-4">
            <p className="text-white font-medium">
              What would you like to do next?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleMakeChanges}
                className="bg-blue-500 hover:bg-blue-400 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              >
                🔄 Make Changes to Package
              </button>
              <button
                onClick={handleExit}
                className="bg-gray-600 hover:bg-gray-500 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              >
                ✨ Complete & Exit
              </button>
            </div>
            <p className="text-gray-400 text-sm mt-4">
              You can always come back to make changes or generate a new
              package.
            </p>
          </div>
        </div>
      )}

      {/* Navigation */}
      {!downloaded && (
        <div className="flex justify-between">
          <button
            onClick={onBack}
            className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
            disabled={generating}
          >
            ← Back to Review
          </button>

          {!generated ? (
            <button
              onClick={handleGenerate}
              disabled={!confirmed || generating}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                confirmed && !generating
                  ? "bg-emerald-400 text-black hover:bg-emerald-300"
                  : "bg-gray-700 text-gray-400 cursor-not-allowed"
              }`}
            >
              {generating ? "Generating..." : "Generate Package →"}
            </button>
          ) : (
            <div className="text-emerald-400 font-medium px-6 py-3">
              Ready to download! ↑
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// AI Content Generation Library Component
const AIContentLibrary = ({ formData, isOpen, onClose }) => {
  const [selectedContent, setSelectedContent] = useState(null);
  const [generatedContent, setGeneratedContent] = useState({});
  const [generating, setGenerating] = useState(false);

  const contentTypes = [
    {
      id: "affidavit",
      title: "Affidavit of Support",
      description: "Personal statement from friends/family",
      icon: "📝",
      category: "Personal Statements",
    },
    {
      id: "cover-letter",
      title: "Professional Cover Letter",
      description: "Case summary for USCIS",
      icon: "📋",
      category: "Official Documents",
    },
    {
      id: "explanation-letter",
      title: "Explanation Letter",
      description: "Clarify unusual circumstances",
      icon: "✉️",
      category: "Explanations",
    },
    {
      id: "relationship-statement",
      title: "Relationship Statement",
      description: "Detailed relationship history",
      icon: "💕",
      category: "Personal Statements",
    },
    {
      id: "financial-explanation",
      title: "Financial Explanation",
      description: "Clarify financial circumstances",
      icon: "💰",
      category: "Explanations",
    },
    {
      id: "travel-explanation",
      title: "Travel History Explanation",
      description: "Explain travel patterns",
      icon: "✈️",
      category: "Explanations",
    },
  ];

  const handleGenerateContent = async (contentType) => {
    setGenerating(true);
    setSelectedContent(contentType);

    try {
      // Call backend AI generation (simplified for demo)
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/generate-content`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            content_type: contentType.id,
            petitioner_details: formData.petitionerDetails,
            beneficiary_details: formData.beneficiaryDetails,
            relationship_details: formData.relationshipDetails,
            case_type: formData.caseType,
          }),
        }
      );

      let content;
      if (response.ok) {
        const data = await response.json();
        content = data.content;
      } else {
        // Fallback to mock content if API fails
        content = generateMockContent(contentType.id);
      }

      setGeneratedContent({
        ...generatedContent,
        [contentType.id]: content,
      });
    } catch (error) {
      console.error("Content generation error:", error);
      // Use mock content as fallback
      setGeneratedContent({
        ...generatedContent,
        [contentType.id]: generateMockContent(contentType.id),
      });
    } finally {
      setGenerating(false);
    }
  };

  const generateMockContent = (type) => {
    const mockContent = {
      affidavit: `AFFIDAVIT OF SUPPORT FOR IMMIGRATION PETITION

I, [Full Name], hereby declare under penalty of perjury that I have personal knowledge of the bona fide marital relationship between ${
        formData.petitionerDetails?.fullName || "[Petitioner Name]"
      } and ${formData.beneficiaryDetails?.fullName || "[Beneficiary Name]"}.

I have known the petitioner for [Duration] and have observed their relationship since [Date]. Based on my personal observations, I can attest to the genuine nature of their marriage and their commitment to building a life together.

Specific observations include:
- Their interactions demonstrate genuine affection and care
- They share common interests and future plans
- They have integrated their social circles and family relationships
- Their relationship has developed naturally over time

I declare under penalty of perjury under the laws of the United States that the foregoing is true and correct.

Signature: _______________________
Date: ___________________________
[Printed Name]
[Address]
[Phone Number]`,

      "cover-letter": `COVER LETTER FOR IMMIGRATION PETITION

TO: U.S. Citizenship and Immigration Services
FROM: ${formData.petitionerDetails?.fullName || "[Petitioner Name]"}
RE: Form I-130 Petition for ${
        formData.beneficiaryDetails?.fullName || "[Beneficiary Name]"
      }

Dear Immigration Officer,

I am writing to submit my petition for ${
        formData.beneficiaryDetails?.fullName || "[Beneficiary Name]"
      }, my spouse, to immigrate to the United States. This petition is supported by comprehensive evidence demonstrating our bona fide marriage and eligibility for family-based immigration.

Case Summary:
- Petitioner Status: ${formData.petitionerDetails?.status || "[Status]"}
- Beneficiary Country: ${formData.beneficiaryDetails?.country || "[Country]"}
- Marriage Date: ${formData.relationshipDetails?.marriageDate || "[Date]"}
- Relationship Duration: [Duration]

The attached evidence includes:
- Government-issued documents establishing identity and relationship
- Financial documents demonstrating shared financial responsibility
- Photographs spanning our relationship timeline
- Third-party affidavits from family and friends

We respectfully request favorable consideration of this petition and look forward to building our future together in the United States.

Sincerely,
${formData.petitionerDetails?.fullName || "[Petitioner Name]"}`,

      "explanation-letter": `EXPLANATION LETTER

TO: U.S. Citizenship and Immigration Services
FROM: ${formData.petitionerDetails?.fullName || "[Petitioner Name]"}
RE: Explanation of Circumstances

Dear Immigration Officer,

I am writing to provide clarification regarding [specific issue or circumstance] in connection with our immigration petition.

Background:
[Detailed explanation of the situation, including relevant dates, circumstances, and any actions taken]

Resolution:
[Description of how the issue was resolved or is being addressed]

Supporting Documentation:
[List of any additional documents provided to support the explanation]

I believe this explanation provides the necessary context for your review and demonstrates our commitment to compliance with immigration law.

Thank you for your consideration.

Sincerely,
${formData.petitionerDetails?.fullName || "[Petitioner Name]"}`,

      "relationship-statement": `RELATIONSHIP STATEMENT

Our Love Story: ${
        formData.petitionerDetails?.fullName || "[Petitioner Name]"
      } and ${formData.beneficiaryDetails?.fullName || "[Beneficiary Name]"}

How We Met:
${formData.relationshipDetails?.howMet || "[Description of how you met]"}

Our Relationship Timeline:
${
  formData.relationshipDetails?.marriageDate
    ? `- Marriage Date: ${formData.relationshipDetails.marriageDate}`
    : "- Marriage Date: [Date]"
}
- [Add other significant milestones]

Our Life Together:
We have built a life together that demonstrates our commitment to each other and our shared future. Our relationship is characterized by mutual support, shared responsibilities, and genuine love.

Daily Life:
[Description of daily routines, shared activities, and how you function as a couple]

Future Plans:
[Description of your plans for the future, including career goals, family plans, and life aspirations]

This statement reflects our genuine commitment to each other and our desire to build our life together in the United States.

Sincerely,
${formData.petitionerDetails?.fullName || "[Petitioner Name]"} and ${
        formData.beneficiaryDetails?.fullName || "[Beneficiary Name]"
      }`,

      "financial-explanation": `FINANCIAL EXPLANATION LETTER

TO: U.S. Citizenship and Immigration Services
FROM: ${formData.petitionerDetails?.fullName || "[Petitioner Name]"}
RE: Financial Circumstances Explanation

Dear Immigration Officer,

I am writing to provide clarification regarding our financial circumstances and ability to support ${
        formData.beneficiaryDetails?.fullName || "[Beneficiary Name]"
      }.

Current Financial Status:
- Annual Income: ${formData.petitionerDetails?.income || "[Income]"}
- Employment: ${formData.petitionerDetails?.employment || "[Employment]"}
- Assets: [Description of assets]

Financial Support Plan:
[Detailed explanation of how you will financially support the beneficiary]

Additional Support:
[Description of any additional financial support from family, joint sponsors, etc.]

I am committed to providing financial support for my spouse and ensuring compliance with all immigration requirements.

Sincerely,
${formData.petitionerDetails?.fullName || "[Petitioner Name]"}`,

      "travel-explanation": `TRAVEL HISTORY EXPLANATION

TO: U.S. Citizenship and Immigration Services
FROM: ${formData.petitionerDetails?.fullName || "[Petitioner Name]"}
RE: Travel History Explanation

Dear Immigration Officer,

I am writing to provide clarification regarding travel history and patterns related to our immigration petition.

Travel Summary:
[Detailed explanation of travel patterns, including dates, destinations, and purposes]

Relationship-Related Travel:
[Description of travel undertaken to maintain the relationship, including visits, meetings, and shared trips]

Current Status:
[Explanation of current travel status and any ongoing considerations]

All travel was conducted in accordance with applicable laws and regulations. The pattern of travel demonstrates our commitment to maintaining our relationship despite geographical challenges.

Thank you for your consideration.

Sincerely,
${formData.petitionerDetails?.fullName || "[Petitioner Name]"}`,
    };

    return mockContent[type] || "Content generated successfully!";
  };

  const handleDownloadDocument = (contentType) => {
    const content = generatedContent[contentType.id];
    if (!content) return;

    // Create Word document using a simple approach
    const element = document.createElement("a");
    const file = new Blob([content], { type: "application/msword" });
    element.href = URL.createObjectURL(file);
    element.download = `${contentType.title.replace(
      /\s+/g,
      "_"
    )}_${Date.now()}.doc`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold text-white">
                AI Content Generation Library
              </h2>
              <p className="text-gray-400">
                Generate professional documents for your immigration case
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>

          {/* Content Types Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {contentTypes.map((contentType) => (
              <div
                key={contentType.id}
                className="bg-gray-800 p-4 rounded-lg border border-gray-700"
              >
                <div className="flex items-center mb-3">
                  <span className="text-2xl mr-3">{contentType.icon}</span>
                  <div>
                    <h3 className="text-white font-medium">
                      {contentType.title}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {contentType.description}
                    </p>
                  </div>
                </div>
                <div className="text-xs text-blue-400 mb-3">
                  {contentType.category}
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleGenerateContent(contentType)}
                    disabled={
                      generating && selectedContent?.id === contentType.id
                    }
                    className="flex-1 bg-emerald-400 text-black px-3 py-2 rounded text-sm font-medium hover:bg-emerald-300 transition-colors disabled:opacity-50"
                  >
                    {generating && selectedContent?.id === contentType.id
                      ? "Generating..."
                      : "Generate"}
                  </button>

                  {generatedContent[contentType.id] && (
                    <button
                      onClick={() => handleDownloadDocument(contentType)}
                      className="bg-blue-400 text-white px-3 py-2 rounded text-sm font-medium hover:bg-blue-300 transition-colors"
                    >
                      📥 Download
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Content Preview */}
          {selectedContent && generatedContent[selectedContent.id] && (
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-white mb-4">
                Generated: {selectedContent.title}
              </h3>
              <div className="bg-gray-900 p-4 rounded border max-h-64 overflow-y-auto">
                <pre className="text-gray-300 text-sm whitespace-pre-wrap">
                  {generatedContent[selectedContent.id]}
                </pre>
              </div>
              <div className="mt-4 flex justify-between items-center">
                <p className="text-gray-400 text-sm">
                  Preview of generated content. Click Download to save as Word
                  document.
                </p>
                <button
                  onClick={() => handleDownloadDocument(selectedContent)}
                  className="bg-emerald-400 text-black px-6 py-2 rounded font-medium hover:bg-emerald-300 transition-colors"
                >
                  📥 Download as Word Doc
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Step 6: User Correction / Addition Loop
const Step6UserCorrection = ({ formData, setFormData, onNext, onBack }) => {
  const [improvements, setImprovements] = useState({});
  const [uploadedNewDocs, setUploadedNewDocs] = useState([]);
  const [showContentLibrary, setShowContentLibrary] = useState(false);

  const handleAddDocument = (category, file) => {
    const newDoc = {
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      category: category,
      uploaded: true,
      isNew: true,
    };

    setUploadedNewDocs([...uploadedNewDocs, newDoc]);
  };

  const handleContinue = () => {
    setFormData({
      ...formData,
      improvements,
      newDocuments: uploadedNewDocs,
    });
    onNext();
  };

  // Calculate current strength dynamically
  const currentStrength = formData.analysisResults?.overall_score || 0;

  // Calculate recommendations addressed based on user actions
  const recommendationsAddressed = Object.keys(improvements).filter(
    (key) => improvements[key]
  ).length;

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">
          Improve Your Case
        </h3>
        <p className="text-gray-400 text-lg">
          Add missing documents and enhance existing evidence
        </p>
      </div>

      {/* Improvement Dashboard */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">
          Improvement Progress
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400 mb-1">
              {currentStrength}%
            </div>
            <div className="text-gray-400 text-sm">Current Strength</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-400 mb-1">
              {uploadedNewDocs.length}
            </div>
            <div className="text-gray-400 text-sm">New Documents Added</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400 mb-1">
              {recommendationsAddressed}
            </div>
            <div className="text-gray-400 text-sm">
              Recommendations Addressed
            </div>
          </div>
        </div>
      </div>

      {/* High Priority Items */}
      <div className="bg-red-900/20 border border-red-400 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4 flex items-center">
          <span className="text-2xl mr-3">🚨</span>
          High Priority Improvements
        </h4>
        <div className="space-y-4">
          <div className="bg-gray-800 p-4 rounded">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white">
                Joint Tax Returns (Past 2 Years)
              </span>
              <button
                className="bg-emerald-400 text-black px-3 py-1 rounded text-sm font-medium hover:bg-emerald-300 transition-colors"
                onClick={() => document.getElementById("tax-returns").click()}
              >
                Upload
              </button>
              <input
                id="tax-returns"
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) =>
                  Array.from(e.target.files).forEach((file) =>
                    handleAddDocument("tax-returns", file)
                  )
                }
              />
            </div>
            <p className="text-gray-400 text-sm">
              Critical for proving financial unity
            </p>
          </div>

          <div className="bg-gray-800 p-4 rounded">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white">
                Joint Utility Bills (12 Months)
              </span>
              <button
                className="bg-emerald-400 text-black px-3 py-1 rounded text-sm font-medium hover:bg-emerald-300 transition-colors"
                onClick={() => document.getElementById("utility-bills").click()}
              >
                Upload
              </button>
              <input
                id="utility-bills"
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) =>
                  Array.from(e.target.files).forEach((file) =>
                    handleAddDocument("utility-bills", file)
                  )
                }
              />
            </div>
            <p className="text-gray-400 text-sm">
              Shows shared residence history
            </p>
          </div>
        </div>
      </div>

      {/* AI Content Generation Library */}
      <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4 flex items-center">
          <span className="text-2xl mr-3">🤖</span>
          AI Content Generation Library
        </h4>
        <p className="text-gray-300 mb-4">
          Generate professional documents with AI assistance. Choose from
          various document types and download as Word documents.
        </p>
        <div className="text-center">
          <button
            onClick={() => setShowContentLibrary(true)}
            className="bg-emerald-400 text-black px-8 py-3 rounded-lg font-medium hover:bg-emerald-300 transition-colors transform hover:scale-105"
          >
            📚 Open Content Library
          </button>
        </div>
      </div>

      {/* AI Content Library Modal */}
      <AIContentLibrary
        formData={formData}
        isOpen={showContentLibrary}
        onClose={() => setShowContentLibrary(false)}
      />

      {/* New Documents Added */}
      {uploadedNewDocs.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">
            Recently Added Documents
          </h4>
          <div className="space-y-2">
            {uploadedNewDocs.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between bg-gray-700 p-2 rounded"
              >
                <div className="flex items-center">
                  <span className="text-emerald-400 mr-2">📄</span>
                  <span className="text-white text-sm">{doc.name}</span>
                  <span className="text-emerald-400 text-xs ml-2 bg-emerald-900/20 px-2 py-1 rounded">
                    NEW
                  </span>
                </div>
                <button
                  onClick={() =>
                    setUploadedNewDocs(
                      uploadedNewDocs.filter((d) => d.id !== doc.id)
                    )
                  }
                  className="text-red-400 hover:text-red-300 text-sm"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hybrid Chat Assistant */}
      <HybridChatAssistant formData={formData} stepContext="general" />

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Recommendations
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium bg-emerald-400 text-black hover:bg-emerald-300 transition-colors"
        >
          Continue to Final Review →
        </button>
      </div>
    </div>
  );
};
const agents = [
  {
    id: 1,
    name: "Family-Based Agent",
    icon: "👨‍👩‍👧‍👦",
    color: "emerald",
    description: "Helps with I-130, I-485, I-751",
    features: [
      "Relationship evidence checklist",
      "Red flag detection & Risk assessment",
      "USCIS-ready response packet generator",
      "Advanced AI feedback",
      "Case Strength Scoring & document gap detection",
    ],
  },
  {
    id: 2,
    name: "Work Visa Agent",
    icon: "💼",
    color: "orange",
    description: "Supports H-1B, L-1, O-1 filings",
    features: [
      "Evidence coaching & red flag alerts",
      "AI-generated cover letters & summary memos",
      "Full packet generation",
    ],
  },
  {
    id: 3,
    name: "Asylum & Protection Agent",
    icon: "🛡️",
    color: "red",
    description: "For asylum, VAWA, humanitarian relief",
    features: [
      "Personal statement builder",
      "Red flag detection & Risk assessment",
      "USCIS-ready response packet generator",
      "Advanced AI feedback",
      "Case Strength Scoring",
      "Evidence coach",
    ],
  },
  {
    id: 4,
    name: "Student & Exchange Visa Agent",
    icon: "🎓",
    color: "blue",
    description: "Helps with F-1, J-1, M-1, OPT",
    features: [
      "Red flag detection & Risk assessment",
      "USCIS-ready response packet generator",
      "Advanced AI feedback",
      "Evidence summaries",
    ],
  },
  {
    id: 5,
    name: "Citizenship Agent",
    icon: "🇺🇸",
    color: "orange",
    description: "For N-400 and naturalization prep",
    features: [
      "Eligibility pre-check",
      "Form preparation assistant",
      "USCIS civics & interview coaching",
      "Risk flag and completeness analysis",
    ],
  },
  {
    id: 6,
    name: "RFE / NOID Response Agent",
    icon: "📄",
    color: "gray",
    description: "Your coach for tough USCIS letters",
    features: [
      "Upload and analyze USCIS notice",
      "Red flag detection & Risk assessment",
      "USCIS-ready response packet generator",
      "Advanced AI feedback",
      "Case Strength Scoring & document gap detection",
    ],
  },
  {
    id: 7,
    name: "DACA / TPS Renewal Agent",
    icon: "🔄",
    color: "blue",
    description: "Monitoring status & renewal requirements",
    features: [
      "Renewal reminders + deadline tracking",
      "Evidence preparation tools",
      "Case risk detection + document scanner",
      "Prebuilt response kits",
    ],
  },
  {
    id: 8,
    name: "Visitor & Tourist Visa Agent",
    icon: "✈️",
    color: "blue",
    description: "Executing B-1/B-2 visitor processes",
    features: [
      "DS-160 walkthrough",
      "Interview tips + red flag alerts",
      "Visa extension coaching",
      "Travel and intent explanation templates",
    ],
  },
];

// AI Agents Component
const AIAgents = ({ onStartCaseReview, user, onAuthRequired }) => {
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [activeAgentFlow, setActiveAgentFlow] = useState(null);
  const [agentChats, setAgentChats] = useState({});
  const [chatInput, setChatInput] = useState("");
  const [styles, setStyles] = useState({});
  const [enableTilt, setEnableTilt] = useState(true);

  useEffect(() => {
    const checkScreen = () => {
      setEnableTilt(window.innerWidth >= 800);
    };

    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  const handleMouseMove = (e, index) => {
    if (!enableTilt) return;

    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = -(y - centerY) / 40;
    const rotateY = (x - centerX) / 40;

    setStyles((prev) => ({
      ...prev,
      [index]: {
        transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
        transition: "transform 0.1s ease",
      },
    }));
  };

  const handleMouseLeave = (index) => {
    if (!enableTilt) return;

    setStyles((prev) => ({
      ...prev,
      [index]: {
        transform: "rotateX(0deg) rotateY(0deg)",
        transition: "transform 0.3s ease",
      },
    }));
  };

  // Get chat messages for current agent
  const getCurrentAgentChat = () => {
    if (!selectedAgent) return [];
    const selectedAgentData = agents.find(
      (agent) => agent.id === selectedAgent
    );
    if (!selectedAgentData) return [];
    const agentId =
      selectedAgentData.slug ||
      selectedAgentData.name.toLowerCase().replace(/[^a-z0-9]/g, "-");
    return agentChats[agentId] || [];
  };

  // Update chat for current agent
  const updateCurrentAgentChat = (newMessages) => {
    if (!selectedAgent) return;
    const selectedAgentData = agents.find(
      (agent) => agent.id === selectedAgent
    );
    if (!selectedAgentData) return;
    const agentId =
      selectedAgentData.slug ||
      selectedAgentData.name.toLowerCase().replace(/[^a-z0-9]/g, "-");
    setAgentChats((prev) => ({
      ...prev,
      [agentId]: newMessages,
    }));
  };

  // Function to render formatted text with basic markdown support
  const renderFormattedText = (text) => {
    if (!text) return "";

    let formattedText = text
      .replace(
        /### (.*$)/gm,
        '<h3 class="text-white font-bold text-base mt-3 mb-2">$1</h3>'
      )
      .replace(
        /## (.*$)/gm,
        '<h2 class="text-white font-bold text-lg mt-3 mb-2">$1</h2>'
      )
      .replace(
        /# (.*$)/gm,
        '<h1 class="text-white font-bold text-xl mt-3 mb-2">$1</h1>'
      )
      .replace(
        /\*\*(.*?)\*\*/g,
        '<strong class="text-blue-300 font-semibold">$1</strong>'
      )
      .replace(
        /^• (.*$)/gm,
        '<div class="flex items-start mt-1"><span class="text-blue-400 mr-2 mt-1">•</span><span>$1</span></div>'
      )
      .replace(
        /^- (.*$)/gm,
        '<div class="flex items-start mt-1"><span class="text-blue-400 mr-2 mt-1">•</span><span>$1</span></div>'
      )
      .replace(
        /^(\d+)\. (.*$)/gm,
        '<div class="flex items-start mt-1"><span class="text-blue-400 mr-2 mt-1 font-medium">$1.</span><span>$2</span></div>'
      )
      .replace(/\n\n/g, "<br/><br/>")
      .replace(/\n/g, "<br/>");

    return formattedText;
  };

  // Agent-specific questions for main chat interface
  const getAgentSpecificQuestions = (agentType) => {
    const agentQuestions = {
      "family-based-agent": [
        "What documents do I need for my I-130 petition?",
        "How long does family-based immigration take?",
        "What income requirements must I meet as a sponsor?",
        "Can I file I-130 and I-485 at the same time?",
        "What evidence proves our relationship is genuine?",
      ],
      "work-visa-agent": [
        "What are the requirements for H-1B visa?",
        "How long does the H-1B process take?",
        "What documents does my employer need to provide?",
        "Can I change jobs while my H-1B is pending?",
        "What's the difference between L-1A and L-1B?",
      ],
      "asylum-protection-agent": [
        "What evidence do I need for my asylum case?",
        "How long do I have to file for asylum?",
        "What should I include in my personal statement?",
        "Can family members be included in my asylum case?",
        "What country condition evidence should I gather?",
      ],
      "student-exchange-visa-agent": [
        "What documents do I need for F-1 visa?",
        "How do I apply for OPT work authorization?",
        "Can I transfer to a different school on F-1?",
        "What are the requirements for J-1 visa?",
        "How long can I stay on student visa?",
      ],
      "citizenship-agent": [
        "Am I eligible for naturalization?",
        "What documents do I need for N-400 application?",
        "How long does the citizenship process take?",
        "What questions are on the civics test?",
        "Can I travel while my N-400 is pending?",
      ],
      "rfe-noid-response-agent": [
        "What does an RFE mean for my case?",
        "How long do I have to respond to an RFE?",
        "What evidence should I submit in my RFE response?",
        "Can I get an extension to respond to an RFE?",
        "What's the difference between RFE and NOID?",
      ],
      "daca-tps-renewal-agent": [
        "When should I file my DACA renewal?",
        "What documents do I need for DACA renewal?",
        "How much does DACA renewal cost?",
        "What if my DACA expired before I renewed?",
        "Can I travel with advance parole on DACA?",
      ],
      "visitor-tourist-visa-agent": [
        "What documents do I need for B-2 tourist visa?",
        "How long can I stay on a visitor visa?",
        "What's the difference between B-1 and B-2 visas?",
        "Can I extend my visitor visa while in the US?",
        "What should I prepare for the visa interview?",
      ],
    };

    return agentQuestions[agentType] || agentQuestions["family-based-agent"];
  };

  const agents = [
    {
      id: 1,
      name: "Family-Based Agent",
      agentKey:"family-based",
      icon: "👨‍👩‍👧‍👦",
      description: "Helps with I-130, I-485, I-751",
      features: [
        "Relationship evidence checklist",
        "Red flag detection & Risk assessment",
        "USCIS-ready response packet generator",
        "Advanced AI feedback",
        "Case Strength Scoring & document gap detection",
      ],
      route: "/family-based",
    },
    {
      id: 2,
      name: "Work Visa Agent",
      icon: "💼",
      agentKey:"work-visa",
      description: "Supports H-1B, L-1, O-1 filings",
      features: [
        "Evidence coaching & red flag alerts",
        "AI-generated cover letters & summary memos",
        "Full packet generation",
      ],
      route: "/work-visa",
    },
    {
      id: 3,
      name: "Asylum & Protection Agent",
      icon: "🛡️",
      agentKey:"asylum-protection",
      description: "For asylum, VAWA, humanitarian relief",
      features: [
        "Personal statement builder",
        "Red flag detection & Risk assessment",
        "USCIS-ready response packet generator",
        "Advanced AI feedback",
        "Case Strength Scoring",
        "Evidence coach",
      ],
      route: "/asylum-protection",
    },
    {
      id: 4,
      name: "Student & Exchange Visa Agent",
      icon: "🎓",
      agentKey:"student-visa",
      description: "Helps with F-1, J-1, M-1, OPT",
      features: [
        "Red flag detection & Risk assessment",
        "USCIS-ready response packet generator",
        "Advanced AI feedback",
        "Evidence summaries",
      ],
      route: "/student-exchange",
    },
    {
      id: 5,
      name: "Citizenship Agent",
      icon: "🇺🇸",
      agentKey:"citizenship",
      description: "For N-400 and naturalization prep",
      features: [
        "Eligibility pre-check",
        "Form preparation assistant",
        "USCIS civics & interview coaching",
        "Risk flag and completeness analysis",
      ],
      route: "/citizenship",
    },
    {
      id: 6,
      name: "RFE / NOID Response Agent",
      icon: "📄",
      agentKey:"rfe-noid",
      description: "Your coach for tough USCIS letters",
      features: [
        "Upload and analyze USCIS notice",
        "Red flag detection & Risk assessment",
        "USCIS-ready response packet generator",
        "Advanced AI feedback",
        "Case Strength Scoring & document gap detection",
      ],
      route: "/rfe-noid",
    },
    {
      id: 7,
      name: "DACA / TPS Renewal Agent",
      icon: "🔄",
      agentKey:"daca-tps",
      description: "Monitoring status & renewal requirements",
      features: [
        "Renewal reminders + deadline tracking",
        "Evidence preparation tools",
        "Case risk detection + document scanner",
        "Prebuilt response kits",
      ],
      route: "/daca-tps",
    },
    {
      id: 8,
      name: "Visitor & Tourist Visa Agent",
      icon: "✈️",
      agentKey:"visitor-tourist",
      description: "Executing B-1/B-2 visitor processes",
      features: [
        "DS-160 walkthrough",
        "Interview tips + red flag alerts",
        "Visa extension coaching",
        "Travel and intent explanation templates",
      ],
      route: "/visitor-tourist",
    },
  ];

  const handleAgentSelect = (agent) => {
    if (!user) {
      onAuthRequired("signin");
      return;
    }

    setSelectedAgent(agent.id);
    setShowAgentModal(true);
  };

  const handleFlowSelect = (flowType) => {
    if (!user) {
      onAuthRequired("signin");
      return;
    }

    if (flowType === "review") {
      const selectedAgentData = agents.find(
        (agent) => agent.id === selectedAgent
      );

      const agentTypeMap = {
        "Family-Based Agent": "family-based-agent",
        "Work Visa Agent": "work-visa-agent",
        "Asylum & Protection Agent": "asylum---protection-agent",
        "Student & Exchange Visa Agent": "student---exchange-visa-agent",
        "Citizenship Agent": "citizenship-agent",
        "RFE / NOID Response Agent": "rfe---noid-response-agent",
        "DACA / TPS Renewal Agent": "daca---tps-renewal-agent",
        "Visitor & Tourist Visa Agent": "visitor---tourist-visa-agent",
      };

      const agentType =
        agentTypeMap[selectedAgentData?.name] || "family-based-agent";
      onStartCaseReview(agentType);
      setShowAgentModal(false);
    } else if (flowType === "chat") {
      setActiveAgentFlow("chat");
    }
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim()) return;

    const currentChat = getCurrentAgentChat();

    const newMessage = {
      id: Date.now(),
      text: chatInput,
      sender: "user",
      timestamp: new Date(),
    };

    updateCurrentAgentChat([...currentChat, newMessage]);
    const currentInput = chatInput;
    setChatInput("");

    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
      const selectedAgentData = agents.find(
        (agent) => agent.id === selectedAgent
      );
      const agentType =
        selectedAgentData?.agentKey || selectedAgentData?.name.toLowerCase().replace(/[^a-z]/g, "-") ||
        "family-based";

      const response = await fetch(`${backendUrl}/api/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: currentInput,
          agent_type: agentType,
          conversation_history: currentChat.map((msg) => ({
            sender: msg.sender === "user" ? "user" : "assistant",
            text: msg.text,
          })),
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 403) {
          // Handle subscription limit errors
          const errorData = await response.json();
          const errorMessage = errorData.detail || "Access denied";
          
          const errorResponse = {
            id: Date.now() + 1,
            text: `🚫 ${errorMessage}\n\n💡 To access this agent, please upgrade your subscription plan. Click on your profile menu and visit the subscription management section to upgrade.`,
            sender: "ai",
            timestamp: new Date(),
          };
          updateCurrentAgentChat([...currentChat, newMessage, errorResponse]);
          return;
        } else if (response.status === 401) {
          // Handle authentication errors
          const errorResponse = {
            id: Date.now() + 1,
            text: "🔐 Please log in to continue using the AI agents. You need to be authenticated to access this feature.",
            sender: "ai",
            timestamp: new Date(),
          };
          updateCurrentAgentChat([...currentChat, newMessage, errorResponse]);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const aiResponse = {
        id: Date.now() + 1,
        text: data.response,
        sender: "ai",
        timestamp: new Date(),
      };

      updateCurrentAgentChat([...currentChat, newMessage, aiResponse]);
    } catch (error) {
      console.error("Error calling AI:", error);
      const errorResponse = {
        id: Date.now() + 1,
        text:
          "I apologize, but I'm having trouble connecting to the AI service right now. Please try again in a moment. Error: " +
          error.message,
        sender: "ai",
        timestamp: new Date(),
      };
      updateCurrentAgentChat([...currentChat, newMessage, errorResponse]);
    }
  };

  const selectedAgentData = agents.find((agent) => agent.id === selectedAgent);

  return (
    <div id="ai-agents-section" className="px-6 py-16">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">
            Explore Our AI Agents
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Find the right help for your immigration journey. Click on any agent
            below to learn more:
          </p>
        </div>

        {/* Updated Agents Grid with Access Control */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 perspective-[1000px]">
          {agents.map((agent, index) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onStartCaseReview={onStartCaseReview}
              onAgentSelect={handleAgentSelect}
              user={user}
              onAuthRequired={onAuthRequired}
              style={styles[index]}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              index={index}
            />
          ))}
        </div>

        {/* Agent Modal */}
        <AgentModal
          agent={selectedAgentData}
          isOpen={showAgentModal}
          onClose={() => setShowAgentModal(false)}
          onSelectFlow={handleFlowSelect}
        />

        {/* Chat Interface */}
        {activeAgentFlow === "chat" && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-3xl mr-4">
                      {selectedAgentData?.icon}
                    </span>
                    <div>
                      <h2 className="text-2xl font-bold text-white">
                        {selectedAgentData?.name}
                      </h2>
                      <p className="text-gray-400">
                        Ask me anything about your case
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveAgentFlow(null)}
                    className="text-gray-400 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="flex-1 p-6 overflow-y-auto">
                <div className="space-y-4">
                  {getCurrentAgentChat().length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-400 mb-4">
                        Start your conversation with the{" "}
                        {selectedAgentData?.name}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {(() => {
                          const agentNameToKey = {
                            "Family-Based Agent": "family-based-agent",
                            "Work Visa Agent": "work-visa-agent",
                            "Asylum & Protection Agent":
                              "asylum-protection-agent",
                            "Student & Exchange Visa Agent":
                              "student-exchange-visa-agent",
                            "Citizenship Agent": "citizenship-agent",
                            "RFE / NOID Response Agent":
                              "rfe-noid-response-agent",
                            "DACA / TPS Renewal Agent":
                              "daca-tps-renewal-agent",
                            "Visitor & Tourist Visa Agent":
                              "visitor-tourist-visa-agent",
                          };

                          const agentType =
                            agentNameToKey[selectedAgentData?.name] ||
                            "family-based-agent";
                          const questions =
                            getAgentSpecificQuestions(agentType);
                          return questions
                            .slice(0, 2)
                            .map((question, index) => (
                              <button
                                key={index}
                                onClick={() => setChatInput(question)}
                                className="bg-gray-800 p-3 rounded text-left text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                              >
                                {question}
                              </button>
                            ));
                        })()}
                      </div>
                    </div>
                  ) : (
                    getCurrentAgentChat().map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${
                          message.sender === "user"
                            ? "justify-end"
                            : "justify-start"
                        }`}
                      >
                        <div
                          className={`max-w-md lg:max-w-lg px-4 py-3 rounded-lg ${
                            message.sender === "user"
                              ? "bg-[#00fba6] text-black"
                              : "bg-gray-800 text-white"
                          }`}
                        >
                          {message.sender === "user" ? (
                            <p className="text-sm">{message.text}</p>
                          ) : (
                            <div
                              className="text-sm formatted-response"
                              dangerouslySetInnerHTML={{
                                __html: renderFormattedText(message.text),
                              }}
                            />
                          )}
                          <p className="text-xs mt-1 opacity-70">
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="p-6 border-t border-gray-800">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                    placeholder="Ask me anything about immigration..."
                    className="flex-1 bg-gray-800 text-white px-4 py-2 rounded border border-gray-600 focus:border-[#00fba6] focus:outline-none"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!chatInput.trim()}
                    className="bg-[#00fba6] text-black px-4 py-2 rounded font-medium hover:bg-[#00d9f1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Featured Tool Component
const FeaturedTool = () => {
  const steps = [
    {
      number: "1",
      title: "Conversational Intake",
      description:
        "No complicated government forms. Our assistant walks you through questions in plain language — like talking to a real person.",
    },
    {
      number: "2",
      title: "Smart Form Mapping",
      description:
        "Your responses automatically map to correct USCIS form sections (I-485, I-130, I-90) each ready to review and download.",
    },
    {
      number: "3",
      title: "Real-Time Guidance",
      description:
        "Flags missing answers, formatting issues, and inconsistent data before they become costly filing problems.",
    },
  ];

  return (
    <div className="px-6 py-16 bg-gray-900/50">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-emerald-400 mb-4">
            Featured Tool: AI-Powered Immigration Form-Filling Assistant
          </h2>
          <h3 className="text-2xl font-semibold text-white mb-4">
            AI Form-Filling Assistant
          </h3>
          <p className="text-gray-400 max-w-3xl mx-auto">
            Take the stress, confusion, and human error out of immigration
            filing. Complete complex USCIS forms with ease and confidence.
          </p>
        </div>

        {/* How It Works */}
        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-emerald-400 text-center mb-8">
            How It Works:
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-emerald-400 text-black rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {step.number}
                </div>
                <h4 className="text-xl font-semibold text-white mb-3">
                  {step.title}
                </h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Form Support Component
const FormSupport = () => {
  const forms = [
    "I-130 — Petition for Alien Relative",
    "I-485 — Adjustment of Status",
    "I-751 — Remove Conditions on Residence",
    "N-400 — Naturalization",
    "DS-160 — Non-immigrant Visa",
    "NOID/RFE Response Letters",
  ];

  return (
    <div className="px-6 py-16">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl font-bold text-emerald-400 mb-8">
          Forms We Support:
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {forms.map((form, index) => (
            <button
              key={index}
              className="bg-gray-900 border border-emerald-400 text-emerald-400 px-6 py-3 rounded-lg hover:bg-emerald-400 hover:text-black transition-colors"
            >
              {form}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Value Proposition Component
const ValueProposition = () => {
  const benefits = [
    {
      icon: "🔒",
      title: "Secure & Encrypted",
      description: "Your data is protected with bank-level security",
    },
    {
      icon: "⚖️",
      title: "Trained on U.S. Immigration Law",
      description: "AI trained on thousands of successful cases",
    },
    {
      icon: "👥",
      title: "Backed by Immigration Experts",
      description: "Reviewed and validated by legal professionals",
    },
    {
      icon: "🤖",
      title: "Not Legal Advice, But Trusted AI Guidance",
      description: "Smart assistance without attorney fees",
    },
  ];

  return (
    <div className="px-6 py-16 bg-gray-900/30">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">
            Why Pay Thousands When You Can Pay Tens
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto">
            Get professional-grade case review at 1/10th the cost of hiring an
            attorney.
          </p>
          <p className="text-gray-400 max-w-3xl mx-auto mt-4">
            Skip the $500/hour attorney fees. Our AI immigration agent reviews
            your case like an experienced lawyer - catching critical errors and
            strengthening your application for a fraction of cost.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((benefit, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl mb-4">{benefit.icon}</div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {benefit.title}
              </h3>
              <p className="text-gray-400 text-sm">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// CTA Section Component
const CTASection = ({ user, onAuthRequired, onOpenAIExpert }) => {
  return (
    <div className="px-6 py-16 text-center">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-white mb-4">
          Ready to File with Confidence
        </h2>
        <p className="text-gray-400 mb-8">
          Join 10,000+ immigrants who've strengthened their cases with our
          AI-powered review.
        </p>
        <FeatureButton
          feature="start_free_case_review"
          onClick={() => {
            if (!user) {
              onAuthRequired("signin");
              return;
            }
            // User is authenticated, open AI Immigration Expert flow (same as case review)
            onOpenAIExpert();
          }}
          className="bg-emerald-400 text-black px-8 py-3 rounded-full font-medium hover:bg-emerald-300 transition-colors"
          showUpgradeModal={false}
        >
          Start Free Case Review
        </FeatureButton>
      </div>
    </div>
  );
};

// For Attorneys - Comprehensive Platform Section
const ForAttorneys = () => {


  const platformFeatures = [
    {
      icon: "🔍",
      title: "AI Case Analysis",
      description:
        "Get instant case strength assessments, identify potential issues early, and receive strategic recommendations powered by analysis of thousands of successful cases.",
    },
    {
      icon: "📋",
      title: "Automated Document Review",
      description:
        "Upload client documents and receive comprehensive analysis, completeness checks, and recommendations for strengthening applications.",
    },
    {
      icon: "😊",
      title: "Client Self-Service Portal",
      description:
        "Empower clients with guided form completion, document preparation, and 24/7 support, reducing your administrative burden.",
    },
    {
      icon: "📄",
      title: "RFE/NOID Response Assistant",
      description:
        "Quickly analyze USCIS notices, identify required evidence, and generate comprehensive response strategies with supporting documentation checklists.",
    },
    {
      icon: "📊",
      title: "Practice Analytics",
      description:
        "Track case outcomes, identify success patterns, and optimize your practice with data-driven insights and performance metrics.",
    },
    {
      icon: "🔒",
      title: "Compliance & Security",
      description:
        "Built with attorney-client privilege in mind, featuring bank-level security, audit trails, and full compliance with legal industry standards.",
    },
  ];

  const specializedAgents = [
    "👨‍👩‍👧‍👦 Family-Based Immigration Specialist",
    "💼 Work Visa & Employment Expert",
    "🛡️ Asylum & Protection Advocate",
    "📚 Student & Exchange Visa Guide",
    "🇺🇸 Naturalization & Citizenship Coach",
    "📋 RFE/NOID Response Specialist",
    "🔄 DACA/TPS Renewal Expert",
    "🧳 Visitor & Tourist Visa Assistant",
  ];

  const areasOfSpecialization = [
    "Family-Based",
    "Work Visa",
    "Asylum",
    "Citizenship",
    "RFE/NOID",
    "Other",
  ];


  const benefits = [
    {
      icon: "📋",
      title: "Review AI-prepared case files",
      description: "for clients who are ready to take the next step",
    },
    {
      icon: "💰",
      title: "Earn from on-demand consultations",
      description: "and case reviews — without the client acquisition hassle",
    },
    {
      icon: "📦",
      title: "Receive structured, pre-organized case packets",
      description: "to save you time and effort",
    },
    {
      icon: "🚀",
      title: "Be part of a forward-thinking legal tech movement",
      description: "",
    },
  ];
  
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    lawFirm: "",
    stateOfPractice: "",
    areasOfSpecialization: [],
    additionalInfo: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      areasOfSpecialization: checked
        ? [...prev.areasOfSpecialization, name]
        : prev.areasOfSpecialization.filter((area) => area !== name),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("🔵 Attorney waitlist form submission started");
    console.log("🔵 Form data:", formData);

    setIsSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    try {
      // Validate required fields
      const requiredFields = {
        fullName: "Full Name",
        email: "Email Address",
        lawFirm: "Law Firm/Practice Name",
        stateOfPractice: "State of Practice",
      };

      for (const [field, label] of Object.entries(requiredFields)) {
        if (!formData[field].trim()) {
          console.log("❌ Validation failed for:", field);
          setSubmitStatus({
            type: "error",
            message: `${label} is required`,
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        console.log("❌ Email validation failed");
        setSubmitStatus({
          type: "error",
          message: "Please enter a valid email address",
        });
        setIsSubmitting(false);
        return;
      }

      console.log("✅ Validation passed, submitting to backend");

      // Submit to backend
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
      console.log("🔵 Backend URL:", backendUrl);

      const response = await fetch(`${backendUrl}/api/attorney/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      console.log("🔵 Response status:", response.status);

      const data = await response.json();
      console.log("🔵 Response data:", data);

      if (response.ok) {
        console.log("✅ Submission successful!");
        setSubmitStatus({
          type: "success",
          message:
            "Thank you for your interest! We'll be in touch soon with early access details.",
        });

        // Reset form
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          lawFirm: "",
          stateOfPractice: "",
          areasOfSpecialization: [],
          additionalInfo: "",
        });
      } else {
        console.log("❌ Submission failed:", data.detail);
        setSubmitStatus({
          type: "error",
          message: data.detail || "An error occurred. Please try again.",
        });
      }
    } catch (error) {
      console.error("🔴 Attorney waitlist error:", error);
      setSubmitStatus({
        type: "error",
        message: "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsSubmitting(false);
      console.log("🔵 Form submission completed");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800">
      {/* Hero Section */}
      <div className="px-6 py-16">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            Immigrantli for Attorneys
          </h1>
          <p className="text-xl text-emerald-400 mb-4">
            AI-powered support that complements your practice — not replaces it.
          </p>
          <p className="text-gray-400 text-lg max-w-4xl mx-auto">
            Immigrantli's AI-powered platform helps immigration attorneys
            streamline case management, improve client outcomes, and scale their
            practice efficiently.
          </p>
        </div>
      </div>

      {/* What Immigrantli Can Do Section */}
      <div className="px-6 py-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-white mb-4 text-center">
            🔍 What Immigrantli Can Do:
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {platformFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-800 p-8 rounded-xl border border-gray-700 hover:border-emerald-500 transition-all duration-300"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Specialized AI Agents Section */}
      <div className="px-6 py-16 bg-gray-900/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-white mb-8 text-center">
            Specialized AI Agents for Every Case Type
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {specializedAgents.map((agent, index) => (
              <div
                key={index}
                className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-emerald-500 transition-all duration-300"
              >
                <p className="text-white font-medium text-center">{agent}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-xl text-emerald-400 font-semibold mb-2">
              You remain the legal expert.
            </p>
            <p className="text-gray-400 text-lg">
              We just help users get organized, confident, and prepared.
            </p>
          </div>
        </div>
      </div>

      {/* Waitlist Section */}
      <div className="px-6 py-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Information */}
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Transform Your Practice?
              </h2>
              <p className="text-gray-400 mb-8 text-lg">
                Join the waitlist for early access to Immigrantli's attorney
                platform and be among the first to experience the future of
                immigration law practice.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-3 h-3 bg-emerald-400 rounded-full mt-2 mr-4"></div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Early Access Benefits
                    </h4>
                    <p className="text-gray-400">
                      Be first to access new features and provide input on
                      platform development
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-3 h-3 bg-emerald-400 rounded-full mt-2 mr-4"></div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Exclusive Pricing
                    </h4>
                    <p className="text-gray-400">
                      Lock in special rates for early adopters
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-3 h-3 bg-emerald-400 rounded-full mt-2 mr-4"></div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Priority Support
                    </h4>
                    <p className="text-gray-400">
                      Direct line to our development team for feature requests
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Waitlist Form */}
            <div className="bg-gray-800 p-8 rounded-xl border border-gray-700">
              <h3 className="text-2xl font-semibold text-emerald-400 mb-6 text-center">
                Join the Waitlist
              </h3>

              <form className="space-y-6" onSubmit={handleSubmit}>
                {/* Status Message */}
                {submitStatus.message && (
                  <div
                    className={`p-4 rounded-lg text-center ${
                      submitStatus.type === "success"
                        ? "bg-emerald-900/30 border border-emerald-400 text-emerald-400"
                        : "bg-red-900/30 border border-red-400 text-red-400"
                    }`}
                  >
                    {submitStatus.message}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name *"
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="text"
                    name="lawFirm"
                    placeholder="Law Firm/Practice Name *"
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                    value={formData.lawFirm}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>

                <input
                  type="text"
                  name="stateOfPractice"
                  placeholder="State of Practice *"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                  value={formData.stateOfPractice}
                  onChange={handleInputChange}
                  required
                />

                <div>
                  <label className="block text-white mb-3 font-medium">
                    Areas of Specialization
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {areasOfSpecialization.map((area) => (
                      <label
                        key={area}
                        className="flex items-center text-gray-300"
                      >
                        <input
                          type="checkbox"
                          name={area}
                          className="mr-3 accent-emerald-400"
                          checked={formData.areasOfSpecialization.includes(
                            area
                          )}
                          onChange={handleCheckboxChange}
                        />
                        {area}
                      </label>
                    ))}
                  </div>
                </div>

                <textarea
                  name="additionalInfo"
                  placeholder="Message (Optional)"
                  rows="4"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-lg font-medium transition-colors disabled:bg-emerald-700 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Joining Waitlist...
                    </div>
                  ) : (
                    "Join Waitlist"
                  )}
                </button>

                <p className="text-gray-400 text-sm text-center">
                  Your information is secure and will only be used to contact
                  you about early access opportunities.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Original Attorney Network Component (keeping for compatibility)
const AttorneyNetwork = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    lawFirm: "",
    barNumber: "",
    stateOfPractice: "",
    yearsOfExperience: "",
    areasOfSpecialization: [],
    additionalInfo: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });

  const benefits = [
    {
      icon: "📋",
      title: "Review AI-prepared case files",
      description: "for clients who are ready to take the next step",
    },
    {
      icon: "💰",
      title: "Earn from on-demand consultations",
      description: "and case reviews — without the client acquisition hassle",
    },
    {
      icon: "📦",
      title: "Receive structured, pre-organized case packets",
      description: "to save you time and effort",
    },
    {
      icon: "🚀",
      title: "Be part of a forward-thinking legal tech movement",
      description: "",
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      areasOfSpecialization: checked
        ? [...prev.areasOfSpecialization, name]
        : prev.areasOfSpecialization.filter((area) => area !== name),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("🔵 Form submission started");
    console.log("🔵 Form data:", formData);

    setIsSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    try {
      // Validate required fields
      const requiredFields = {
        fullName: "Full Name",
        email: "Email Address",
        lawFirm: "Law Firm",
        barNumber: "Bar Number",
        stateOfPractice: "State of Practice",
        yearsOfExperience: "Years of Experience",
      };

      for (const [field, label] of Object.entries(requiredFields)) {
        if (!formData[field].trim()) {
          console.log("❌ Validation failed for:", field);
          setSubmitStatus({
            type: "error",
            message: `${label} is required`,
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        console.log("❌ Email validation failed");
        setSubmitStatus({
          type: "error",
          message: "Please enter a valid email address",
        });
        setIsSubmitting(false);
        return;
      }

      console.log("✅ Validation passed, submitting to backend");

      // Submit to backend
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
      console.log("🔵 Backend URL:", backendUrl);

      const response = await fetch(`${backendUrl}/api/attorney/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      console.log("🔵 Response status:", response.status);

      const data = await response.json();
      console.log("🔵 Response data:", data);

      if (response.ok) {
        console.log("✅ Submission successful!");
        setSubmitStatus({
          type: "success",
          message: data.message,
        });

        // Reset form
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          lawFirm: "",
          barNumber: "",
          stateOfPractice: "",
          yearsOfExperience: "",
          areasOfSpecialization: [],
          additionalInfo: "",
        });
      } else {
        console.log("❌ Submission failed:", data.detail);
        setSubmitStatus({
          type: "error",
          message: data.detail || "An error occurred. Please try again.",
        });
      }
    } catch (error) {
      console.error("🔴 Attorney registration error:", error);
      setSubmitStatus({
        type: "error",
        message: "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsSubmitting(false);
      console.log("🔵 Form submission completed");
    }
  };

  return (
    <div className="flex items-center justify-center">
      <section className="text-white py-16 px-4 sm:px-8 relative overflow-hidden mt-30 max-[1200px]:w-[90%]">
        {/* Background gradient effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 animate-pulse delay-1000"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-start text-left">
            {/* Left Side - Content Details */}
            <div className="space-y-8">
              {/* Header Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
                    Join Our Early Attorney Network
                  </h2>
                </div>
                <p className="text-gray-300 text-lg leading-relaxed max-w-2xl">
                  We're inviting a select group of licensed attorneys to join
                  our early access network — helping shape how AI and legal
                  expertise work together to serve immigrants better.
                </p>
              </div>

              {/* Why Join Section */}
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-[#54edb6] flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#54edb6] rounded-full"></span>
                  Why Join?
                </h3>

                <div className="space-y-6">
                  {benefits.map((benefit, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-4 p-4 rounded-lg hover:bg-white/5 transition-all duration-300 group"
                    >
                      <div className="text-2xl group-hover:scale-110 transition-transform duration-300">
                        {benefit.icon}
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-lg mb-1 group-hover:text-[#54edb6] transition-colors duration-300">
                          {benefit.title}
                        </h4>
                        {benefit.description && (
                          <p className="text-gray-400 leading-relaxed">
                            {benefit.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Call to Action Text */}
              <div className="p-6 bg-gradient-to-r from-[#54edb6]/10 to-[#54edb6]/5 rounded-lg border border-[#54edb6]/20">
                <p className="text-gray-300 leading-relaxed">
                  Fill out the short form and we'll be in touch with early
                  access, updates, and collaboration opportunities.
                </p>
              </div>
            </div>

            {/* Right Side - Contact Form */}
            <div className="lg:sticky lg:top-8">
              <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-[#54edb6]/20 rounded-xl p-8 shadow-2xl">
                <h3 className="text-2xl font-semibold text-[#54edb6] mb-6 text-center">
                  Join the Network
                </h3>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Status Message */}
                  {submitStatus.message && (
                    <div
                      className={`p-4 rounded-lg text-center ${
                        submitStatus.type === "success"
                          ? "bg-emerald-900/30 border border-emerald-400 text-emerald-400"
                          : "bg-red-900/30 border border-red-400 text-red-400"
                      }`}
                    >
                      {submitStatus.message}
                    </div>
                  )}

                  {/* Name and Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="fullName"
                      placeholder="Full Name *"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-[#54edb6] focus:outline-none transition-all duration-200 focus:shadow-[0_0_10px_rgba(84,237,182,0.3)]"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address *"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-[#54edb6] focus:outline-none transition-all duration-200 focus:shadow-[0_0_10px_rgba(84,237,182,0.3)]"
                    />
                  </div>

                  {/* Phone and Law Firm */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-[#54edb6] focus:outline-none transition-all duration-200 focus:shadow-[0_0_10px_rgba(84,237,182,0.3)]"
                    />
                    <input
                      type="text"
                      name="lawFirm"
                      placeholder="Law Firm / Practice Name *"
                      value={formData.lawFirm}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-[#54edb6] focus:outline-none transition-all duration-200 focus:shadow-[0_0_10px_rgba(84,237,182,0.3)]"
                    />
                  </div>

                  {/* Bar Number and State */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="barNumber"
                      placeholder="Bar Number *"
                      value={formData.barNumber}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-[#54edb6] focus:outline-none transition-all duration-200 focus:shadow-[0_0_10px_rgba(84,237,182,0.3)]"
                    />
                    <input
                      type="text"
                      name="stateOfPractice"
                      placeholder="State of Practice *"
                      value={formData.stateOfPractice}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-[#54edb6] focus:outline-none transition-all duration-200 focus:shadow-[0_0_10px_rgba(84,237,182,0.3)]"
                    />
                  </div>

                  {/* Years of Experience */}
                  <select
                    name="yearsOfExperience"
                    value={formData.yearsOfExperience}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-black/40 border border-gray-600 rounded-lg text-white focus:border-[#54edb6] focus:outline-none transition-all duration-200 focus:shadow-[0_0_10px_rgba(84,237,182,0.3)]"
                  >
                    <option value="">
                      Years of Immigration Law Experience *
                    </option>
                    <option value="0-2">0-2 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="6-10">6-10 years</option>
                    <option value="11-15">11-15 years</option>
                    <option value="16+">16+ years</option>
                  </select>

                  {/* Specializations */}
                  <div>
                    <label className="block text-sm text-gray-300 mb-3 font-medium">
                      Areas of Specialization *
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        "Family-Based Immigration",
                        "Employment-Based Immigration",
                        "Asylum & Refugee Law",
                        "Naturalization & Citizenship",
                        "Student & Exchange Visas",
                        "Business Immigration",
                        "Other",
                      ].map((specialization) => (
                        <label
                          key={specialization}
                          className="flex items-center gap-2 text-xs text-gray-300 hover:text-white transition-colors duration-200 cursor-pointer p-2 rounded hover:bg-white/5"
                        >
                          <input
                            type="checkbox"
                            name={specialization}
                            checked={formData.areasOfSpecialization.includes(
                              specialization
                            )}
                            onChange={handleCheckboxChange}
                            className="w-4 h-4 text-[#54edb6] bg-black/40 border-gray-600 rounded focus:ring-[#54edb6] focus:ring-2"
                          />
                          <span className="text-xs">{specialization}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Additional Info */}
                  <textarea
                    name="additionalInfo"
                    placeholder="Tell us about your practice and why you're interested in joining our network (Optional)"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 bg-black/40 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-[#54edb6] focus:outline-none transition-all duration-200 resize-none focus:shadow-[0_0_10px_rgba(84,237,182,0.3)]"
                  ></textarea>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#54edb6] to-[#54edb6]/80 text-black font-semibold px-6 py-4 rounded-lg hover:from-[#54edb6]/90 hover:to-[#54edb6]/70 transition-all duration-200 shadow-lg hover:shadow-[0_0_20px_rgba(84,237,182,0.4)] hover:scale-[1.02] transform disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                        Submitting...
                      </div>
                    ) : (
                      "Join Early Attorney Network"
                    )}
                  </button>
                </form>

                {/* Privacy Note */}
                <p className="text-xs text-gray-400 text-center mt-4 leading-relaxed">
                  Your information is secure and will only be used to contact
                  you about our attorney network program.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Contact Us Component
const ContactUs = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear any previous errors when user starts typing
    if (submitError) setSubmitError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Create FormData object
      const formDataToSend = new FormData();
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject || 'General Inquiry');
      formDataToSend.append('message', formData.message);

      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setShowConfirmation(true);
        setFormData({
          fullName: "",
          email: "",
          subject: "",
          message: ""
        });
        // Auto-hide confirmation after 5 seconds
        setTimeout(() => setShowConfirmation(false), 5000);
      } else {
        const errorData = await response.json();
        setSubmitError(errorData.detail || 'Failed to submit contact form');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-black text-white py-16 px-6">
      <section className="relative overflow-hidden">
        {/* Background gradient elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-[#00fba6]/10 to-[#00d9f1]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-[#00d9f1]/10 to-[#00fba6]/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-start text-left">
            {/* Left Side - Content */}
            <div className="space-y-8">
              {/* Header Section */}
              <div className="space-y-4">
                <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
                  Contact Us – We're Here to Help
                </h2>
                <p className="text-gray-300 text-lg leading-relaxed max-w-2xl">
                  Have questions about your immigration case or need support with our platform? 
                  Our team is ready to assist you with personalized guidance and solutions.
                </p>
              </div>

              {/* Contact Benefits */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#00fba6] to-[#00d9f1] rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-black text-xl">💬</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Quick Response</h3>
                    <p className="text-gray-300 leading-relaxed">
                      Our support team typically responds within 24 hours to address your questions and concerns.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#00d9f1] to-[#00fba6] rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-black text-xl">🎯</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Expert Guidance</h3>
                    <p className="text-gray-300 leading-relaxed">
                      Get personalized assistance from our team of immigration technology specialists.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#00fba6] to-[#00d9f1] rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-black text-xl">🤝</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Partnership Opportunities</h3>
                    <p className="text-gray-300 leading-relaxed">
                      Interested in collaborating? We're always open to exploring new partnership possibilities.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Contact Form */}
            <div className="relative">
              {/* Form Container */}
              <div className="relative bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-2xl p-8">
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] via-transparent to-white/[0.03] pointer-events-none rounded-2xl"></div>
                
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-white mb-6 text-center">
                    Send Us a Message
                  </h3>

                  {/* Success Confirmation */}
                  {showConfirmation && (
                    <div className="mb-6 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-green-400 text-xl">✅</span>
                        <p className="text-green-300 font-medium">
                          Thank you for reaching out! Our team will review your message and get back to you shortly.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {submitError && (
                    <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
                      <p className="text-red-300">{submitError}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Full Name */}
                    <div>
                      <label className="block text-white font-medium mb-2">
                        Full Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/[0.05] border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#54edb6] focus:ring-1 focus:ring-[#54edb6] transition-all duration-200"
                        placeholder="Enter your full name"
                      />
                    </div>

                    {/* Email Address */}
                    <div>
                      <label className="block text-white font-medium mb-2">
                        Email Address <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/[0.05] border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#54edb6] focus:ring-1 focus:ring-[#54edb6] transition-all duration-200"
                        placeholder="Enter your email address"
                      />
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-white font-medium mb-2">
                        Subject <span className="text-gray-500">(optional)</span>
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/[0.05] border border-white/20 rounded-lg text-white focus:outline-none focus:border-[#54edb6] focus:ring-1 focus:ring-[#54edb6] transition-all duration-200 [&>option]:bg-gray-800 [&>option]:text-white"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          color: 'white'
                        }}
                      >
                        <option value="" style={{backgroundColor: '#1f2937', color: 'white'}}>Select a subject (optional)</option>
                        <option value="General Inquiry" style={{backgroundColor: '#1f2937', color: 'white'}}>General Inquiry</option>
                        <option value="Support Request" style={{backgroundColor: '#1f2937', color: 'white'}}>Support Request</option>
                        <option value="Feedback" style={{backgroundColor: '#1f2937', color: 'white'}}>Feedback</option>
                        <option value="Partnership Opportunities" style={{backgroundColor: '#1f2937', color: 'white'}}>Partnership Opportunities</option>
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-white font-medium mb-2">
                        Message <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="5"
                        className="w-full px-4 py-3 bg-white/[0.05] border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#54edb6] focus:ring-1 focus:ring-[#54edb6] transition-all duration-200 resize-vertical"
                        placeholder="Tell us how we can help you..."
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-[#54edb6] to-[#54edb6]/80 text-black font-semibold px-6 py-4 rounded-lg hover:from-[#54edb6]/90 hover:to-[#54edb6]/70 transition-all duration-200 shadow-lg hover:shadow-[0_0_20px_rgba(84,237,182,0.4)] hover:scale-[1.02] transform disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                          Sending...
                        </div>
                      ) : (
                        "Send Message"
                      )}
                    </button>
                  </form>

                  {/* Privacy Note */}
                  <p className="text-xs text-gray-400 text-center mt-4 leading-relaxed">
                    Your information is secure and will only be used to respond to your inquiry.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-black text-white py-12 px-24 mt-30  max-[600px]:px-12">
      <div className="mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          <div className="space-y-4">
            <h3 className="text-[#54edb6] text-lg font-semibold">Services</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="#"
                  className="text-[#8f9094] text-sm hover:text-white transition-colors"
                >
                  Form Review
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-[#8f9094] text-sm hover:text-white transition-colors"
                >
                  Case Analysis
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-[#8f9094] text-sm hover:text-white transition-colors"
                >
                  NOID/RFE Help
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-[#8f9094] text-sm hover:text-white transition-colors"
                >
                  Interview Prep
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-[#8f9094] text-sm hover:text-white transition-colors"
                >
                  Immigrantvli for Attorneys
                </a>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-[#54edb6] text-lg font-semibold">Case Types</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="#"
                  className="text-[#8f9094] text-sm hover:text-white transition-colors"
                >
                  Family Immigration
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-[#8f9094] text-sm hover:text-white transition-colors"
                >
                  Employment Visas
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-[#8f9094] text-sm hover:text-white transition-colors"
                >
                  Citizenship
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-[#8f9094] text-sm hover:text-white transition-colors"
                >
                  Asylum & TPS
                </a>
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className="text-[#54edb6] text-lg font-semibold">Support</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="#"
                  className="text-[#8f9094] text-sm hover:text-white transition-colors"
                >
                  Help Center
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-[#8f9094] text-sm hover:text-white transition-colors"
                >
                  Contact Us
                </a>
              </li>
              <li>
                <a
                  href="/privacy-policy"
                  className="text-[#8f9094] text-sm hover:text-white transition-colors"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="/terms-of-service"
                  className="text-[#8f9094] text-sm hover:text-white transition-colors"
                >
                  Terms of Service
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-700">
          <div className="text-center space-y-2">
            <p className="text-[#8f9094] text-sm">
              10 Glenlake Pkwy NE, Atlanta, GA 30328
            </p>
            <p className="text-[#8f9094] text-sm">
              © 2024 Immigrantvli.com. All rights reserved. | Immigrantvli.com does not provide legal advice or substitute for an attorney.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Components = {
  Header,
  Banner,
  Hero,
  StatsSection,
  SuccessStories,
  AIAgents,
  FeaturedTool,
  FormSupport,
  ValueProposition,
  CTASection,
  AttorneyNetwork,
  ContactUs,
  ForAttorneys,
  Footer,
  FamilyBasedAgentFlow,
  HybridChatAssistant,
  AIContentLibrary,
};
